// PreviewTester/PreviewTester.js - Fully Agnostic with No Hardcoded Values
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import nunjucks from "nunjucks/browser/nunjucks";
import {
  flattenObject,
  isArrayData,
  safeJSONParse,
  deepMerge,
  hasActiveChild,
} from "./utils/helpers";
import { extractNestedConditions } from "./utils/templateParser";
import { generateTestValues } from "./utils/valueGenerator";
import { parseTemplateForEdit } from "./utils/parseTemplateForEdit";
import { parseTemplateForEditFirstLoadOnly } from "./utils/parseTemplateForEditFirstLoadOnly";

import {
  generateTemplateFromJSON,
  generateCase3Template,
  detectNestedArrays,
} from "./utils/templateGenerator";
// PreviewTester/PreviewTester.js - Add these imports at the top

import {
  detectSelectionType,
  createMinimalTransformationStep,
  STYLE_TRANSFORMATIONS,
  generateTableHTML,
  generateCardHTML,
  generateListHTML,
  generatePositionSelector,
  addOperationToPhase,
  removeOperationFromPhase,
  nextPhase,
  prevPhase,
  finalizeCombinedStep,
} from "./utils/transformationRules";

// ✅ ADD THESE MISSING IMPORTS:
import {
  createStructuralStep,
  createMediaStep,
  createStyleStep,
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  createCombinedStep, // ✅ Now available from transformationRules
  isMediaStep,
  isCombinedStep,
  cleanSelectors,
} from "./utils/transformationSchema";

import LeftPanel from "./components/LeftPanel";
import RightPanel from "./components/RightPanel";
import ScenarioBranch from "../PreviewEditorHome/ScenarioBranch";
import styles from "./PreviewTester.module.css";
import { TemplateValidator } from "./utils/templateValidator";
import {
  wrapFieldValuesWithSpans,
  applyTransformationLogic,
  extractImageUrls,
  escapeRegExp,
  findElementByPath,
  getNthChildPath,
  getRelativePath,
  isImageUrl,
  createImageHtml,
  applyMediaToAllInLoop,
  stripStructuralEmptyLines,
} from "./utils/renderingUtils";

import { applyMediaTransformation } from "../../PreviewTesterHome/PreviewTester/utils/transformationRules";

import LayoutChoicePopup from "./components/LayoutChoicePopup";


import { enhanceTemplateWithLoopWrappers } from "./utils/templateEnhancer";
import {
  SelectorTracker,
  recalculateSelector,
} from "./utils/selectorRecalculator";
import {
  TourProvider,
  TourManager,
  markTourCompleted,
} from "../../PreviewTesterHome/Tour";

console.log("here is pigs");

// Configure nunjucks
const nunjucksEnv = nunjucks.configure({ autoescape: false });

nunjucksEnv.addFilter("wrapLoopContent", function (content, varName) {
  if (!content) return content;
  const trimmed = content.trim();
  if (!trimmed) return content;
  return `<div class="for-loop-started" data-loop-type="inner" data-loop-var="${varName}">${trimmed}</div>`;
});

nunjucksEnv.addFilter("wrapOuterLoopContent", function (content, varName) {
  if (!content) return content;
  return content;
});

console.log(
  "[Nunjucks] Custom filters registered: wrapLoopContent, wrapOuterLoopContent",
);

// Constants for entry types
const ENTRY_TYPES = {
  LOCAL_APP: "local_app_level",
  DEFAULT_APP: "default_app_level",
  DEFAULT_GLOBAL: "default_global_level",
};

const SCENARIO_CONFIGS = {
  [ENTRY_TYPES.LOCAL_APP]: {
    label: "Create Local App",
    description: "Has config_id and kb_id",
    icon: "📱",
    color: "#4a6cf7",
    requiresConfigId: true,
    requiresKbId: true,
    requiresStatusCode: true,
    deletable: true,
  },
  [ENTRY_TYPES.DEFAULT_APP]: {
    label: "Create Default App",
    description: "No config_id, has kb_id",
    icon: "🌐",
    color: "#38a169",
    requiresConfigId: false,
    requiresKbId: true,
    requiresStatusCode: true,
    deletable: false,
  },
  [ENTRY_TYPES.DEFAULT_GLOBAL]: {
    label: "Create Default Global",
    description: "No config_id, no kb_id",
    icon: "🌍",
    color: "#e53e3e",
    requiresConfigId: false,
    requiresKbId: false,
    requiresStatusCode: true,
    deletable: false,
  },
};

// DEFAULT TEMPLATE - Shows the entire response data
const DEFAULT_TEMPLATE = "{{ response_data | dump | safe }}";
const DEFAULT_SAMPLE_RESPONSE = "";
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const PreviewTester = ({
  // Props for modal mode
  initialConfigId = null,
  initialKbId = null,
  initialStatusCode = null,
  initialScenario = null,
  initialTemplate = null,
  initialSampleResponse = null,
  // Mode flag - if true, don't make API calls, just return data
  modalMode = false,
  onTemplateDataReady = null,
  // Existing props from parent
  sampleResponse: propSampleResponse,
  template: propTemplate,
  kbId: propKbId,
  statusCode: propStatusCode,
  appData: propAppData,
  onSave: propOnSave,
  onChange: propOnChange,
  mode: propMode,
  showControls: propShowControls = true,
  showScenarioTree: propShowScenarioTree = true,
  showTransformationPanel: propShowTransformationPanel = true,
  forceSampleLoad: propForceSampleLoad = false,
}) => {
  console.log("[DEBUG] PreviewTester component rendering");
  const [actions, setActions] = useState([]);

  const selectorTrackerRef = useRef(new SelectorTracker());

  const [scrollMode, setScrollMode] = useState("vertical");

  const scrollModeRef = useRef(scrollMode);

  // State for validation
  const [validation, setValidation] = useState({
    valid: true,
    errors: [],
    warnings: [],
    loopInfo: null,
    caseType: null,
  });

  // Get query parameters from URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Extract query parameters
  const queryConfigId = queryParams.get("config_id") || "";
  const queryKbId = queryParams.get("kb_id") || "";
  const queryStatusCode = queryParams.get("status_code") || "";
  const queryScenario = queryParams.get("scenario") || null;
  const queryTemplate = queryParams.get("template") || null;
  const querySampleResponse = queryParams.get("sample_response") || null;

  const queryId = queryParams.get("id") || null;
  const isInitialLoadFlow = useRef(false);

  const viewMode = queryParams.get("view") || null;
  const isAllTemplatesView = viewMode === "all_templates";

    // === Recalculate-Fresh layout-choice popup ===
  // When the user clicks "Recalculate Template Fresh" on the
  // saved-transformations popup, we need to re-ask the Single-vs-
  // Multiple question. Rather than nudging LeftPanel's detection
  // (which races against the URL-param effects), we run the popup
  // directly here and rebuild the template locally.
  const [showRecalcLayoutPopup, setShowRecalcLayoutPopup] = useState(false);
  const [recalcParsedResponse, setRecalcParsedResponse] = useState(null);
  const [recalcDetectedArrays, setRecalcDetectedArrays] = useState(null);


  // Use props if provided, otherwise use query params
  const configIdFromProps =
    initialConfigId !== null ? initialConfigId : queryConfigId;
  const kbIdFromProps = initialKbId !== null ? initialKbId : queryKbId;
  const statusCodeFromProps =
    initialStatusCode !== null ? initialStatusCode : queryStatusCode;
  const scenarioFromProps =
    initialScenario !== null ? initialScenario : queryScenario;
  const templateFromProps =
    initialTemplate !== null ? initialTemplate : queryTemplate;
  const sampleResponseFromProps =
    initialSampleResponse !== null
      ? initialSampleResponse
      : querySampleResponse;
  const [isInitialLoad, setIsInitialLoad] = useState(false);

  // Add near other state declarations
  const [showTransformConfirm, setShowTransformConfirm] = useState(false);
  const [pendingTransformSteps, setPendingTransformSteps] = useState([]);
  const hasShownReapplyPopup = useRef(false);
  const editorWasOpenedRef = useRef(false);

  // State for transformation steps and actions loaded from API
  const [loadedTransformationSteps, setLoadedTransformationSteps] = useState(
    [],
  );
  const [loadedActions, setLoadedActions] = useState([]);

  const [isReapplyFlow, setIsReapplyFlow] = useState(false);

  // ============================================================
  // CRITICAL FIX: Use props FIRST, then query params, then defaults
  // ============================================================

  // Core state - with defaults - prioritized: props > query params > defaults
  const [sampleResponse, setSampleResponse] = useState(() => {
    // Priority 1: Direct prop from parent (modal mode)
    if (propSampleResponse) return propSampleResponse;
    // Priority 2: Initial props passed via modal
    if (initialSampleResponse) return initialSampleResponse;
    // Priority 3: Query params from URL
    if (sampleResponseFromProps) return sampleResponseFromProps;
    // Priority 4: Default
    return DEFAULT_SAMPLE_RESPONSE;
  });

  const [template, setTemplate] = useState(() => {
    // Priority 1: Direct prop from parent
    if (propTemplate) return propTemplate;
    // Priority 2: Initial props passed via modal
    if (initialTemplate) return initialTemplate;
    // Priority 3: Query params from URL
    if (templateFromProps) return templateFromProps;
    // Priority 4: Default
    return DEFAULT_TEMPLATE;
  });

  // Determine scenario based on props/query params - FIXED for modal mode
  const detectScenario = () => {
    // MODAL MODE: Always use LOCAL_APP since we have kb_id and status_code
    if (modalMode) {
      return ENTRY_TYPES.LOCAL_APP;
    }

    if (isAllTemplatesView) {
      return ENTRY_TYPES.LOCAL_APP;
    }
    if (scenarioFromProps) {
      return Object.values(ENTRY_TYPES).includes(scenarioFromProps)
        ? scenarioFromProps
        : ENTRY_TYPES.LOCAL_APP;
    }

    if (configIdFromProps && kbIdFromProps) {
      return ENTRY_TYPES.LOCAL_APP;
    } else if (kbIdFromProps) {
      return ENTRY_TYPES.DEFAULT_APP;
    } else {
      return ENTRY_TYPES.DEFAULT_GLOBAL;
    }
  };

  // State for API config data
  const [apiConfigData, setApiConfigData] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [availableStatusCodes, setAvailableStatusCodes] = useState([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Add action handlers
  const handleAddAction = (newAction) => {
    console.log("[PreviewTester] ➕ Adding action:", newAction);
    setActions((prev) => [...prev, newAction]);
  };

  const handleRemoveAction = (actionId) => {
    console.log("[PreviewTester] 🗑️ Removing action:", actionId);
    setActions((prev) => prev.filter((a) => a.id !== actionId));
  };

  const [renderedPreview, setRenderedPreview] = useState("");
  const [error, setError] = useState(null);
  const [parsedSample, setParsedSample] = useState(null);
  const [flattenedFields, setFlattenedFields] = useState({});

  // Edit Mode state
  const [isEditMode, setIsEditMode] = useState(false);

  // ============================================================
  // CRITICAL: templateLines is parsed ONCE on first load
  // ============================================================
  const [templateLines, setTemplateLines] = useState([]);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [isCase2, setIsCase2] = useState(false);
  const [hasParsedOnce, setHasParsedOnce] = useState(false);

  // === STEP 2a: Track user's screen-type choice ===
  // null       = user hasn't been asked yet (or response doesn't need popup)
  // 'single'   = user chose Single Screen
  // 'multiple' = user chose Multiple Screens
  const [userScreenChoice, setUserScreenChoice] = useState(null);

  // UI state
  const [layoutDirection, setLayoutDirection] = useState("vertical");
  const [parsedData, setParsedData] = useState(null);
  const [dataType, setDataType] = useState("text");
  const [scenarioTree, setScenarioTree] = useState([]);
  const [activePathIds, setActivePathIds] = useState([]);
  const [isSampleArray, setIsSampleArray] = useState(false);
  const [arrayLayoutDirection, setArrayLayoutDirection] = useState("vertical");
  const [isControlsOpen, setIsControlsOpen] = useState(true);

  // ✅ UX: Left panel collapse state — pure layout, no business logic
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Selection & Transformation state
  const [isSelectingMode, setIsSelectingMode] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [selectionType, setSelectionType] = useState(null);
  const [transformationRules, setTransformationRules] = useState([]);
  const [showTransformationPanel, setShowTransformationPanel] = useState(false);
  const [transformedPreview, setTransformedPreview] = useState("");

  // Phase Wizard state
  const [combinedStep, setCombinedStep] = useState(null);
  const [showPhaseWizard, setShowPhaseWizard] = useState(false);
  const hasPreviewData =
    renderedPreview && renderedPreview.length > 0 && !error;

  // Selector Tracker state
  const [selectorTracker] = useState(() => new SelectorTracker());
  const [isReapplying, setIsReapplying] = useState(false);

  // Ref for the preview container
  const previewContainerRef = useRef(null);

  // Scenario state with props/query params as initial values
  const [selectedScenario, setSelectedScenario] = useState(detectScenario());
  const [configId, setConfigId] = useState(configIdFromProps);
  const [kbId, setKbId] = useState(kbIdFromProps);
  const [statusCode, setStatusCode] = useState(statusCodeFromProps);

  // ============================================================
  // PARSE TEMPLATE ONCE ON FIRST LOAD
  // ============================================================
  // ============================================================
  // PARSE TEMPLATE ONCE ON FIRST LOAD - WITH TRANSFORMATIONS
  // ============================================================
  useEffect(() => {
    if (template && parsedSample && !hasParsedOnce) {
      console.log("[PreviewTester] 📝 First load - checking for saved data");

      const hasSavedData = loadedTransformationSteps.length > 0;

      if (hasSavedData) {
        console.log(
          "[PreviewTester] 📝 Using parseTemplateForEditFirstLoadOnly (has saved data)",
        );

        const parsedLines = parseTemplateForEditFirstLoadOnly(
          template,
          parsedSample,
          isCase2,
          null,
          loadedTransformationSteps,
          true,
        );

        setTemplateLines(parsedLines);
        setHasParsedOnce(true);
        isInitialLoadFlow.current = true;

        // ✅ CRITICAL: Instead of calling renderPreview() separately,
        // set the transformation steps and then apply them
        // The applyLoadedTransformations will handle the preview
        if (loadedTransformationSteps.length > 0) {
          // Store the steps to apply
          setPendingTransformSteps(loadedTransformationSteps);
          setShowTransformConfirm(true);
          hasAppliedLoadedTransformations.current = true;
        } else {
          // Only render if no saved transformations
          setTimeout(() => {
            renderPreview();
          }, 50);
        }
      } else {
        console.log(
          "[PreviewTester] 📝 Using parseTemplateForEdit (no saved data)",
        );

        const parsedLines = parseTemplateForEdit(
          template,
          parsedSample,
          isCase2,
          null,
        );

        setTemplateLines(parsedLines);
        setHasParsedOnce(true);
        isInitialLoadFlow.current = false;

        // ✅ Render only if no saved data
        setTimeout(() => {
          renderPreview();
        }, 50);
      }
    }
  }, [
    template,
    parsedSample,
    isCase2,
    hasParsedOnce,
    loadedTransformationSteps,
  ]);

  // ✅ Instead, use this useEffect to apply transformations when popup is confirmed
  useEffect(() => {
    // This will handle the "Apply Saved Transformations" confirmation
    if (showTransformConfirm && pendingTransformSteps.length > 0) {
      // The applyLoadedTransformations will be called from the popup button
      // No need to auto-render here
    }
  }, [showTransformConfirm, pendingTransformSteps]);

  // ============================================================
  // REPARSE WHEN TRANSFORMATION RULES CHANGE (but only if we already parsed)
  // ============================================================

  // ============================================================
  // FORCE LOAD SAMPLE RESPONSE IN MODAL MODE
  // ============================================================
  useEffect(() => {
    if (modalMode && propSampleResponse) {
      console.log(
        "[PreviewTester] 🔄 Modal mode: Loading sample response from props",
      );
      setSampleResponse(propSampleResponse);
      // Trigger render after setting the sample
      setTimeout(() => {
        renderPreview();
      }, 50);
    }
  }, [modalMode, propSampleResponse]);

  // ============================================================
  // TOUR COMPLETION HANDLER
  // ============================================================
  const handleSaveSuccess = (responseData) => {
    console.log("[PreviewTester] ✅ Save successful:", responseData);
    markTourCompleted();
  };

  // ============================================================
  // PHASE WIZARD HANDLERS
  // ============================================================
  const handleStartPhaseWizard = () => {
    console.log("[PreviewTester] 🚀 Starting Phase Wizard");

    // ✅ UX: collapse left panel so the user focuses on the preview
    setIsLeftPanelCollapsed(true);

    if (isEditMode) {
      setIsEditMode(false);
    }

    setIsSelectingMode(true);

    let selector = null;
    if (selectedText && selectedText.trim().length > 0 && selectedContainer) {
      selector = generatePositionSelector(
        selectedText,
        selectedContainer,
        selectedRange,
        1,
      );
      console.log(
        "[PreviewTester] ✅ Generated selector from existing selection",
      );
    } else {
      console.log("[PreviewTester] ⏳ No text selected, creating empty step");
    }

    const newStep = createCombinedStep(selector);
    setCombinedStep(newStep);
    setShowPhaseWizard(true);
    setShowTransformationPanel(false);

    if (selectedText && selectedText.trim().length > 0) {
      console.log("[PreviewTester] ✅ Text already selected, showing panel");
      setShowTransformationPanel(true);
    } else {
      console.log(
        "[PreviewTester] ⏳ No text selected yet, waiting for selection",
      );
    }

    console.log("[PreviewTester] ✅ Phase Wizard started with step:", newStep);
  };

  const handleAddOperationToPhase = (phaseName, operationData) => {
    console.log(
      "[PreviewTester] ➕ Adding operation to phase:",
      phaseName,
      operationData,
    );

    const currentSelectors =
      combinedStep?.selectors ||
      (combinedStep?.selector ? [combinedStep.selector] : []);

    const enrichedOperationData = {
      ...operationData,
      selectors: currentSelectors,
    };

    setCombinedStep((prev) =>
      addOperationToPhase(prev, phaseName, enrichedOperationData),
    );

    const operation = operationData.operation;
    const isStructural = phaseName === "structural";
    const isMedia = phaseName === "media";

    console.log(
      "[PreviewTester] 🔧 Immediately applying transformation:",
      operation,
    );
    console.log("[PreviewTester] 🔧 Operation data:", operationData);

    applyTransformation(operation, isStructural, isMedia, operationData);
  };

  const handleRemoveOperationFromPhase = (phaseName, operationIndex) => {
    console.log(
      "[PreviewTester] 🗑️ Removing operation from phase:",
      phaseName,
      operationIndex,
    );
    setCombinedStep((prev) =>
      removeOperationFromPhase(prev, phaseName, operationIndex),
    );
  };

  const handleNextPhase = () => {
    console.log("[PreviewTester] ➡️ Moving to next phase");
    if (isEditMode) {
      setIsEditMode(false);
    }
    setCombinedStep((prev) => nextPhase(prev));
    setShowTransformationPanel(false);
  };

  const handlePrevPhase = () => {
    console.log("[PreviewTester] ⬅️ Moving to previous phase");
    setCombinedStep((prev) => prevPhase(prev));
  };

  const handleFinalizeCombinedStep = () => {
    if (!combinedStep) {
      console.warn("[PreviewTester] ⚠️ No combined step to finalize");
      return;
    }

    console.log("[PreviewTester] ✅ Finalizing combined step:", combinedStep);

    if (isEditMode) {
      setIsEditMode(false);
    }

    const finalized = finalizeCombinedStep(combinedStep);
    setCombinedStep(finalized);

    const rules = convertCombinedStepToRules(finalized);
    console.log("[PreviewTester] 📋 Converted to rules:", rules);

    setTransformationRules((prev) => [...prev, ...rules]);

    setShowPhaseWizard(false);
    setCombinedStep(null);
    setIsSelectingMode(false);
    setShowTransformationPanel(false);
    setSelectedText("");
    setSelectedRange(null);
    setSelectedContainer(null);
    setSelectionType(null);

    setTimeout(() => {
      renderPreview();
      console.log(
        "[PreviewTester] ✅ Preview re-rendered with transformations applied",
      );

      if (transformationRules.length > 0) {
        console.log(
          `[PreviewTester] 📋 Re-applying ${transformationRules.length} transformations after wizard finalize`,
        );
        setPendingTransformSteps(transformationRules);
        setShowTransformConfirm(true);
      }
    }, 100);

    console.log("[PreviewTester] ✅ Phase Wizard completed");
  };

  const handleCancelWizard = () => {
    console.log("[PreviewTester] ❌ Cancelling Phase Wizard");

    setShowPhaseWizard(false);
    setCombinedStep(null);
    setIsSelectingMode(false);
    setShowTransformationPanel(false);
    setSelectedText("");
    setSelectedRange(null);
    setSelectedContainer(null);
    setSelectionType(null);

    // setTimeout(() => {
    //   renderPreview();
    //   console.log(
    //     "[PreviewTester] ✅ Preview re-rendered - all transformations cleared from preview",
    //   );
    // }, 50);
  };

  // ============================================================
  // CONVERT COMBINED STEP TO RULES - Updated to use minimal schema
  // ============================================================
  const convertCombinedStepToRules = (combinedStep) => {
    if (!combinedStep) return [];

    console.log(
      "[PreviewTester] 📋 Converting combined step to rules:",
      combinedStep.id,
    );

    const rules = [];
    const phases = combinedStep.phases;
    const selectors =
      combinedStep.selectors ||
      (combinedStep.selector ? [combinedStep.selector] : []);

    // ============================================================
    // ✅ Clean selectors to new format
    // ============================================================
    const cleanedSelectors = selectors
      .map((s) => {
        // New format: { start: "path", end: "path" }
        if (typeof s.start === "string" && typeof s.end === "string") {
          return {
            start: s.start,
            end: s.end,
            ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
          };
        }
        // Old format: { start: { elementPath: "path" }, end: { elementPath: "path" } }
        if (s.start?.elementPath && s.end?.elementPath) {
          return {
            start: s.start.elementPath,
            end: s.end.elementPath,
            ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
          };
        }
        // Fallback
        return {
          start: s.start?.elementPath || s.start || "",
          end: s.end?.elementPath || s.end || "",
          ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
        };
      })
      .filter((s) => s.start && s.end);

    // ============================================================
    // ✅ STRUCTURAL PHASE - Use createStructuralStep
    // ============================================================
    if (phases.structural?.operations?.length > 0) {
      console.log(
        `[PreviewTester] 📐 Converting ${phases.structural.operations.length} structural operations`,
      );

      phases.structural.operations.forEach((op) => {
        // Extract fields from the operation
        const fields = op.fields || [];
        const operation = op.operation || "table";

        // Use the schema helper
        const rule = createStructuralStep(
          operation,
          fields,
          cleanedSelectors,
          `struct_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        );

        // Add combined step metadata
        rule.combinedStepId = combinedStep.id;
        rule.phase = "structural";

        rules.push(rule);
        console.log(
          `[PreviewTester] ✅ Created structural rule: ${operation} with ${fields.length} fields`,
        );
      });
    }

    // ============================================================
    // ✅ MEDIA PHASE - Use createMediaStep
    // ============================================================
    if (phases.media?.operations?.length > 0) {
      console.log(
        `[PreviewTester] 🖼️ Converting ${phases.media.operations.length} media operations`,
      );

      phases.media.operations.forEach((op) => {
        // Extract fields and image dimensions
        const fields = op.fields || [];
        const imageSize = op.imageSize || { width: 250, height: 250 };
        const hasImageUrls = op.hasImageUrls || false;

        // Use the schema helper
        const rule = createMediaStep(
          fields,
          cleanedSelectors,
          imageSize,
          `media_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        );

        // Add combined step metadata
        rule.combinedStepId = combinedStep.id;
        rule.phase = "media";
        rule.hasImageUrls = hasImageUrls;

        rules.push(rule);
        console.log(
          `[PreviewTester] ✅ Created media rule with ${fields.length} fields, ${imageSize.width}×${imageSize.height}`,
        );
      });
    }

    // ============================================================
    // ✅ STYLES PHASE - Use createStyleStep
    // ============================================================
    if (phases.styles?.operations?.length > 0) {
      console.log(
        `[PreviewTester] 🎨 Converting ${phases.styles.operations.length} style operations`,
      );

      phases.styles.operations.forEach((op) => {
        // Extract fields from the operation
        const fields = op.fields || [];
        const operation = op.operation || "highlight";

        // Use the schema helper
        const rule = createStyleStep(
          operation,
          fields,
          cleanedSelectors,
          `style_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        );

        // Add combined step metadata
        rule.combinedStepId = combinedStep.id;
        rule.phase = "styles";

        rules.push(rule);
        console.log(
          `[PreviewTester] ✅ Created style rule: ${operation} with ${fields.length} fields`,
        );
      });
    }

    console.log(
      `[PreviewTester] ✅ Converted combined step to ${rules.length} rules`,
    );
    return rules;
  };

  // Refs
  const hasRenderedOnce = useRef(false);
  const layoutChangeRef = useRef(false);

  const isTemplateLoadingFromApi = useRef(false);

  // ============================================================
  // EFFECT 1: FETCH TEMPLATE FROM API CONFIG
  // ============================================================
  useEffect(() => {
    const fetchTemplateFromApi = async () => {
      if (!configId || !statusCode) {
        console.log(
          "[PreviewTester] ⏭️ Skipping API fetch - no config_id or status_code",
        );
        return;
      }

      if (template && template !== DEFAULT_TEMPLATE) {
        console.log(
          "[PreviewTester] ⏭️ Template already exists, skipping API fetch",
        );
        return;
      }

      console.log(
        "[PreviewTester] 📥 Fetching template from final_response_versions for config_id:",
        configId,
        "status:",
        statusCode,
      );

      try {
        const region = "us";
        const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
        const domainBase =
          process.env.REACT_APP_SL_BE_BASE_ENV_PART;
        const token = localStorage.getItem("token") || "";

        const url = `https://${region}.autobot2.${domainBase}/${stackPrefix}/final_response_versions?config_id=${configId}&status_code=${statusCode}`;

        const response = await fetch(url, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data = await response.json();
        console.log(
          "[PreviewTester] ✅ final_response_versions fetched:",
          data,
        );

        const items = data.items || [];
        const matchedItem = items.length > 0 ? items[0] : null;

        if (matchedItem) {
          const templateString = matchedItem.resp_template || "";
          const sampleResponseBody = matchedItem.sample_response || "";

          console.log(
            "[PreviewTester] 📝 Template from final_response_versions:",
            templateString.substring(0, 100) + "...",
          );

          if (templateString) {
            console.log(
              "[PreviewTester] ✅ Setting template from final_response_versions",
            );
            setTemplate(templateString);

            if (sampleResponseBody) {
              setSampleResponse(sampleResponseBody);
            }

            setTimeout(() => {
              console.log(
                "[PreviewTester] 🚀 Auto-rendering after template load...",
              );
              renderPreview();
            }, 300);
          } else {
            console.warn(
              "[PreviewTester] ⚠️ No template found in final_response_versions",
            );
          }
        } else {
          console.log(
            "[PreviewTester] ⚠️ No item found for status:",
            statusCode,
          );
          fetchFromApiConfig();
        }
      } catch (error) {
        console.error(
          "[PreviewTester] ❌ Error fetching from final_response_versions:",
          error,
        );
        fetchFromApiConfig();
      }
    };

    const fetchFromApiConfig = async () => {
      try {
        const region = "us";
        const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
        const domainBase =
          process.env.REACT_APP_SL_BE_BASE_ENV_PART;
        const token = localStorage.getItem("token") || "";

        const url = `https://${region}.autobot2.${domainBase}/${stackPrefix}/api_config?api_config_id=${configId}`;

        const response = await fetch(url, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch API config: ${response.status}`);
        }

        const data = await response.json();
        console.log("[PreviewTester] ✅ API config fetched (fallback):", data);

        const apiConfig = data.api_config || {};

        let templateString = "";
        if (apiConfig.response_template && statusCode) {
          templateString = apiConfig.response_template[statusCode] || "";
          console.log(
            "[PreviewTester] 📝 Template from api_config:",
            templateString.substring(0, 100) + "...",
          );
        }

        if (templateString) {
          setTemplate(templateString);
          setTimeout(() => {
            renderPreview();
          }, 300);
        }
      } catch (error) {
        console.error(
          "[PreviewTester] ❌ Error fetching from api_config:",
          error,
        );
      }
    };

    fetchTemplateFromApi();
  }, [configId, statusCode]);

  const hasAppliedLoadedTransformations = useRef(false);

  // ============================================================
  // FETCH TRANSFORMATION STEPS & ACTIONS FROM final_response
  // ============================================================
  useEffect(() => {
    const fetchTransformationSteps = async () => {
      if (!configId || !statusCode) {
        return;
      }

      try {
        const region = "us";
        const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
        const domainBase =
          process.env.REACT_APP_SL_BE_BASE_ENV_PART;
        const token = localStorage.getItem("token") || "";

        const finalResponseUrl = `https://${region}.autobot2.${domainBase}/${stackPrefix}/final_response_versions?config_id=${configId}&status_code=${statusCode}`;

        console.log(
          "[PreviewTester] 📥 Fetching final_response for config_id:",
          configId,
          "and status_code:",
          statusCode,
        );

        const finalResponse = await fetch(finalResponseUrl, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        if (!finalResponse.ok) {
          console.warn(
            "[PreviewTester] ⚠️ Failed to fetch final_response:",
            finalResponse.status,
          );
          return;
        }

        const finalData = await finalResponse.json();
        console.log("[PreviewTester] ✅ Final response fetched:", finalData);

        const items = finalData.items || [];
        const matchedItem = items.length > 0 ? items[0] : null;

        if (matchedItem) {
          const transformationSteps = matchedItem.transformation_steps || [];
          const actions = matchedItem.actions || [];

          console.log("==================================================");
          console.log("📋 Template Data for Status:", statusCode);
          console.log("==================================================");
          console.log("📝 config_id:", configId);
          console.log("📝 status_code:", statusCode);
          console.log("📝 req_uid:", matchedItem.id);
          console.log("📝 transformation_steps:", transformationSteps);
          console.log(
            "📝 transformation_steps count:",
            transformationSteps.length,
          );
          console.log("📝 actions:", actions);
          console.log("📝 actions count:", actions.length);
          console.log("==================================================");

          const controls = matchedItem.controls || {};
          const arrayLayoutDirection =
            controls.array_layout_direction || "vertical";

          console.log(
            "[PreviewTester] 📐 array_layout_direction from API:",
            arrayLayoutDirection,
          );

          // ✅ ADD THESE 3 LINES - Set initial scroll/layout from saved controls
          if (controls.scroll_mode) {
            setScrollMode(controls.scroll_mode);
            scrollModeRef.current = controls.scroll_mode; // Also update ref
          }
          if (controls.layout_direction) {
            setLayoutDirection(controls.layout_direction);
          }
          if (controls.array_layout_direction) {
            setArrayLayoutDirection(controls.array_layout_direction);
          }

          setLoadedTransformationSteps(transformationSteps);
          setLoadedActions(actions);
          setActions(actions);

          setArrayLayoutDirection(arrayLayoutDirection);
          console.log("📝 array_layout_direction:", arrayLayoutDirection);

          if (transformationSteps.length > 0) {
            setIsInitialLoad(true);
          }

          hasAppliedLoadedTransformations.current = false;
        } else {
          console.log(
            "[PreviewTester] ⚠️ No item found for status:",
            statusCode,
          );
        }
      } catch (error) {
        console.error(
          "[PreviewTester] ❌ Error fetching transformation steps:",
          error,
        );
      }
    };

    fetchTransformationSteps();
  }, [configId, statusCode]);

  // ============================================================
  // AUTO-APPLY LOADED TRANSFORMATIONS - WITH CONFIRMATION
  // ============================================================
  useEffect(() => {
    if (!loadedTransformationSteps || loadedTransformationSteps.length === 0) {
      return;
    }

    if (hasAppliedLoadedTransformations.current) {
      return;
    }

    if (!renderedPreview || renderedPreview.length < 10) {
      return;
    }

    const container = document.querySelector(".item-ui-screen");

    if (!container) {
      console.log(
        "[PreviewTester] ⏳ Container not ready, will check again...",
      );
      const checkAgain = setTimeout(() => {
        const retryContainer = document.querySelector(".item-ui-screen");
        if (retryContainer) {
          console.log(
            "[PreviewTester] ✅ Container found on retry, showing confirmation",
          );
          setPendingTransformSteps(loadedTransformationSteps);
          setShowTransformConfirm(true);
          hasAppliedLoadedTransformations.current = true;
        } else {
          console.warn(
            "[PreviewTester] ⚠️ Container still not found, skipping transformations",
          );
          hasAppliedLoadedTransformations.current = true;
        }
      }, 500);
      return () => clearTimeout(checkAgain);
    }

    console.log("[PreviewTester] ✅ Container found, showing confirmation");
    isInitialLoadFlow.current = true;

    setPendingTransformSteps(loadedTransformationSteps);
    setShowTransformConfirm(true);
    hasAppliedLoadedTransformations.current = true;
  }, [loadedTransformationSteps, renderedPreview]);

  // ============================================================
  // EFFECT 2: AUTO-RENDER ON URL NAVIGATION
  // ============================================================
  useEffect(() => {
    const hasSampleResponse =
      sampleResponse && sampleResponse.trim().length > 0;
    const hasTemplate = template && template.trim().length > 0;

    const isFromUrl = configId || kbId || statusCode;

    if (configId && isTemplateLoadingFromApi.current) {
      console.log("[PreviewTester] ⏳ Waiting for API fetch...");
      return;
    }

    if (configId && template === DEFAULT_TEMPLATE && !template) {
      console.log(
        "[PreviewTester] ⏳ Template is default, waiting for API fetch...",
      );
      return;
    }

    if (
      hasSampleResponse &&
      hasTemplate &&
      isFromUrl &&
      !hasRenderedOnce.current
    ) {
      console.log(
        "[PreviewTester] 🚀 Auto-rendering preview from URL params...",
      );
      hasRenderedOnce.current = true;

      setTimeout(() => {
        if (handleRender) {
          handleRender();
          console.log("[PreviewTester] ✅ Auto-render triggered");
        }
      }, 300);
    }
  }, [sampleResponse, template, configId, kbId, statusCode]);

  function recalculateTransformationSelectors(
    rules,
    editorChanges,
    currentLines,
  ) {
    console.log(
      "[RECALCULATION RELATED TRACKINGS::] recalculateTransformationSelectors START",
    );

    // ✅ Guard against undefined or null rules
    if (!rules || !Array.isArray(rules) || rules.length === 0) {
      console.log(
        "[RECALCULATION RELATED TRACKINGS::] ⏭️ No rules to recalculate",
      );
      return rules || [];
    }

    if (
      !editorChanges ||
      !Array.isArray(editorChanges) ||
      editorChanges.length === 0
    ) {
      console.log("[RECALCULATION RELATED TRACKINGS::] ⏭️ No changes to apply");
      return rules;
    }

    console.log("[RECALCULATION RELATED TRACKINGS::] changes:", editorChanges);
    console.log(
      "[RECALCULATION RELATED TRACKINGS::] transformationRules count:",
      rules.length,
    );

    const result = [];

    // Process each rule
    for (const rule of rules) {
      console.log(
        `[RECALCULATION RELATED TRACKINGS::] 🔧 Reapplying rule: ${rule.id}`,
      );

      if (
        !rule.selectors ||
        !Array.isArray(rule.selectors) ||
        rule.selectors.length === 0
      ) {
        console.log(
          "[RECALCULATION RELATED TRACKINGS::] ⚠️ Rule has no selectors, skipping",
        );
        result.push(rule);
        continue;
      }

      console.log(
        `[RECALCULATION RELATED TRACKINGS::] 📊 Rule has ${rule.selectors.length} selectors`,
      );

      // ✅ FIX: Check rule.type instead of rule.isStructural
      const isStructural =
        rule.type === "structural" || rule.type === "combined";

      // Recalculate the selectors
      const recalculatedSelectors = recalculateSelector(
        rule.selectors,
        editorChanges,
        isStructural,
        currentLines,
      );

      if (recalculatedSelectors && recalculatedSelectors.length > 0) {
        // ✅ Update the rule with recalculated selectors
        const updatedRule = {
          ...rule,
          selectors: recalculatedSelectors,
        };

        // ✅ Also update _metadata.selectorPath if it exists
        if (
          rule._metadata?.selectorPath &&
          recalculatedSelectors[0]?.start?.elementPath
        ) {
          updatedRule._metadata = {
            ...rule._metadata,
            selectorPath: recalculatedSelectors[0].start.elementPath,
          };
        }

        result.push(updatedRule);
        console.log(
          `[RECALCULATION RELATED TRACKINGS::] ✅ Rule updated with ${recalculatedSelectors.length} selectors`,
        );
      } else {
        // If recalculated selectors came back empty, the target was deleted
        console.log(
          "[RECALCULATION RELATED TRACKINGS::] 🗑️ Rule target was deleted, removing rule",
        );
        // Don't push to result - this removes the rule
      }
    }

    console.log(
      `[RECALCULATION RELATED TRACKINGS::] ✅ Recalculation complete. Rules: ${result.length} (was ${rules.length})`,
    );

    return result;
  }


  const handleTrackChange = useCallback(
    (change) => {
      console.log(
        "[RECALCULATION RELATED TRACKINGS::] 📝 handleTrackChange:",
        change,
      );

      // ============================================================
      // ✅ DEV-ONLY GUARD — 6B
      // INVARIANT: A change's path must not be identical to any
      //            existing rule's loopSelector.
      //
      // Reason: If they collide, the change was almost certainly
      // produced by a path-generation bug (as happened when a button
      // added after an inner loop reused the inner loop's own path).
      // A collision here means the recalculation will shift the loop
      // for the wrong reason.
      //
      // Owned by: generateSelectorPathForRow in TemplateEditor.js
      // ============================================================
      if (process.env.NODE_ENV !== "production") {
        const changePath =
          change.selectorPath ||
          change.start_path ||
          change.newSelectorPath ||
          change.oldSelectorPath ||
          "";

        if (changePath && Array.isArray(transformationRules)) {
          for (const rule of transformationRules) {
            const selectors = rule?.selectors || [];
            for (const sel of selectors) {
              const loopSel =
                typeof sel.loopSelector === "string"
                  ? sel.loopSelector
                  : sel.loopSelector?.elementPath || "";

              if (loopSel && loopSel === changePath) {
                console.warn(
                  "[INVARIANT:6B] Path collision — a change targets the exact same path as an existing loopSelector.",
                  {
                    ruleId: rule.id,
                    ruleType: rule.type,
                    collidingPath: changePath,
                    changeType: change.type,
                  },
                );
              }
            }
          }
        }
      }

      // Ensure transformationRules is always an array
      const currentRules = transformationRules || [];

      // Record the change
      if (selectorTrackerRef.current) {
        selectorTrackerRef.current.recordChange({
          type: change.type,
          start_path:
            change.selectorPath ||
            change.start_path ||
            change.oldSelectorPath ||
            "",
          end_path:
            change.newSelectorPath ||
            change.end_path ||
            change.selectorPath ||
            "",
          ...change,
        });
      }

      // Trigger recalculation
      const changes = selectorTrackerRef.current?.getChanges() || [];

      if (changes.length > 0 && currentRules.length > 0) {
        console.log(
          "[RECALCULATION RELATED TRACKINGS::] 🔄 Triggering recalculation",
        );
        console.log("[RECALCULATION RELATED TRACKINGS::] changes:", changes);
        console.log(
          "[RECALCULATION RELATED TRACKINGS::] transformationRules count:",
          currentRules.length,
        );

        const recalculatedRules = recalculateTransformationSelectors(
          currentRules,
          changes,
          templateLines,
        );

        if (recalculatedRules && recalculatedRules.length > 0) {
          console.log(
            `[RECALCULATION RELATED TRACKINGS::] ✅ Setting ${recalculatedRules.length} recalculated rules`,
          );
          setTransformationRules(recalculatedRules);
        } else {
          console.log(
            "[RECALCULATION RELATED TRACKINGS::] ⚠️ No rules after recalculation, clearing",
          );
          setTransformationRules([]);
        }

        selectorTrackerRef.current?.clearChanges();
      } else if (changes.length > 0 && currentRules.length === 0) {
        console.log(
          "[RECALCULATION RELATED TRACKINGS::] ⏭️ No transformations to reapply",
        );
        selectorTrackerRef.current?.clearChanges();
      }
    },
    [transformationRules, templateLines, setTransformationRules],
  );


  // === STEP 2b: DATA-DRIVEN CASE CLASSIFICATION ===
  // Case type is now derived from:
  //   1. The data shape (array / primitive / object)
  //   2. The user's explicit screen-type choice (for objects with nested arrays)
  //
  // It NO LONGER inspects the template's loop structure to decide the case —
  // the template only determines whether the loop budget is respected.
  //
  //   • unclassified  → empty / null / invalid JSON
  //   • case1         → root array (forced multiple screens)
  //   • case2         → primitive, flat object, or nested object with single screen
  //   • case3         → nested object with user-chosen multiple screens
  const computeCaseAndValidate = useCallback(
    (template, parsedSample) => {
      let caseType = "unclassified";

      // ============================================================
      // 1. UNCLASSIFIED — empty / null / invalid
      // ============================================================
      const isEmpty =
        parsedSample === null ||
        parsedSample === undefined ||
        (typeof parsedSample === "string" && parsedSample.trim() === "") ||
        (typeof parsedSample === "object" &&
          !Array.isArray(parsedSample) &&
          Object.keys(parsedSample).length === 0 &&
          typeof parsedSample !== "function");

      // Note: an empty object `{}` IS a valid object — do not treat as unclassified.
      // Only truly empty / null / invalid JSON gets unclassified.
      const isTrulyEmpty = parsedSample === null || parsedSample === undefined;

      const isInvalidJson = false; // parse failures are caught upstream

      if (isTrulyEmpty) {
        caseType = "unclassified";
      }
      // ============================================================
      // 2. ROOT ARRAY → case1 (forced multiple screens)
      // ============================================================
      else if (Array.isArray(parsedSample)) {
        caseType = "case1";
      }
      // ============================================================
      // 3. PRIMITIVE (string / number / boolean) → case2
      // ============================================================
      else if (typeof parsedSample !== "object") {
        caseType = "case2";
      }
      // ============================================================
      // 4. OBJECT → decide based on nested arrays + user choice
      // ============================================================
      else {
        const hasNestedArray = Object.values(parsedSample).some(
          (v) => Array.isArray(v) && v.length > 0,
        );

        if (!hasNestedArray) {
          // Flat object → single screen (case2)
          caseType = "case2";
        } else {
          // Nested array exists → depends on user's choice.
          // When no runtime choice exists (e.g. reload from DB), infer
          // from the template's own loop depth: a nested loop means the
          // saved shape is multi-screen (case3).
          if (userScreenChoice === "single") {
            caseType = "case2";
          } else if (userScreenChoice === "multiple") {
            caseType = "case3";
          } else {
            const loopInfo = TemplateValidator.analyzeLoops(template);
            caseType = loopInfo.maxDepth >= 2 ? "case3" : "case2";
          }
        }
      }

      // ============================================================
      // 5. RUN THE VALIDATOR
      // ============================================================
      const result = TemplateValidator.validateForCase(template, caseType);
      setValidation({ ...result, caseType });

      return { caseType, validation: result };
    },
    [userScreenChoice],
  );

  // === STEP 2c: Re-validate when template OR data OR user choice changes ===
  useEffect(() => {
    if (template && parsedSample !== null && parsedSample !== undefined) {
      computeCaseAndValidate(template, parsedSample);
    }
  }, [template, parsedSample, userScreenChoice, computeCaseAndValidate]);

  useEffect(() => {
    if (parsedSample) {
      const flattened = flattenObject({ response_data: parsedSample });
      setFlattenedFields(flattened);
      const variableNames = Object.keys(flattened).map((key) =>
        key.replace("response_data.", ""),
      );
      setAvailableVariables(variableNames);
    }
  }, [parsedSample]);

  useEffect(() => {
    console.log(
      "[PreviewTester] 📋 Transformation rules updated, count:",
      transformationRules.length,
    );
    if (transformationRules.length > 0) {
      console.log(
        "[PreviewTester] 📋 Rules:",
        transformationRules.map((r) => r.operation).join(", "),
      );
      const firstRule = transformationRules[0];
      if (firstRule) {
        console.log("[PreviewTester] 📋 First rule type:", firstRule.type);
        console.log(
          "[PreviewTester] 📋 First rule operation:",
          firstRule.operation,
        );
        console.log(
          "[PreviewTester] 📋 First rule originalText length:",
          firstRule.originalText?.length || 0,
        );
        console.log(
          "[PreviewTester] 📋 First rule transformedText length:",
          firstRule.transformedText?.length || 0,
        );
        if (firstRule.selector) {
          console.log(
            "[PreviewTester] 📋 First rule selector:",
            firstRule.selector,
          );
        }
      }
    }
  }, [transformationRules]);

  // PreviewTester/PreviewTester.js

  // Add this useEffect near other useEffect hooks (around line 500-600)

  // Update the ref whenever scrollMode changes
  useEffect(() => {
    scrollModeRef.current = scrollMode;
  }, [scrollMode]);

  // PreviewTester/PreviewTester.js

  // ============================================================
  // WATCH FOR LAYOUT/DIRECTION CHANGES - Show re-apply popup
  // ============================================================
  useEffect(() => {
    // ✅ Skip if no transformations exist
    const hasTransformations =
      transformationRules.length > 0 || loadedTransformationSteps.length > 0;

    if (!hasTransformations) {
      return;
    }

    // ✅ Skip if we're already showing the popup or applying
    if (showTransformConfirm || isApplyingTransformations.current) {
      return;
    }

    // ✅ Check if layout/direction changed from what was saved
    // We track the last applied layout to detect changes
    const currentLayout = {
      scrollMode,
      arrayLayoutDirection,
      layoutDirection,
    };

    // Store previous layout in a ref
    if (!layoutChangeRef.current) {
      layoutChangeRef.current = currentLayout;
      // ✅ Also update scrollModeRef with initial value
      scrollModeRef.current = scrollMode;
      return;
    }

    const prevLayout = layoutChangeRef.current;

    // Check if any layout setting changed
    const hasLayoutChanged =
      prevLayout.scrollMode !== currentLayout.scrollMode ||
      prevLayout.arrayLayoutDirection !== currentLayout.arrayLayoutDirection ||
      prevLayout.layoutDirection !== currentLayout.layoutDirection;

    if (hasLayoutChanged) {
      console.log("[PreviewTester] 🔄 Layout changed, showing re-apply popup");
      console.log("[PreviewTester] 📊 Previous:", prevLayout);
      console.log("[PreviewTester] 📊 Current:", currentLayout);

      // ✅ Update scrollModeRef to the CURRENT value before showing popup
      scrollModeRef.current = scrollMode;

      // ✅ Show the popup with current transformations
      const stepsToApply =
        transformationRules.length > 0
          ? transformationRules
          : loadedTransformationSteps;

      if (stepsToApply.length > 0) {
        // ✅ Pass the current scrollMode with the steps
        const stepsWithCurrentScroll = stepsToApply.map((step) => ({
          ...step,
          _scrollMode: scrollModeRef.current, // ✅ Use the ref value (current)
          _arrayLayoutDirection: arrayLayoutDirection,
          _layoutDirection: layoutDirection,
        }));

        setPendingTransformSteps(stepsWithCurrentScroll);
        setShowTransformConfirm(true);
      }

      // Update the ref
      layoutChangeRef.current = currentLayout;
    }
  }, [
    scrollMode,
    arrayLayoutDirection,
    layoutDirection,
    transformationRules,
    loadedTransformationSteps,
    showTransformConfirm,
  ]);

  // ============================================================
  // WATCH FOR LAYOUT/DIRECTION CHANGES - Show re-apply popup
  // ============================================================
  // PreviewTester/PreviewTester.js

  // ============================================================
  // WATCH FOR LAYOUT/DIRECTION CHANGES - Show re-apply popup
  // ============================================================
  useEffect(() => {
    // ✅ Skip if no transformations exist
    const hasTransformations =
      transformationRules.length > 0 || loadedTransformationSteps.length > 0;

    if (!hasTransformations) {
      return;
    }

    // ✅ Skip if we're already showing the popup or applying
    if (showTransformConfirm || isApplyingTransformations.current) {
      return;
    }

    // ✅ Check if layout/direction changed from what was saved
    // We track the last applied layout to detect changes
    const currentLayout = {
      scrollMode,
      arrayLayoutDirection,
      layoutDirection,
    };

    // Store previous layout in a ref
    if (!layoutChangeRef.current) {
      layoutChangeRef.current = currentLayout;
      // ✅ Also update scrollModeRef with initial value
      scrollModeRef.current = scrollMode;
      return;
    }

    const prevLayout = layoutChangeRef.current;

    // Check if any layout setting changed
    const hasLayoutChanged =
      prevLayout.scrollMode !== currentLayout.scrollMode ||
      prevLayout.arrayLayoutDirection !== currentLayout.arrayLayoutDirection ||
      prevLayout.layoutDirection !== currentLayout.layoutDirection;

    if (hasLayoutChanged) {
      console.log("[PreviewTester] 🔄 Layout changed, showing re-apply popup");
      console.log("[PreviewTester] 📊 Previous:", prevLayout);
      console.log("[PreviewTester] 📊 Current:", currentLayout);

      // ✅ Update scrollModeRef to the CURRENT value before showing popup
      scrollModeRef.current = scrollMode;

      // ✅ Show the popup with current transformations
      const stepsToApply =
        transformationRules.length > 0
          ? transformationRules
          : loadedTransformationSteps;

      if (stepsToApply.length > 0) {
        // ✅ Pass the current scrollMode with the steps
        const stepsWithCurrentScroll = stepsToApply.map((step) => ({
          ...step,
          _scrollMode: scrollModeRef.current, // ✅ Use the ref value (current)
          _arrayLayoutDirection: arrayLayoutDirection,
          _layoutDirection: layoutDirection,
        }));

        setPendingTransformSteps(stepsWithCurrentScroll);
        setShowTransformConfirm(true);
      }

      // Update the ref
      layoutChangeRef.current = currentLayout;
    }
  }, [
    scrollMode,
    arrayLayoutDirection,
    layoutDirection,
    transformationRules,
    loadedTransformationSteps,
    showTransformConfirm,
  ]);

  // ============================================================
  // WATCH FOR DIRECTION CHANGES - Trigger re-apply popup
  // ============================================================
  useEffect(() => {
    const fetchApiConfig = async () => {
      console.log("wasthisfireedddd?");
      if (!configId) return;

      setLoadingConfig(true);
      setConfigError(null);
      setAvailableStatusCodes([]);

      try {
        const region = process.env.REACT_APP_REGION || "us";
        const token = localStorage.getItem("token") || "";

        const url = `https://${region}.autobot2.${backend_api_base_url}/api_config?api_config_id=${configId}`;

        const response = await fetch(url, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setApiConfigData(data);

          const sampleResponses = data.api_config?.sample_responses_by_status;
          if (sampleResponses && typeof sampleResponses === "object") {
            const statusCodes = Object.keys(sampleResponses);
            setAvailableStatusCodes(statusCodes);

            if (statusCode && sampleResponses[statusCode]) {
              setSampleResponse(sampleResponses[statusCode]);
              console.log(
                `[DEBUG] Loaded sample response for status ${statusCode}:`,
                sampleResponses[statusCode],
              );
            } else if (statusCodes.length > 0 && !statusCode) {
              setShowStatusDropdown(true);
              if (statusCodes.length === 1) {
                const firstStatus = statusCodes[0];
                setStatusCode(firstStatus);
                setSampleResponse(sampleResponses[firstStatus]);
                setShowStatusDropdown(false);
                updateUrlParams(selectedScenario, configId, kbId, firstStatus);
              }
            } else if (statusCode && !sampleResponses[statusCode]) {
              setConfigError(
                `No sample response found for status ${statusCode}`,
              );
              setShowStatusDropdown(true);
            }
          } else {
            setConfigError(`No sample responses found in API config`);
            setShowStatusDropdown(false);
          }
        } else {
          setConfigError(`Failed to fetch API config: ${response.status}`);
        }
      } catch (err) {
        console.error("[DEBUG] Error fetching API config:", err);
        setConfigError(err.message);
      } finally {
        setLoadingConfig(false);
      }
    };

    fetchApiConfig();
  }, [configId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      renderPreview();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const newScenario = detectScenario();
    setSelectedScenario(newScenario);
    setConfigId(configIdFromProps);
    setKbId(kbIdFromProps);
    setStatusCode(statusCodeFromProps);
    if (templateFromProps) {
      setTemplate(templateFromProps);
    }
    if (sampleResponseFromProps && !isAllTemplatesView) {
      setSampleResponse(sampleResponseFromProps);
    }
  }, [
    location.search,
    configIdFromProps,
    kbIdFromProps,
    statusCodeFromProps,
    scenarioFromProps,
    templateFromProps,
    sampleResponseFromProps,
  ]);

  const handleStatusSelect = (selectedStatus) => {
    setStatusCode(selectedStatus);
    setShowStatusDropdown(false);

    if (apiConfigData?.api_config?.sample_responses_by_status) {
      const sampleResponses =
        apiConfigData.api_config.sample_responses_by_status;
      if (sampleResponses[selectedStatus]) {
        setSampleResponse(sampleResponses[selectedStatus]);
        setConfigError(null);
        console.log(
          `[DEBUG] Loaded sample response for status ${selectedStatus}`,
        );
        updateUrlParams(selectedScenario, configId, kbId, selectedStatus);
      }
    }
  };

  // ============================================================
  // APPLY LOADED TRANSFORMATIONS (called after user confirms)
  // ============================================================
  // ============================================================
  // APPLY LOADED TRANSFORMATIONS (called after user confirms)
  // ============================================================
  const convertButtonGroupStepsToTemplateLines = useCallback(
    (steps, currentTemplateLines) => {
      console.log(
        "[🔘 BUTTON GROUP] Converting button group steps to template lines",
      );
      console.log("[🔘 BUTTON GROUP] Steps to convert:", steps.length);
      console.log(
        "[🔘 BUTTON GROUP] Current template lines:",
        currentTemplateLines?.length || 0,
      );

      // ============================================================
      // ✅ Filter for button group steps - supports both old and new formats
      // ============================================================
      const buttonGroupSteps = steps.filter(
        (step) =>
          step.type === "button-group" &&
          // New format: just type === 'button-group'
          (!step.isButtonGroupTransformation ||
            // Old format: has isButtonGroupTransformation flag
            step.isButtonGroupTransformation === true),
      );

      if (buttonGroupSteps.length === 0) {
        console.log("[🔘 BUTTON GROUP] No button group steps to convert");
        return currentTemplateLines;
      }

      console.log(
        `[🔘 BUTTON GROUP] Found ${buttonGroupSteps.length} button group steps to convert`,
      );

      let newLines = [...currentTemplateLines];

      for (const step of buttonGroupSteps) {
        console.log("[🔘 BUTTON GROUP] Processing step:", step.id || step);

        // ============================================================
        // ✅ Get group name - supports both old and new formats
        // ============================================================
        const groupName =
          step._metadata?.groupName ||
          step.groupName ||
          step.originalText ||
          "Button Group";

        console.log("[🔘 BUTTON GROUP] Group name:", groupName);

        // ============================================================
        // ✅ Get buttons - supports both old (objects) and new (strings) formats
        // ============================================================
        let buttons = step.buttons || [];

        console.log("[🔘 BUTTON GROUP] Raw buttons:", buttons);
        console.log("[🔘 BUTTON GROUP] Buttons type:", typeof buttons[0]);

        // If buttons are strings (new format), convert to objects with IDs
        if (buttons.length > 0 && typeof buttons[0] === "string") {
          buttons = buttons.map((text, idx) => ({
            id: `btn-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
            text: text,
            index: idx,
          }));
          console.log(
            `[🔘 BUTTON GROUP] Converted ${buttons.length} string buttons to objects`,
          );
        } else if (buttons.length > 0 && typeof buttons[0] === "object") {
          // Old format: buttons are already objects, ensure they have the right structure
          buttons = buttons.map((btn, idx) => ({
            id:
              btn.id ||
              `btn-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
            text: btn.text || String(btn) || `Button ${idx + 1}`,
            index: btn.index !== undefined ? btn.index : idx,
            selector: btn.selector || "",
          }));
          console.log(
            `[🔘 BUTTON GROUP] Normalized ${buttons.length} object buttons`,
          );
        } else {
          // Fallback: no buttons, create default
          buttons = [
            {
              id: `btn-${Date.now()}-0-${Math.random().toString(36).substr(2, 4)}`,
              text: "Button 1",
              index: 0,
            },
            {
              id: `btn-${Date.now()}-1-${Math.random().toString(36).substr(2, 4)}`,
              text: "Button 2",
              index: 1,
            },
          ];
          console.log("[🔘 BUTTON GROUP] Created default buttons");
        }

        // ============================================================
        // ✅ Get group row ID - supports both old and new formats
        // ============================================================
        const groupRowId =
          step.groupLocation?.groupRowId ||
          step._metadata?.groupRowId ||
          step.groupLocation?.groupId ||
          step.id ||
          step.groupId ||
          `button-group-${Date.now()}`;

        console.log("[🔘 BUTTON GROUP] Group row ID:", groupRowId);

        // ============================================================
        // ✅ Check if this group already exists in template lines
        // ============================================================
        const existingIndex = newLines.findIndex(
          (line) =>
            line.type === "button-group" &&
            (line.id === groupRowId || line.content === groupName),
        );

        if (existingIndex !== -1) {
          // ✅ Update existing button group
          const existingLine = newLines[existingIndex];
          console.log(
            `[🔘 BUTTON GROUP] Updating existing button group at index ${existingIndex}`,
          );
          console.log(
            `[🔘 BUTTON GROUP] Existing buttons: ${existingLine.buttons?.length || 0}`,
          );
          console.log(`[🔘 BUTTON GROUP] New buttons: ${buttons.length}`);

          newLines[existingIndex] = {
            ...existingLine,
            buttons: buttons,
            content: groupName,
            trimmed: groupName,
            // Preserve existing properties
            type: "button-group",
            isButtonGroup: true,
            isCustom: true,
            customId: existingLine.customId || groupRowId,
            customMessage: groupName,
          };
          console.log(
            `[🔘 BUTTON GROUP] Updated existing button group: ${groupName} with ${buttons.length} buttons`,
          );
        } else {
          // ✅ Create new button group row
          console.log(
            `[🔘 BUTTON GROUP] Creating new button group: ${groupName}`,
          );

          const newRow = {
            id: groupRowId,
            content: groupName,
            trimmed: groupName,
            type: "button-group",
            indent: "",
            isLoopStart: false,
            isLoopEnd: false,
            loopVariable: null,
            loopArray: null,
            condition: null,
            variables: [],
            isInForLoop: false,
            isInIfBlock: false,
            depth: 0,
            originalIndex: -1,
            isCustom: true,
            isButtonGroup: true,
            customId: groupRowId,
            customMessage: groupName,
            buttons: buttons,
            // Additional properties for tracking
            _sourceType: "button-group",
            _convertedFromStep: step.id,
          };

          // ✅ Insert after existing button groups (keep them together)
          let insertIndex = newLines.length;
          const firstButtonGroupIndex = newLines.findIndex(
            (line) => line.type === "button-group",
          );
          if (firstButtonGroupIndex !== -1) {
            let lastButtonGroupIndex = firstButtonGroupIndex;
            for (let i = firstButtonGroupIndex; i < newLines.length; i++) {
              if (newLines[i].type === "button-group") {
                lastButtonGroupIndex = i;
              } else {
                break;
              }
            }
            insertIndex = lastButtonGroupIndex + 1;
            console.log(
              `[🔘 BUTTON GROUP] Inserting after existing button groups at index ${insertIndex}`,
            );
          } else {
            console.log(
              "[🔘 BUTTON GROUP] No existing button groups found, appending to end",
            );
          }

          newLines.splice(insertIndex, 0, newRow);
          console.log(
            `[🔘 BUTTON GROUP] Created new button group row: ${groupName} with ${buttons.length} buttons`,
          );
        }
      }

      // ============================================================
      // ✅ Final cleanup: Ensure all button group rows have proper structure
      // ============================================================
      const finalLines = newLines.map((line) => {
        if (line.type === "button-group" && line.buttons) {
          // Ensure each button has required properties
          line.buttons = line.buttons.map((btn, idx) => ({
            id:
              btn.id ||
              `btn-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 4)}`,
            text: btn.text || `Button ${idx + 1}`,
            index: btn.index !== undefined ? btn.index : idx,
            selector: btn.selector || "",
          }));
          // Ensure consistent type
          line.isButtonGroup = true;
          line.type = "button-group";
        }
        return line;
      });

      console.log("[🔘 BUTTON GROUP] Conversion complete!");
      console.log(
        "[🔘 BUTTON GROUP] Lines before:",
        currentTemplateLines?.length || 0,
      );
      console.log("[🔘 BUTTON GROUP] Lines after:", finalLines.length);
      console.log(
        "[🔘 BUTTON GROUP] Button groups in final:",
        finalLines.filter((l) => l.type === "button-group").length,
      );

      return finalLines;
    },
    [],
  );

  const isApplyingTransformations = useRef(false);

  // PreviewTester.js - applyLoadedTransformations

  // PreviewTester.js - Updated applyLoadedTransformations with multi-select styles support

  // ============================================================
  // SHARED RENDERING FUNCTION - Used by both renderPreview and applyLoadedTransformations
  // ============================================================
  // ============================================================
  // SHARED RENDERING FUNCTION - Used by both renderPreview and applyLoadedTransformations
  // ============================================================
  // ============================================================
  // SHARED RENDERING FUNCTION - Used by both renderPreview and applyLoadedTransformations
  // ============================================================
  // PreviewTester.js - Updated renderScreens

  // PreviewTester.js - Full renderScreens with fix

  // PreviewTester.js - Updated renderScreens

  const renderScreens = useCallback(
    (
      enhancedTemplate,
      parsedSample,
      effectiveArrayData,
      isCase3WithMetadata,
      isCase2,
      linesToUse,
      shouldTreatAsArray = true,
    ) => {
      console.log("[renderScreens] 📊 Rendering screens");

      // ============================================================
      // Single screen (Case 2)
      // ============================================================
      if (
        !shouldTreatAsArray ||
        !effectiveArrayData ||
        !Array.isArray(effectiveArrayData) ||
        effectiveArrayData.length === 0
      ) {
        console.log("[renderScreens] 📊 Single screen rendering (Case 2)");
        const context = { response_data: parsedSample, __iterIndex: 0 };
        let fullRendered = nunjucksEnv.renderString(enhancedTemplate, context);

        if (fullRendered && typeof fullRendered === "string") {
          fullRendered = fullRendered.replace(/&gt;/g, ">");
          fullRendered = fullRendered.replace(/&lt;/g, "<");
          fullRendered = fullRendered.replace(/&amp;/g, "&");
          fullRendered = fullRendered.replace(/&quot;/g, '"');
        }

        let wrappedContent = wrapFieldValuesWithSpans(
          fullRendered,
          linesToUse,
          false,
          false,
        );

        // ✅ Case 2 emits the iter wrapper inline in the template (see
        //    templateEnhancer.js), so boundary empty-lines need cleanup
        //    here. Position-aware strip preserves user-intentional
        //    interior blank lines.
        const shouldWrapIterationsCase2 = /data-wrap-iterations="true"/.test(
          enhancedTemplate,
        );
        if (shouldWrapIterationsCase2) {
          wrappedContent = stripStructuralEmptyLines(wrappedContent);
        }

        return `<div class="item-ui-screen" data-item-ui-index="0"><div class="contentWraps">${wrappedContent}</div></div>`;
      }

      // ============================================================
      // Multiple screens (Case 1/3)
      // ============================================================
      console.log(
        `[renderScreens] 📊 Rendering ${effectiveArrayData.length} items (Case 1/3)`,
      );

      const enhancedLines = enhancedTemplate.split("\n");
      let outerLoopStartLine = -1;
      let outerLoopEndLine = -1;
      let loopDepth = 0;
      let loopVarName = "item";
      let loopIterable = "response_data";

      // Find the outer loop
      for (let i = 0; i < enhancedLines.length; i++) {
        const trimmed = enhancedLines[i].trim();
        const isForLoopStart =
          /{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/.test(trimmed);
        const isForLoopEnd = /{%\s*endfor\s*%}/.test(trimmed);

        if (isForLoopStart && outerLoopStartLine === -1) {
          outerLoopStartLine = i;
          const match = trimmed.match(
            /{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/,
          );
          if (match) {
            loopVarName = match[1];
            loopIterable = match[2].trim();
          }
          loopDepth++;
          continue;
        }

        if (isForLoopEnd && loopDepth > 0) {
          loopDepth--;
          if (loopDepth === 0 && outerLoopEndLine === -1) {
            outerLoopEndLine = i;
          }
          continue;
        }
      }

      // ✅ Extract loop body
      let loopBodyLines = [];
      if (
        outerLoopStartLine !== -1 &&
        outerLoopEndLine !== -1 &&
        outerLoopStartLine < outerLoopEndLine
      ) {
        loopBodyLines = enhancedLines.slice(
          outerLoopStartLine + 1,
          outerLoopEndLine,
        );
      }

      // If no loop body found, use fallback
      let loopBody =
        loopBodyLines.length > 0
          ? loopBodyLines.join("\n")
          : enhancedTemplate
            .replace(/{%\s*for\s+[a-zA-Z_]+\s+in\s+[^%]+\s*%}/g, "")
            .replace(/{%\s*endfor\s*%}/g, "");

      const hasInnerLoop = /{%\s*for\s+[a-zA-Z_]+\s+in\s+/.test(loopBody);
      const iterablePath = loopIterable.split(".");
      const isRootArray =
        iterablePath.length === 1 && iterablePath[0] === "response_data";

      const shouldWrapIterations = /data-wrap-iterations="true"/.test(
        enhancedTemplate,
      );

      const screenHTMLs = [];

      for (let index = 0; index < effectiveArrayData.length; index++) {
        const item = effectiveArrayData[index];
        console.log(`[renderScreens] 🔍 Rendering item ${index}`);

        let bodyContext = {};

        if (isRootArray) {
          bodyContext = { response_data: [item] };
        } else if (
          iterablePath.length === 2 &&
          iterablePath[0] === "response_data"
        ) {
          const arrayKey = iterablePath[1];
          if (isCase3WithMetadata) {
            bodyContext = {
              response_data: {
                ...parsedSample,
                [arrayKey]: [item],
              },
            };
          } else {
            bodyContext = {
              response_data: {
                [arrayKey]: [item],
              },
            };
          }
        } else {
          bodyContext = { response_data: [item] };
        }

        let itemRendered = "";

        // ✅ Render ONLY the loop body
        if (loopBody && loopBody.trim()) {
          try {
            let renderContext = {};
            if (hasInnerLoop) {
              // ✅ Inject __iterIndex so the per-iteration wrapper resolves
              renderContext = { ...bodyContext, __iterIndex: index };
            } else {
              renderContext = {
                [loopVarName]: item,
                __iterIndex: index,
              };
            }
            itemRendered = nunjucksEnv.renderString(loopBody, renderContext);
          } catch (err) {
            // ============================================================
            // ⚠️ EXPECTED WARNING — DO NOT SUPPRESS
            //
            // This fires on essentially every Case 1/3 render because the
            // extracted loopBody is a fragment (missing its outer {% for %} /
            // {% endfor %} markers), and Nunjucks occasionally throws
            // "unexpected end of file" when a fragment contains certain tag
            // combinations — usually the {{ loop.index0 }} inside an inline
            // per-iteration wrapper div.
            //
            // The fallback below (full-template render) handles this correctly
            // and produces the expected HTML. This warning is intentionally
            // kept loud so that:
            //   1. Any FUTURE deviation (different error, extra failures) is
            //      immediately noticeable against this baseline.
            //   2. Anyone debugging a render issue sees the fallback path is
            //      active and working.
            //
            // If you're here because the warning is noisy — it is. Do not
            // suppress it. Fix the underlying cause (or document why it can't
            // be fixed) instead. See the two-tier rendering strategy in
            // renderScreens().
            // ============================================================
            console.warn(
              `[renderScreens] ⚠️ Loop body render failed:`,
              err.message,
            );
          }
        }

        // Fallback: render full template
        if (!itemRendered || !itemRendered.trim()) {
          try {
            itemRendered = nunjucksEnv.renderString(enhancedTemplate, {
              ...bodyContext,
              __iterIndex: index,
            });
          } catch (err) {
            console.warn(
              `[renderScreens] ⚠️ Full template render failed:`,
              err.message,
            );
            itemRendered = JSON.stringify(item, null, 2);
          }
        }

        // ✅ Post-process
        if (itemRendered && typeof itemRendered === "string") {
          itemRendered = itemRendered.replace(/&gt;/g, ">");
          itemRendered = itemRendered.replace(/&lt;/g, "<");
          itemRendered = itemRendered.replace(/&amp;/g, "&");
          itemRendered = itemRendered.replace(/&quot;/g, '"');
        }

        // If still empty, use JSON fallback
        if (!itemRendered || !itemRendered.trim()) {
          itemRendered = JSON.stringify(item, null, 2);
        }

        // ============================================================
        // ✅ Strip the SINGLE structural newline that the enhancer's
        //    template layout produces at the top/bottom of the loop
        //    body. Because the enhancer does NOT emit an inline
        //    <div data-iter-id> for Case 1/3, whatever blank lines
        //    remain are user-authored and must survive.
        // ============================================================
        const renderedLines = itemRendered.split("\n");

        if (renderedLines.length > 0 && renderedLines[0].trim() === "") {
          renderedLines.shift();
          console.log(
            "[renderScreens] 🗑️ Removed leading structural empty line",
          );
        }

        if (
          renderedLines.length > 0 &&
          renderedLines[renderedLines.length - 1].trim() === ""
        ) {
          renderedLines.pop();
          console.log(
            "[renderScreens] 🗑️ Removed trailing structural empty line",
          );
        }

        const cleanedRendered = renderedLines.join("\n");

        // ============================================================
        // ✅ Wrap field values. User-intentional blank lines (if any)
        //    become interior empty-line rows and are preserved.
        // ============================================================
        let wrappedBody = wrapFieldValuesWithSpans(
          cleanedRendered,
          linesToUse,
          true,
          true,
        );

        // ============================================================
        // ✅ Strip structural empty-lines that the enhancer's inline
        //    iteration wrappers (for inner loops) may have introduced.
        //    This is a no-op when there are no [data-iter-id] elements.
        //    Must run BEFORE the outer JS wrapper is added.
        // ============================================================
        if (shouldWrapIterations) {
          wrappedBody = stripStructuralEmptyLines(wrappedBody);
        }

        const screenHTML = `<div class="item-ui-screen" data-item-ui-index="${index}"><div class="contentWraps">${wrappedBody}</div></div>`;
        screenHTMLs.push(screenHTML);
      }

      return screenHTMLs.join("\n");
    },
    [],
  );

  const applyLoadedTransformations = useCallback(
    (steps) => {
      console.log("[PreviewTester] 🚀 Applying transformations...");
      console.log("[PreviewTester] 📋 Transformations to apply:", steps.length);
      console.log("[PreviewTester] 📋 Is initial load:", isInitialLoad);

      // ============================================================
      // ✅ DEV-ONLY GUARD — 6A
      // INVARIANT: Steps applied at load time must have a well-formed
      //            selector shape.
      //
      //   • Outside-loop steps (no loopSelector) → 3-segment path
      //         wrapper > contentWraps > target
      //
      //   • Inside-loop steps (with loopSelector):
      //       – loopSelector must be 3 or 5 segments
      //       – start / end must be 2 or 3 segments (relative to loop)
      //
      // A wrong segment count here means the path-generation pipeline
      // (generateSelectorPathForRow / getNthChildPath / getRelativePath)
      // has drifted. The step will still "apply" silently but will shift
      // incorrectly during recalculation.
      //
      // Owned by: generateSelectorPathForRow in TemplateEditor.js,
      //           getNthChildPath + getRelativePath in renderingUtils.js.
      // ============================================================
      if (process.env.NODE_ENV !== "production") {
        steps.forEach((step, idx) => {
          const sel = step?.selectors?.[0];
          if (!sel) return;

          const hasLoopSelector =
            typeof sel.loopSelector === "string" && sel.loopSelector.length > 0;

          const startPath =
            typeof sel.start === "string" ? sel.start : sel.start?.elementPath;
          const endPath =
            typeof sel.end === "string" ? sel.end : sel.end?.elementPath;

          if (startPath) {
            const segCount = startPath.split(" > ").length;
            if (!hasLoopSelector && segCount !== 3) {
              console.warn(
                "[INVARIANT:6A] Step outside a loop has unexpected path length.",
                {
                  stepIndex: idx,
                  stepType: step.type,
                  stepId: step.id,
                  segCount,
                  expected: 3,
                  start: startPath,
                },
              );
            }
          }

          if (hasLoopSelector) {
            const lsSegCount = (sel.loopSelector || "").split(" > ").length;
            if (lsSegCount !== 3 && lsSegCount !== 5) {
              console.warn(
                "[INVARIANT:6A] loopSelector has unexpected length.",
                {
                  stepIndex: idx,
                  stepId: step.id,
                  lsSegCount,
                  expected: "3 (outer-loop-stripped Case 1/3) or 5 (Case 2 / nested)",
                  loopSelector: sel.loopSelector,
                },
              );
            }
          }

          // start and end must both exist for any non-button step.
          if (step.type !== "button" && step.type !== "button-group") {
            if (!startPath || !endPath) {
              console.warn(
                "[INVARIANT:6A] Non-button step is missing start or end path.",
                {
                  stepIndex: idx,
                  stepType: step.type,
                  stepId: step.id,
                  start: startPath,
                  end: endPath,
                },
              );
            }
          }
        });
      }

      // ✅ Set flag to prevent auto-render
      isApplyingTransformations.current = true;

      hasShownReapplyPopup.current = true;
      editorWasOpenedRef.current = false;

      let currentPreview = renderedPreview;
      let currentParsedSample = parsedSample;
      let currentTemplate = template;

      const appliedSteps = [];

      // ✅ STEP 1: Re-parse template to ensure ALL types are correct
      if (steps.length > 0 && template && parsedSample) {
        console.log("[PreviewTester] 📝 Re-parsing template for proper types");

        let reParsedLines;

        // ✅ CRITICAL: Use the appropriate parser based on context
        if (isInitialLoad) {
          console.log(
            "[PreviewTester] 📝 Using parseTemplateForEditFirstLoadOnly (initial load from DB)",
          );
          reParsedLines = parseTemplateForEditFirstLoadOnly(
            template,
            parsedSample,
            isCase2,
            null,
            steps,
            false,
          );
        } else {
          console.log(
            "[PreviewTester] 📝 Using parseTemplateForEdit (re-apply after edits)",
          );
          reParsedLines = parseTemplateForEdit(
            template,
            parsedSample,
            isCase2,
            templateLines,
          );
        }

        console.log(
          `[PreviewTester] 📝 Re-parsed ${reParsedLines.length} lines`,
        );

        // ============================================================
        // ✅ Extract the template's outer loop iterable, so the
        //    "which array creates screens" decision is driven by the
        //    template (the user's choice) — not by Object.keys
        //    insertion order.
        //
        //    Mirrors the same fix in renderPreview. Without this, a
        //    saved template that iterates `rooms` is silently
        //    re-rendered against `staff` (whichever array appears
        //    first in the JSON), collapsing to a single wrong screen.
        // ============================================================
        let loopIterable = "";
        {
          const tplLines = (template || "").split("\n");
          for (const raw of tplLines) {
            const t = raw.trim();
            if (!t || t.startsWith("{#")) continue;
            const m = t.match(
              /{%\s*for\s+[a-zA-Z_][a-zA-Z0-9_]*\s+in\s+([^%]+?)\s*%}/,
            );
            if (m) {
              loopIterable = m[1].trim();
              break;
            }
          }
        }
        console.log(
          `[PreviewTester] 🎯 [applyLoaded] Template's outer loop iterates: ${loopIterable}`,
        );

        // ✅ STEP 2: Determine if we have multiple screens using isCase2 from state
        let shouldTreatAsArray = false;
        let effectiveArrayData = null;
        let nestedArrayKey = null;
        let isCase3WithMetadata = false;

        if (Array.isArray(parsedSample)) {
          shouldTreatAsArray = true;
          effectiveArrayData = parsedSample;
          console.log("[PreviewTester] 📊 Case 1: Root array detected");
        } else if (
          !isCase2 &&
          typeof parsedSample === "object" &&
          parsedSample !== null
        ) {
          // ============================================================
          // ✅ Resolve the template's declared iterable first.
          //    Fall back to "first array found" ONLY if the template's
          //    path doesn't resolve against the sample.
          // ============================================================
          let foundNestedArray = false;

          if (loopIterable && loopIterable.startsWith("response_data.")) {
            const iterableKey = loopIterable.slice("response_data.".length);
            const pathSegments = iterableKey.split(".");
            let candidate = parsedSample;
            let resolved = true;
            for (const seg of pathSegments) {
              if (
                candidate &&
                typeof candidate === "object" &&
                seg in candidate
              ) {
                candidate = candidate[seg];
              } else {
                resolved = false;
                break;
              }
            }

            if (resolved && Array.isArray(candidate) && candidate.length > 0) {
              shouldTreatAsArray = true;
              nestedArrayKey = iterableKey;
              effectiveArrayData = candidate;
              foundNestedArray = true;

              const otherKeys = Object.keys(parsedSample).filter(
                (k) => k !== pathSegments[0],
              );
              isCase3WithMetadata = otherKeys.length > 0;

              console.log(
                `[PreviewTester] 📊 Case 3: Nested array "${iterableKey}" with ${candidate.length} items (resolved from template)`,
              );
              if (isCase3WithMetadata) {
                console.log(
                  `[PreviewTester] 📊 Case 3 with metadata: ${otherKeys.join(", ")}`,
                );
              }
            } else {
              console.warn(
                `[PreviewTester] ⚠️ Template iterates "${loopIterable}" but that path did not resolve to a non-empty array in the sample. Falling back to first array found.`,
              );
            }
          }

          // Fallback — same heuristic as before, but only reached when
          // the template's iterable did not resolve.
          if (!foundNestedArray) {
            for (const key of Object.keys(parsedSample)) {
              if (
                Array.isArray(parsedSample[key]) &&
                parsedSample[key].length > 0
              ) {
                shouldTreatAsArray = true;
                nestedArrayKey = key;
                effectiveArrayData = parsedSample[key];
                foundNestedArray = true;
                const otherKeys = Object.keys(parsedSample).filter(
                  (k) => k !== key,
                );
                isCase3WithMetadata = otherKeys.length > 0;
                console.warn(
                  `[PreviewTester] 📊 Case 3 FALLBACK: using first array "${key}" with ${parsedSample[key].length} items (template iterable did not resolve)`,
                );
                break;
              }
            }
          }
        } else {
          console.log("[PreviewTester] 📊 Case 2: Single screen with metadata");
          shouldTreatAsArray = false;
        }

        const enhancedTemplate = enhanceTemplateWithLoopWrappers(
          template,
          reParsedLines,
        );

        // ✅ STEP 3: Render using the SHARED function
        const renderedHTML = renderScreens(
          enhancedTemplate,
          parsedSample,
          effectiveArrayData,
          isCase3WithMetadata,
          isCase2,
          reParsedLines,
          shouldTreatAsArray,
        );

        currentPreview = renderedHTML;
        setTemplateLines(reParsedLines);

        console.log(
          "[PreviewTester] ✅ Re-rendered preview with proper screen handling",
        );
      }

      // ✅ STEP 4: Apply ALL transformations with multi-select support
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        console.log(
          `[PreviewTester] 🔧 Applying step ${i + 1}/${steps.length}:`,
          step.operation || step.type,
        );

        // ============================================================
        // HANDLE MEDIA TRANSFORMATIONS — delegate to applyMediaToAllInLoop
        // (single source of truth for wrapped-loop aware media replacement)
        // ============================================================
        if (step.type === "media") {
          console.log(
            "[PreviewTester] 🖼️ Applying media transformation (delegated):",
            step.operation,
          );

          const selectors = step.selectors || [];
          if (selectors.length === 0) {
            console.warn(
              "[PreviewTester] ⚠️ Media step has no selectors, skipping",
            );
            continue;
          }

          const selector = selectors[0];
          const hasLoopSelector =
            selector.loopSelector && selector.loopSelector.length > 0;

          const resolveDim = (v, fallback) => {
            if (v == null) return fallback;
            if (typeof v === "number") return v;
            if (typeof v === "string") {
              const n = parseInt(v, 10);
              return Number.isFinite(n) ? n : fallback;
            }
            if (typeof v === "object" && v.parsedValue != null) {
              const n = parseInt(v.parsedValue, 10);
              return Number.isFinite(n) ? n : fallback;
            }
            return fallback;
          };

          const imageWidth = resolveDim(
            step.imageSize?.width ?? step.imageWidth ?? step.data?.width,
            250,
          );
          const imageHeight = resolveDim(
            step.imageSize?.height ?? step.imageHeight ?? step.data?.height,
            250,
          );
          const preserveAspectRatio =
            step.imageSize?.preserveAspectRatio !== undefined
              ? step.imageSize.preserveAspectRatio
              : step.preserveAspectRatio !== undefined
                ? step.preserveAspectRatio
                : true;

          if (hasLoopSelector) {
            const mediaResult = applyMediaToAllInLoop(
              selector,
              step.originalText || "",
              currentPreview,
              document.querySelector(".item-ui-screen"),
              {
                width: imageWidth,
                height: imageHeight,
                preserveAspectRatio,
              },
            );

            if (!mediaResult.success) {
              console.warn(
                "[PreviewTester] ⚠️ applyMediaToAllInLoop failed:",
                mediaResult.error,
              );
              continue;
            }

            currentPreview = mediaResult.newPreview;

            appliedSteps.push({
              ...step,
              applied: true,
              transformedText: mediaResult.newPreview,
            });

            console.log(
              `[PreviewTester] ✅ Media step ${i + 1} applied successfully (delegated), replaced ${mediaResult.replacedCount} images`,
            );
            continue;
          }

          console.log(
            "[PreviewTester] 🖼️ No loop selector, applying single transformation",
          );

          const parser = new DOMParser();
          const doc = parser.parseFromString(currentPreview, "text/html");
          let screens = doc.querySelectorAll(".item-ui-screen");

          if (screens.length === 0) {
            const contentWrapsList = doc.querySelectorAll(".contentWraps");
            if (contentWrapsList.length > 0) {
              const wrappedScreens = [];
              for (const wrap of contentWrapsList) {
                const wrapper = doc.createElement("div");
                wrapper.className = "item-ui-screen";
                wrapper.setAttribute("data-item-ui-index", "0");
                const parent = wrap.parentNode;
                parent.replaceChild(wrapper, wrap);
                wrapper.appendChild(wrap);
                wrappedScreens.push(wrapper);
              }
              screens = wrappedScreens;
            }
          }

          if (screens.length === 0) {
            console.warn(
              "[PreviewTester] ⚠️ No screens found for media transformation",
            );
            continue;
          }

          const createImageHtmlWithDimensions = (url, width, height) => {
            const cleanUrl = url.replace(/"/g, "&quot;");
            const objectFit = preserveAspectRatio ? "cover" : "fill";
            return `<div class="media-wrapper" style="display:inline-block;margin:4px 0;">
      <img 
        src="${cleanUrl}" 
        alt="Image" 
        class="preview-image rendered-image" 
        style="width:${width}px;height:${height}px;object-fit:${objectFit};border-radius:8px;border:1px solid #e2e8f0;max-width:100%;box-shadow:0 2px 4px rgba(0,0,0,0.1);" 
        loading="lazy" 
        onerror="this.style.display='none';this.parentElement.innerHTML='<div style=\\'color:#a0aec0;font-size:12px;\\'>🖼️ Image unavailable</div>';"
      />
    </div>`;
          };

          const startPath =
            typeof selector.start === "string"
              ? selector.start
              : selector.start?.elementPath || "";

          let totalReplaced = 0;

          for (let screenIdx = 0; screenIdx < screens.length; screenIdx++) {
            const screen = screens[screenIdx];
            const screenIndex =
              screen.getAttribute("data-item-ui-index") || screenIdx;

            let containerForPath = screen;
            let adjustedStartPath = startPath;

            const contentWraps = screen.querySelector(".contentWraps");

            if (contentWraps) {
              const contentWrapsPath = getNthChildPath(contentWraps, screen);
              const startPathParts = startPath.split(" > ");
              const wrapperParts = contentWrapsPath.split(" > ");

              if (
                wrapperParts.length > 0 &&
                startPathParts.length >= wrapperParts.length
              ) {
                let isPrefix = true;
                for (let j = 0; j < wrapperParts.length; j++) {
                  if (startPathParts[j] !== wrapperParts[j]) {
                    isPrefix = false;
                    break;
                  }
                }
                if (isPrefix) {
                  let partsToStrip = wrapperParts.length;
                  if (startPathParts.length > wrapperParts.length) {
                    const nextPart = startPathParts[wrapperParts.length];
                    const lastWrapperPart =
                      wrapperParts[wrapperParts.length - 1];
                    if (nextPart === lastWrapperPart) {
                      partsToStrip = wrapperParts.length + 1;
                    }
                  }
                  adjustedStartPath = startPathParts
                    .slice(partsToStrip)
                    .join(" > ");
                  containerForPath = contentWraps;
                  console.log(
                    `[PreviewTester] 🔄 Adjusted path from "${startPath}" to "${adjustedStartPath}"`,
                  );
                }
              }
            }

            let targetElement = findElementByPath(
              containerForPath,
              adjustedStartPath,
            );
            if (!targetElement) {
              targetElement = findElementByPath(screen, startPath);
            }

            if (!targetElement) {
              console.log(
                `[PreviewTester] 🖼️ ⚠️ No target element found in screen ${screenIndex}`,
              );
              continue;
            }

            let imageUrl = null;
            let elementToReplace = null;

            if (targetElement.getAttribute("data-image-url")) {
              imageUrl = targetElement.getAttribute("data-image-url");
              elementToReplace = targetElement;
            } else if (
              targetElement.classList?.contains("value") &&
              targetElement.classList?.contains("image-url")
            ) {
              imageUrl = targetElement.textContent?.trim() || "";
              elementToReplace = targetElement;
            } else {
              const valueSpan =
                targetElement.querySelector?.(".value.image-url");
              if (valueSpan) {
                imageUrl = valueSpan.textContent?.trim() || "";
                elementToReplace = valueSpan;
              }
            }

            if (!elementToReplace || !imageUrl) {
              console.log(
                `[PreviewTester] 🖼️ ⚠️ No image URL found in screen ${screenIndex}`,
              );
              continue;
            }

            const imgHtml = createImageHtmlWithDimensions(
              imageUrl,
              imageWidth,
              imageHeight,
            );
            const tempDiv = doc.createElement("div");
            tempDiv.innerHTML = imgHtml;
            const newElement = tempDiv.firstChild;
            if (newElement) {
              elementToReplace.parentNode.replaceChild(
                newElement,
                elementToReplace,
              );
              totalReplaced++;
              console.log(
                `[PreviewTester] 🖼️ Replaced image in screen ${screenIndex} (${imageWidth}×${imageHeight})`,
              );
            }
          }

          if (totalReplaced === 0) {
            console.warn(
              "[PreviewTester] ⚠️ No images were replaced for media step",
            );
            continue;
          }

          let serialized = "";
          for (const child of doc.body.children) {
            serialized += child.outerHTML;
          }
          currentPreview = serialized;

          appliedSteps.push({
            ...step,
            applied: true,
            transformedText: serialized,
          });

          console.log(
            `[PreviewTester] ✅ Media step ${i + 1} applied successfully, replaced ${totalReplaced} images`,
          );
          continue;
        }

        // ============================================================
        // ✅ HANDLE BUTTON TRANSFORMATIONS (new minimal schema)
        // ============================================================
        if (step.type === "button") {
          console.log("[🔘 BUTTON] Applying button transformation");
          appliedSteps.push({
            ...step,
            applied: true,
            transformedText: currentPreview,
          });
          console.log("[🔘 BUTTON] Button step added to rules");
          continue;
        }

        // ============================================================
        // HANDLE BUTTON GROUP TRANSFORMATIONS (new minimal schema)
        // ============================================================
        if (step.type === "button-group") {
          console.log(
            "[🔘 BUTTON GROUP] Applying button group style transformation",
          );
          appliedSteps.push({
            ...step,
            applied: true,
            transformedText: currentPreview,
          });
          continue;
        }

        // ============================================================
        // HANDLE STYLE & STRUCTURAL TRANSFORMATIONS
        // ============================================================
        if (step.type === "style" || step.type === "structural") {
          console.log(
            `[PreviewTester] 🎨 Applying ${step.type} transformation`,
          );

          const selectors = step.selectors || [];
          if (selectors.length === 0) {
            console.warn("[PreviewTester] ⚠️ Step has no selectors, skipping");
            continue;
          }

          let operations = [];
          let styleMeta = { full_row: false, alignment: "left" };

          if (step.type === "style") {
            if (
              step.operations &&
              Array.isArray(step.operations) &&
              step.operations.length > 0
            ) {
              operations = step.operations;
            } else if (step.operation) {
              operations = [step.operation];
            } else {
              operations = ["highlight"];
            }

            if (step.style_meta) {
              styleMeta = step.style_meta;
            }

            console.log(
              `[PreviewTester] 🎨 Applying ${operations.length} style operations:`,
              operations,
            );
            console.log("[PreviewTester] 🎨 Style meta:", styleMeta);
          } else {
            operations = [step.operation || "table"];
          }

          let selectedText = "";
          if (step.type === "button") {
            selectedText = step.buttonText || "";
          } else if (step.type === "button-group") {
            selectedText = step.groupName || "";
          } else {
            selectedText = step.originalText || "";
          }

          if (!selectedText && step.fields && step.fields.length > 0) {
            selectedText = step.fields[0];
          }

          if (!selectedText) {
            console.warn(
              "[PreviewTester] ⚠️ No selected text for style step, skipping",
            );
            continue;
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(currentPreview, "text/html");
          const containerElement = doc.querySelector(".item-ui-screen");

          if (!containerElement) {
            console.warn(
              "[PreviewTester] ⚠️ No container found for style step",
            );
            continue;
          }

          const cleanedSelectors = selectors.map((s) => {
            if (typeof s.start === "string" && typeof s.end === "string") {
              return {
                start: { elementPath: s.start, nodeType: 1 },
                end: { elementPath: s.end, nodeType: 1 },
                loopSelector: s.loopSelector || undefined,
              };
            }
            return s;
          });

          const stepScrollMode =
            step._scrollMode || scrollModeRef.current || scrollMode;

          const result = applyTransformationLogic(
            selectedText,
            operations[0],
            step.type === "structural",
            currentPreview,
            currentTemplate,
            currentParsedSample,
            containerElement,
            null,
            [],
            isSampleArray,
            dataType,
            arrayLayoutDirection,
            layoutDirection,
            {
              selectors: cleanedSelectors,
              operations: operations,
              style_meta: styleMeta,
              scrollMode: stepScrollMode,
            },
          );

          if (result.success && result.newPreview) {
            console.log(
              `[PreviewTester] ✅ ${step.type} step ${i + 1} applied successfully`,
            );
            currentPreview = result.newPreview;
            appliedSteps.push({
              ...step,
              applied: true,
              transformedText: result.newPreview,
              operations: operations,
              style_meta: styleMeta,
            });
          } else {
            console.warn(
              `[PreviewTester] ⚠️ ${step.type} step ${i + 1} failed:`,
              result.error,
            );
          }
        }

        // ============================================================
        // HANDLE COMBINED STEPS
        // ============================================================
        if (step.type === "combined" && step.phases) {
          console.log(
            `[PreviewTester] 📋 Applying combined step with phases:`,
            Object.keys(step.phases),
          );

          if (step.phases.structural?.operations?.length > 0) {
            for (const op of step.phases.structural.operations) {
              console.log(
                `[PreviewTester] 📐 Applying structural operation: ${op.operation}`,
              );
            }
          }

          if (step.phases.media?.operations?.length > 0) {
            for (const op of step.phases.media.operations) {
              console.log(
                `[PreviewTester] 🖼️ Applying media operation: ${op.operation}`,
              );
            }
          }

          if (step.phases.styles?.operations?.length > 0) {
            for (const op of step.phases.styles.operations) {
              console.log(
                `[PreviewTester] 🎨 Applying style operation: ${op.operation}`,
              );
            }
          }

          appliedSteps.push({
            ...step,
            applied: true,
            transformedText: currentPreview,
          });
          continue;
        }
      }

      // ✅ STEP 5: Set transformation rules
      if (appliedSteps.length > 0) {
        console.log("[PreviewTester] 📋 Setting transformationRules");
        setTransformationRules(appliedSteps);
      } else {
        console.log(
          "[PreviewTester] 📋 No transformations applied, clearing rules",
        );
        setTransformationRules([]);
      }

      // ✅ STEP 6: Set the preview
      setRenderedPreview(currentPreview);
      setTransformedPreview(currentPreview);

      console.log("[PreviewTester] ✅ Transformations applied successfully!");
      console.log(
        "[PreviewTester] 📋 transformationRules count:",
        appliedSteps.length,
      );

      setTimeout(() => {
        isApplyingTransformations.current = false;
        console.log("[PreviewTester] 🔄 Transformations flag reset");
      }, 300);
    },
    [
      transformationRules,
      renderedPreview,
      parsedSample,
      template,
      isSampleArray,
      dataType,
      arrayLayoutDirection,
      layoutDirection,
      setRenderedPreview,
      setTransformedPreview,
      isInitialLoad,
      setTemplateLines,
      templateLines,
      convertButtonGroupStepsToTemplateLines,
      isCase2,
      scrollModeRef,
      renderScreens,
    ],
  );

  // ============================================================
  // HANDLE DISMISS RE-APPLY POPUP - Keep current view (raw structure)
  // ============================================================
  const handleDismissReapplyPopup = () => {
    console.log(
      "[PreviewTester] 📌 User chose to keep current view (raw structure)",
    );
    setShowTransformConfirm(false);
    setPendingTransformSteps([]);
    hasShownReapplyPopup.current = true;
    editorWasOpenedRef.current = false;

    console.log(
      "[PreviewTester] 📋 transformationRules preserved, count:",
      transformationRules.length,
    );
  };

  // ============================================================
  // HANDLE CLOSE EDITOR - Show reapply popup if needed
  // ============================================================
  const handleCloseEditor = useCallback(() => {
    console.log("[PreviewTester] 📝 Closing editor...");

    const hasTransformations = transformationRules.length > 0;

    if (hasTransformations) {
      console.log(
        `[PreviewTester] 🔄 Found ${transformationRules.length} transformations to potentially reapply`,
      );
      setPendingTransformSteps(transformationRules);
      setShowTransformConfirm(true);
      hasShownReapplyPopup.current = true;
    } else {
      console.log("[PreviewTester] ℹ️ No transformations to reapply");
    }

    // ✅ UX: collapse left panel back into the rail after editor closes
    setIsLeftPanelCollapsed(true);

    setIsEditMode(false);
  }, [
    transformationRules,
    setIsEditMode,
    setIsLeftPanelCollapsed,
    setShowTransformConfirm,
    setPendingTransformSteps,
  ]);

  // ============================================================
  // HANDLE OPEN EDITOR - Simplified
  // ============================================================
  const handleOpenEditor = useCallback(() => {
    console.log("[PreviewTester] 📝 Opening editor...");
    editorWasOpenedRef.current = true;
    hasShownReapplyPopup.current = false;

    // ✅ UX: ensure left panel is expanded so the editor is visible
    setIsLeftPanelCollapsed(false);

    setIsEditMode(true);
    setIsEditorOpen(true);
    console.log("[PreviewTester] ✅ Editor opened");
  }, [setIsEditMode, setIsLeftPanelCollapsed]);


  // Add transformation step
  // ✅ FIX: Just add the step, no questions asked
  const handleAddTransformationStep = useCallback(
    (step) => {
      console.log("[🔘 BUTTON GROUP] Adding transformation step:", step);
      console.log("[🔘 BUTTON GROUP] Step type:", step.type);
      console.log("[🔘 BUTTON GROUP] Step operation:", step.operation);

      // ============================================================
      // ✅ DEV-ONLY GUARD — 5B
      // INVARIANT: Every normalized step has:
      //              • id            (string, non-empty)
      //              • type          (string, non-empty)
      //              • operation     (string, non-empty)
      //              • selectors     (array of { start, end } strings)
      //              • timestamp     (ISO string)
      //
      // Reason: These five fields are the contract between the editor,
      // the recalculation engine, and the saved DB row. A step that
      // ships with any of them missing will appear correct in the UI
      // but silently fail during recalculation, or produce a DB row
      // that cannot be re-hydrated on reload.
      //
      // Owned by: createMinimalTransformationStep in
      //           transformationRules.js — the canonical producer of
      //           these fields.
      // ============================================================
      if (process.env.NODE_ENV !== "production") {
        const missing = [];
        if (!step || typeof step !== "object") {
          console.warn(
            "[INVARIANT:5B] handleAddTransformationStep received a non-object step.",
            { step },
          );
          return;
        }
        if (!step.id || typeof step.id !== "string") missing.push("id");
        if (!step.type || typeof step.type !== "string") missing.push("type");
        if (!step.operation || typeof step.operation !== "string")
          missing.push("operation");
        if (!Array.isArray(step.selectors)) missing.push("selectors[]");
        if (!step.timestamp || typeof step.timestamp !== "string")
          missing.push("timestamp");

        if (missing.length > 0) {
          console.warn(
            "[INVARIANT:5B] handleAddTransformationStep received an incomplete step.",
            {
              missing,
              stepType: step.type,
              stepId: step.id,
              step,
            },
          );
        }

        // Shape of every selector entry
        if (Array.isArray(step.selectors)) {
          const badSel = step.selectors.filter(
            (s) =>
              !s ||
              typeof s.start !== "string" ||
              typeof s.end !== "string" ||
              s.start.length === 0 ||
              s.end.length === 0,
          );
          if (badSel.length > 0) {
            console.warn(
              "[INVARIANT:5B] Step has selectors with non-string start/end.",
              {
                stepId: step.id,
                stepType: step.type,
                badSelectors: badSel,
              },
            );
          }

          // If a selector has a loopSelector, it must be a non-empty string.
          const badLoopSel = step.selectors.filter(
            (s) =>
              s &&
              s.loopSelector !== undefined &&
              (typeof s.loopSelector !== "string" || s.loopSelector.length === 0),
          );
          if (badLoopSel.length > 0) {
            console.warn(
              "[INVARIANT:5B] Step has selectors with malformed loopSelector.",
              {
                stepId: step.id,
                stepType: step.type,
                badSelectors: badLoopSel,
              },
            );
          }
        }
      }

      // ============================================================
      // ✅ Normalize the step to ensure it's in the new minimal format
      // ============================================================
      const normalizedStep = { ...step };

      // Remove old format flags if present
      delete normalizedStep.isStructural;
      delete normalizedStep.isMedia;
      delete normalizedStep.isStructuralMultiNode;
      delete normalizedStep.isMultiRowSelection;
      delete normalizedStep.rowCount;
      delete normalizedStep.isButtonTransformation;
      delete normalizedStep.isButtonGroupTransformation;
      delete normalizedStep.applied;
      delete normalizedStep.originalText;
      delete normalizedStep.transformedText;
      delete normalizedStep.selectorType;
      delete normalizedStep.data;
      delete normalizedStep.groupLocation;
      delete normalizedStep._selectorMetadata;

      // ============================================================
      // ✅ Ensure selectors are in the new minimal format
      // ============================================================
      if (normalizedStep.selectors && Array.isArray(normalizedStep.selectors)) {
        normalizedStep.selectors = normalizedStep.selectors
          .filter((s) => s)
          .map((s) => {
            if (typeof s.start === "string" && typeof s.end === "string") {
              const result = {
                start: s.start,
                end: s.end,
              };
              if (s.loopSelector) {
                result.loopSelector = s.loopSelector;
              }
              return result;
            }
            if (s.start?.elementPath && s.end?.elementPath) {
              const result = {
                start: s.start.elementPath,
                end: s.end.elementPath,
              };
              if (s.loopSelector) {
                result.loopSelector = s.loopSelector;
              }
              return result;
            }
            const startPath = s.start?.elementPath || s.start || "";
            const endPath = s.end?.elementPath || s.end || startPath;
            const result = {
              start: startPath,
              end: endPath,
            };
            if (s.loopSelector) {
              result.loopSelector = s.loopSelector;
            }
            return result;
          })
          .filter((s) => s.start && s.end);
      }

      // ============================================================
      // ✅ If it's a button group, ensure buttons are strings (new format)
      // ============================================================
      if (normalizedStep.type === "button-group") {
        if (normalizedStep.buttons && Array.isArray(normalizedStep.buttons)) {
          if (
            normalizedStep.buttons.length > 0 &&
            typeof normalizedStep.buttons[0] === "object"
          ) {
            normalizedStep.buttons = normalizedStep.buttons.map(
              (b) => b.text || String(b),
            );
            console.log(
              "[🔘 BUTTON GROUP] Converted button objects to strings:",
              normalizedStep.buttons,
            );
          }
        } else {
          normalizedStep.buttons = [];
        }

        if (!normalizedStep.groupName && normalizedStep._metadata?.groupName) {
          normalizedStep.groupName = normalizedStep._metadata.groupName;
        }
        if (!normalizedStep.groupName) {
          normalizedStep.groupName = "Button Group";
        }

        if (!normalizedStep.operation) {
          normalizedStep.operation = "isButtonGroup";
        }

        delete normalizedStep._metadata;
        delete normalizedStep.groupLocation;
      }

      // ============================================================
      // ✅ If it's a button, ensure buttonText is set
      // ============================================================
      if (normalizedStep.type === "button") {
        if (
          !normalizedStep.buttonText &&
          normalizedStep.fields &&
          normalizedStep.fields.length > 0
        ) {
          normalizedStep.buttonText = normalizedStep.fields[0];
        }
        if (
          !normalizedStep.buttonText &&
          normalizedStep._metadata?.buttonText
        ) {
          normalizedStep.buttonText = normalizedStep._metadata.buttonText;
        }
        if (!normalizedStep.buttonText) {
          normalizedStep.buttonText = "Button";
        }

        if (!normalizedStep.operation) {
          normalizedStep.operation = "isButton";
        }

        delete normalizedStep._metadata;
      }

      // ============================================================
      // ✅ If it's a style step, ensure fields are set
      // ============================================================
      if (normalizedStep.type === "style") {
        if (!normalizedStep.fields || normalizedStep.fields.length === 0) {
          if (normalizedStep._metadata?.fields) {
            normalizedStep.fields = normalizedStep._metadata.fields;
          } else if (normalizedStep.fields === undefined) {
            normalizedStep.fields = [];
          }
        }

        if (!normalizedStep.operation) {
          normalizedStep.operation = "highlight";
        }

        delete normalizedStep._metadata;
      }

      // ============================================================
      // ✅ If it's a structural step, ensure fields are set
      // ============================================================
      if (normalizedStep.type === "structural") {
        if (!normalizedStep.fields || normalizedStep.fields.length === 0) {
          if (normalizedStep._metadata?.fields) {
            normalizedStep.fields = normalizedStep._metadata.fields;
          } else if (normalizedStep.fields === undefined) {
            normalizedStep.fields = [];
          }
        }

        if (!normalizedStep.operation) {
          normalizedStep.operation = "table";
        }

        delete normalizedStep._metadata;
      }

      // ============================================================
      // ✅ If it's a media step, ensure imageSize is set
      // ============================================================
      if (normalizedStep.type === "media") {
        if (!normalizedStep.imageSize) {
          if (
            normalizedStep._metadata?.imageWidth &&
            normalizedStep._metadata?.imageHeight
          ) {
            normalizedStep.imageSize = {
              width: normalizedStep._metadata.imageWidth,
              height: normalizedStep._metadata.imageHeight,
            };
          } else {
            normalizedStep.imageSize = { width: 250, height: 250 };
          }
        }

        if (!normalizedStep.fields || normalizedStep.fields.length === 0) {
          normalizedStep.fields = [];
        }

        if (!normalizedStep.operation) {
          normalizedStep.operation = "images";
        }

        delete normalizedStep._metadata;
      }

      // ============================================================
      // ✅ If it's a combined step, clean up phases
      // ============================================================
      if (normalizedStep.type === "combined") {
        if (normalizedStep.phases) {
          if (!normalizedStep.phases.structural) {
            normalizedStep.phases.structural = { operations: [] };
          }
          if (!normalizedStep.phases.media) {
            normalizedStep.phases.media = { operations: [] };
          }
          if (!normalizedStep.phases.styles) {
            normalizedStep.phases.styles = { operations: [] };
          }

          if (
            normalizedStep.phases.structural &&
            !normalizedStep.phases.structural.operations
          ) {
            normalizedStep.phases.structural.operations = [];
          }
          if (
            normalizedStep.phases.media &&
            !normalizedStep.phases.media.operations
          ) {
            normalizedStep.phases.media.operations = [];
          }
          if (
            normalizedStep.phases.styles &&
            !normalizedStep.phases.styles.operations
          ) {
            normalizedStep.phases.styles.operations = [];
          }
        }

        if (!normalizedStep.operation) {
          normalizedStep.operation = "combined";
        }

        delete normalizedStep._metadata;
      }

      // ============================================================
      // ✅ Ensure timestamp is set
      // ============================================================
      if (!normalizedStep.timestamp) {
        normalizedStep.timestamp = new Date().toISOString();
      }

      // ============================================================
      // ✅ Ensure id is set
      // ============================================================
      if (!normalizedStep.id) {
        const prefix = normalizedStep.type || "trans";
        normalizedStep.id = `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      }

      // ============================================================
      // ✅ DEV-ONLY GUARD — 5B (post-normalization)
      // INVARIANT: After normalization, every field the downstream
      //            pipeline depends on MUST be present and correctly
      //            typed. This is the second insertion point of 5B:
      //            the first checked the *input*, this checks the
      //            *output* that is about to be committed to state.
      //
      // Reason: any transformation applied during normalization that
      // silently drops a field would leave the step "correct-looking"
      // but broken downstream. This asserts the invariant at the
      // exact boundary where the step enters state.
      // ============================================================
      if (process.env.NODE_ENV !== "production") {
        const missing = [];
        if (!normalizedStep.id) missing.push("id");
        if (!normalizedStep.type) missing.push("type");
        if (!normalizedStep.operation) missing.push("operation");
        if (!Array.isArray(normalizedStep.selectors))
          missing.push("selectors[]");
        if (!normalizedStep.timestamp) missing.push("timestamp");

        if (missing.length > 0) {
          console.warn(
            "[INVARIANT:5B] Normalized step is still incomplete after normalization.",
            {
              missing,
              normalizedStep,
            },
          );
        }

        // Type-specific invariants
        if (normalizedStep.type === "button" && !normalizedStep.buttonText) {
          console.warn(
            "[INVARIANT:5B] Normalized button step has no buttonText.",
            { normalizedStep },
          );
        }
        if (
          normalizedStep.type === "button-group" &&
          (!Array.isArray(normalizedStep.buttons) ||
            normalizedStep.buttons.some((b) => typeof b !== "string"))
        ) {
          console.warn(
            "[INVARIANT:5B] Normalized button-group step has non-string buttons.",
            {
              buttons: normalizedStep.buttons,
              normalizedStep,
            },
          );
        }
      }

      // ============================================================
      // ✅ Append the normalized step to the array
      // ============================================================
      setTransformationRules((prevRules) => {
        const newRules = [...prevRules, normalizedStep];
        console.log(
          "[🔘 BUTTON GROUP] Added step, total rules:",
          newRules.length,
        );
        console.log(
          "[🔘 BUTTON GROUP] Normalized step:",
          JSON.stringify(normalizedStep, null, 2),
        );
        return newRules;
      });

      // ============================================================
      // ✅ Force re-render of preview and controls
      // ============================================================
      setTimeout(() => {
        if (
          normalizedStep.type === "button" ||
          normalizedStep.type === "button-group"
        ) {
          console.log("[🔘 BUTTON GROUP] Triggering preview update for button");
        }
      }, 50);
    },
    [setTransformationRules],
  );


  // Remove transformation step
  // Remove transformation step
  // ============================================================
  // REMOVE TRANSFORMATION STEP - Supports both old and new formats
  // ============================================================
  const handleRemoveTransformationStep = useCallback(
    (criteria) => {
      console.log("[🔘 BUTTON GROUP] Removing transformation step:", criteria);
      console.log("[🔘 BUTTON GROUP] Criteria type:", typeof criteria);
      console.log(
        "[🔘 BUTTON GROUP] Criteria value:",
        JSON.stringify(criteria),
      );

      setTransformationRules((prev) => {
        console.log("[🔘 BUTTON GROUP] Current rules count:", prev.length);

        // ============================================================
        // ✅ Handle different types of criteria
        // ============================================================

        // Case 1: Direct step ID (string)
        if (typeof criteria === "string") {
          console.log("[🔘 BUTTON GROUP] Removing by step ID:", criteria);
          const filtered = prev.filter((rule) => rule.id !== criteria);
          console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
          return filtered;
        }

        // Case 2: Criteria object with specific properties
        if (typeof criteria === "object" && criteria !== null) {
          // ============================================================
          // ✅ Remove by groupId (for button groups) - NEW FORMAT SUPPORT
          // ============================================================
          if (criteria.groupId) {
            console.log(
              "[🔘 BUTTON GROUP] Removing by groupId:",
              criteria.groupId,
            );
            const filtered = prev.filter((rule) => {
              // NEW format: Use isButtonGroupStep helper
              if (isButtonGroupStep(rule)) {
                // Check by ID
                if (rule.id === criteria.groupId) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by ID:",
                    rule.id,
                  );
                  return false;
                }
                // Check by groupName if provided
                if (
                  criteria.groupName &&
                  rule.groupName === criteria.groupName
                ) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by groupName:",
                    rule.id,
                  );
                  return false;
                }
                return true;
              }

              // OLD format: Check isButtonGroupTransformation flag
              if (rule.isButtonGroupTransformation === true) {
                // Check various possible ID locations
                if (rule.groupLocation?.groupId === criteria.groupId) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by groupLocation.groupId:",
                    rule.id,
                  );
                  return false;
                }
                if (rule.groupLocation?.groupRowId === criteria.groupId) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by groupLocation.groupRowId:",
                    rule.id,
                  );
                  return false;
                }
                if (rule._metadata?.groupRowId === criteria.groupId) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by _metadata.groupRowId:",
                    rule.id,
                  );
                  return false;
                }
                if (rule.groupId === criteria.groupId) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by groupId:",
                    rule.id,
                  );
                  return false;
                }
                // Check by groupName if provided
                if (criteria.groupName) {
                  if (rule._metadata?.groupName === criteria.groupName) {
                    console.log(
                      "[🔘 BUTTON GROUP] Found button group by _metadata.groupName:",
                      rule.id,
                    );
                    return false;
                  }
                  if (rule.groupName === criteria.groupName) {
                    console.log(
                      "[🔘 BUTTON GROUP] Found button group by groupName:",
                      rule.id,
                    );
                    return false;
                  }
                  if (rule.originalText === criteria.groupName) {
                    console.log(
                      "[🔘 BUTTON GROUP] Found button group by originalText:",
                      rule.id,
                    );
                    return false;
                  }
                }
              }
              return true;
            });
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by groupName (for button groups) - NEW FORMAT SUPPORT
          // ============================================================
          if (criteria.groupName) {
            console.log(
              "[🔘 BUTTON GROUP] Removing by groupName:",
              criteria.groupName,
            );
            const filtered = prev.filter((rule) => {
              // NEW format: Use isButtonGroupStep helper
              if (
                isButtonGroupStep(rule) &&
                rule.groupName === criteria.groupName
              ) {
                console.log(
                  "[🔘 BUTTON GROUP] Found button group by groupName:",
                  rule.id,
                );
                return false;
              }

              // OLD format: Check isButtonGroupTransformation flag
              if (rule.isButtonGroupTransformation === true) {
                if (rule._metadata?.groupName === criteria.groupName) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by _metadata.groupName:",
                    rule.id,
                  );
                  return false;
                }
                if (rule.groupName === criteria.groupName) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by groupName:",
                    rule.id,
                  );
                  return false;
                }
                if (rule.originalText === criteria.groupName) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button group by originalText:",
                    rule.id,
                  );
                  return false;
                }
              }
              return true;
            });
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by buttonText (for regular buttons) - NEW FORMAT SUPPORT
          // ============================================================
          if (criteria.buttonText) {
            console.log(
              "[🔘 BUTTON GROUP] Removing by buttonText:",
              criteria.buttonText,
            );
            const filtered = prev.filter((rule) => {
              // NEW format: Use isButtonStep helper
              if (
                isButtonStep(rule) &&
                rule.buttonText === criteria.buttonText
              ) {
                console.log(
                  "[🔘 BUTTON GROUP] Found button by buttonText:",
                  rule.id,
                );
                return false;
              }

              // OLD format: Check isButtonTransformation flag
              if (rule.isButtonTransformation === true) {
                if (rule._metadata?.buttonText === criteria.buttonText) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button by _metadata.buttonText:",
                    rule.id,
                  );
                  return false;
                }
                if (rule.originalText === criteria.buttonText) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button by originalText:",
                    rule.id,
                  );
                  return false;
                }
                if (rule.fields && rule.fields.includes(criteria.buttonText)) {
                  console.log(
                    "[🔘 BUTTON GROUP] Found button by fields:",
                    rule.id,
                  );
                  return false;
                }
              }
              return true;
            });
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by rule ID (for any step)
          // ============================================================
          if (criteria.id) {
            console.log("[🔘 BUTTON GROUP] Removing by rule ID:", criteria.id);
            const filtered = prev.filter((rule) => rule.id !== criteria.id);
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by index (if criteria has index property)
          // ============================================================
          if (
            criteria.index !== undefined &&
            typeof criteria.index === "number"
          ) {
            console.log("[🔘 BUTTON GROUP] Removing by index:", criteria.index);
            if (criteria.index >= 0 && criteria.index < prev.length) {
              const filtered = prev.filter((_, idx) => idx !== criteria.index);
              console.log(
                "[🔘 BUTTON GROUP] Remaining rules:",
                filtered.length,
              );
              return filtered;
            }
            console.warn(
              "[🔘 BUTTON GROUP] Index out of bounds:",
              criteria.index,
            );
            return prev;
          }

          // ============================================================
          // ✅ Remove by custom filter function
          // ============================================================
          if (typeof criteria === "function") {
            console.log("[🔘 BUTTON GROUP] Removing by custom filter function");
            const filtered = prev.filter((rule) => !criteria(rule));
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by type (e.g., remove all style steps)
          // ============================================================
          if (criteria.type) {
            console.log("[🔘 BUTTON GROUP] Removing by type:", criteria.type);
            const filtered = prev.filter((rule) => rule.type !== criteria.type);
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by operation (e.g., remove all highlight steps)
          // ============================================================
          if (criteria.operation) {
            console.log(
              "[🔘 BUTTON GROUP] Removing by operation:",
              criteria.operation,
            );
            const filtered = prev.filter(
              (rule) => rule.operation !== criteria.operation,
            );
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }

          // ============================================================
          // ✅ Remove by custom predicate (if criteria has a `filter` function)
          // ============================================================
          if (criteria.filter && typeof criteria.filter === "function") {
            console.log(
              "[🔘 BUTTON GROUP] Removing by custom filter function from criteria.filter",
            );
            const filtered = prev.filter((rule) => !criteria.filter(rule));
            console.log("[🔘 BUTTON GROUP] Remaining rules:", filtered.length);
            return filtered;
          }
        }

        // ============================================================
        // ✅ Fallback: If nothing matches, log warning and return unchanged
        // ============================================================
        console.warn(
          "[🔘 BUTTON GROUP] No matching removal criteria found, returning unchanged",
        );
        return prev;
      });
    },
    [setTransformationRules],
  );

  const handleUpdateTransformationStep = useCallback(
    (update) => {
      console.log("[🔘 BUTTON GROUP] Updating transformation step:", update);
      console.log("[🔘 BUTTON GROUP] Update type:", update.type);

      setTransformationRules((prev) => {
        // ============================================================
        // ✅ Find the step - supports both old and new formats
        // ============================================================
        const stepIndex = prev.findIndex((r) => {
          // New format: type === 'button-group' and match by groupName or id
          if (r.type === "button-group") {
            if (update.groupId && r.id === update.groupId) return true;
            if (update.groupName && r.groupName === update.groupName)
              return true;
            if (update.groupId && r.groupLocation?.groupId === update.groupId)
              return true;
            if (
              update.groupId &&
              r.groupLocation?.groupRowId === update.groupId
            )
              return true;
            if (update.groupId && r._metadata?.groupRowId === update.groupId)
              return true;
          }
          // Old format: has isButtonGroupTransformation flag
          if (r.isButtonGroupTransformation === true) {
            if (update.groupId && r.groupLocation?.groupId === update.groupId)
              return true;
            if (
              update.groupId &&
              r.groupLocation?.groupRowId === update.groupId
            )
              return true;
            if (update.groupId && r._metadata?.groupRowId === update.groupId)
              return true;
            if (update.groupName && r._metadata?.groupName === update.groupName)
              return true;
            if (update.groupName && r.groupName === update.groupName)
              return true;
          }
          // Also check by id directly for any step
          if (update.id && r.id === update.id) return true;
          return false;
        });

        if (stepIndex === -1) {
          console.log(
            "[🔘 BUTTON GROUP] Step not found for update:",
            update.groupId || update.groupName || update.id,
          );
          return prev;
        }

        const existingStep = prev[stepIndex];
        let updatedStep = { ...existingStep };

        // ============================================================
        // ✅ HANDLE ADD BUTTON - Supports both old and new formats
        // ============================================================
        if (update.type === "addButton" && update.button) {
          console.log(
            "[🔘 BUTTON GROUP] Adding button to step:",
            update.button,
          );

          // Get button text - supports both string and object
          const buttonText =
            typeof update.button === "string"
              ? update.button
              : update.button.text || String(update.button);

          const buttonId = update.button?.id || `btn-${Date.now()}`;

          // Get current buttons - supports both formats
          let currentButtons = updatedStep.buttons || [];

          // If buttons are strings (new format), convert to array of strings
          const isNewFormat =
            currentButtons.length === 0 ||
            typeof currentButtons[0] === "string";

          if (isNewFormat) {
            // New format: buttons are strings
            updatedStep.buttons = [...currentButtons, buttonText];
            updatedStep.fields = [...(updatedStep.fields || []), buttonText];
          } else {
            // Old format: buttons are objects
            const newButton = {
              id: buttonId,
              text: buttonText,
              selector:
                update.button.selector ||
                `${update.groupSelector || existingStep.groupLocation?.selector || existingStep._metadata?.selectorPath || ""} > div:nth-child(${(existingStep.buttons?.length || 0) + 1})`,
              index: existingStep.buttons?.length || 0,
            };

            updatedStep.buttons = [...currentButtons, newButton];
            updatedStep.fields = [...(updatedStep.fields || []), buttonText];
            updatedStep.transformedText = updatedStep.fields.join(", ");
          }

          // Update metadata
          updatedStep._metadata = {
            ...updatedStep._metadata,
            buttonCount: updatedStep.buttons.length,
            groupName:
              updatedStep.groupName ||
              updatedStep._metadata?.groupName ||
              "Button Group",
          };

          // Update group location if groupSelector provided
          if (update.groupSelector) {
            updatedStep.groupLocation = {
              ...updatedStep.groupLocation,
              selector: update.groupSelector,
            };

            // Update selectors to new minimal format
            updatedStep.selectors = [
              {
                start: update.groupSelector,
                end: update.groupSelector,
              },
            ];
          }

          console.log("[🔘 BUTTON GROUP] Added button to step:", buttonText);
        }

        // ============================================================
        // ✅ HANDLE REMOVE BUTTON - Supports both old and new formats
        // ============================================================
        else if (update.type === "removeButton" && update.buttonId) {
          console.log(
            "[🔘 BUTTON GROUP] Removing button from step:",
            update.buttonId,
          );

          let currentButtons = updatedStep.buttons || [];
          const isNewFormat =
            currentButtons.length === 0 ||
            typeof currentButtons[0] === "string";

          if (isNewFormat) {
            // New format: buttons are strings - filter by text
            const buttonText = update.buttonText || "";
            const updatedButtons = currentButtons.filter(
              (b) => b !== buttonText,
            );
            const updatedFields = (updatedStep.fields || []).filter(
              (f) => f !== buttonText,
            );

            if (updatedButtons.length === 0) {
              console.log("[🔘 BUTTON GROUP] No buttons left, removing step");
              return prev.filter((_, idx) => idx !== stepIndex);
            }

            updatedStep.buttons = updatedButtons;
            updatedStep.fields = updatedFields;
          } else {
            // Old format: buttons are objects - filter by id
            const updatedButtons = currentButtons.filter(
              (b) => b.id !== update.buttonId,
            );
            const updatedFields = (updatedStep.fields || []).filter(
              (f) => f !== update.buttonText,
            );

            if (updatedButtons.length === 0) {
              console.log("[🔘 BUTTON GROUP] No buttons left, removing step");
              return prev.filter((_, idx) => idx !== stepIndex);
            }

            updatedStep.buttons = updatedButtons;
            updatedStep.fields = updatedFields;
            updatedStep.transformedText = updatedFields.join(", ");
          }

          // Update metadata
          updatedStep._metadata = {
            ...updatedStep._metadata,
            buttonCount: updatedStep.buttons.length,
          };

          console.log(
            "[🔘 BUTTON GROUP] Removed button from step:",
            update.buttonText || update.buttonId,
          );
        }

        // ============================================================
        // ✅ HANDLE UPDATE GROUP NAME
        // ============================================================
        else if (update.type === "updateGroupName" && update.groupName) {
          console.log(
            "[🔘 BUTTON GROUP] Updating group name to:",
            update.groupName,
          );

          updatedStep.groupName = update.groupName;
          updatedStep._metadata = {
            ...updatedStep._metadata,
            groupName: update.groupName,
          };

          // Also update the content if it exists
          if (updatedStep.content) {
            updatedStep.content = update.groupName;
            updatedStep.trimmed = update.groupName;
          }
        }

        // ============================================================
        // ✅ HANDLE UPDATE BUTTON TEXT
        // ============================================================
        else if (
          update.type === "updateButtonText" &&
          update.buttonId &&
          update.newText
        ) {
          console.log(
            "[🔘 BUTTON GROUP] Updating button text to:",
            update.newText,
          );

          let currentButtons = updatedStep.buttons || [];
          const isNewFormat =
            currentButtons.length === 0 ||
            typeof currentButtons[0] === "string";

          if (isNewFormat) {
            // New format: buttons are strings - find and replace
            const index = currentButtons.indexOf(
              update.oldText || update.buttonText,
            );
            if (index !== -1) {
              const updatedButtons = [...currentButtons];
              updatedButtons[index] = update.newText;
              updatedStep.buttons = updatedButtons;

              // Update fields too
              const fieldIndex = (updatedStep.fields || []).indexOf(
                update.oldText || update.buttonText,
              );
              if (fieldIndex !== -1) {
                const updatedFields = [...(updatedStep.fields || [])];
                updatedFields[fieldIndex] = update.newText;
                updatedStep.fields = updatedFields;
              }
            }
          } else {
            // Old format: buttons are objects - find by id
            const buttonIndex = currentButtons.findIndex(
              (b) => b.id === update.buttonId,
            );
            if (buttonIndex !== -1) {
              const updatedButtons = [...currentButtons];
              updatedButtons[buttonIndex] = {
                ...updatedButtons[buttonIndex],
                text: update.newText,
              };
              updatedStep.buttons = updatedButtons;

              // Update fields too
              const fieldIndex = (updatedStep.fields || []).indexOf(
                update.oldText || update.buttonText,
              );
              if (fieldIndex !== -1) {
                const updatedFields = [...(updatedStep.fields || [])];
                updatedFields[fieldIndex] = update.newText;
                updatedStep.fields = updatedFields;
              }
              updatedStep.transformedText = updatedStep.fields.join(", ");
            }
          }

          // Update metadata
          updatedStep._metadata = {
            ...updatedStep._metadata,
            buttonCount: updatedStep.buttons.length,
          };
        }

        // ============================================================
        // ✅ HANDLE COMPLETE STEP REPLACEMENT (for direct updates)
        // ============================================================
        else if (update.type === "replaceStep" && update.step) {
          console.log(
            "[🔘 BUTTON GROUP] Replacing step with new data:",
            update.step,
          );

          // Clean the step - remove old format flags
          const cleanStep = { ...update.step };
          delete cleanStep.isStructural;
          delete cleanStep.isMedia;
          delete cleanStep.isStructuralMultiNode;
          delete cleanStep.isMultiRowSelection;
          delete cleanStep.rowCount;
          delete cleanStep.isButtonTransformation;
          delete cleanStep.isButtonGroupTransformation;
          delete cleanStep.applied;
          delete cleanStep.originalText;
          delete cleanStep.transformedText;
          delete cleanStep.selectorType;
          delete cleanStep.data;

          // Ensure selectors are in new format
          if (cleanStep.selectors) {
            cleanStep.selectors = cleanStep.selectors.map((s) => {
              if (typeof s.start === "string" && typeof s.end === "string") {
                return {
                  start: s.start,
                  end: s.end,
                  ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
                };
              }
              return {
                start: s.start?.elementPath || s.start || "",
                end: s.end?.elementPath || s.end || "",
                ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
              };
            });
          }

          // If it's a button group, ensure buttons are strings
          if (cleanStep.type === "button-group" && cleanStep.buttons) {
            cleanStep.buttons = cleanStep.buttons.map((b) =>
              typeof b === "string" ? b : b.text || String(b),
            );
          }

          updatedStep = cleanStep;
          console.log("[🔘 BUTTON GROUP] Step replaced successfully");
        }

        // ============================================================
        // ✅ HANDLE FULL STEP OVERWRITE (when update is a complete step)
        // ============================================================
        else if (update.type === undefined && update.id && update.type) {
          // This is a complete step object being passed directly
          console.log(
            "[🔘 BUTTON GROUP] Overwriting step with complete step object",
          );

          // Clean the step
          const cleanStep = { ...update };
          delete cleanStep.isStructural;
          delete cleanStep.isMedia;
          delete cleanStep.isStructuralMultiNode;
          delete cleanStep.isMultiRowSelection;
          delete cleanStep.rowCount;
          delete cleanStep.isButtonTransformation;
          delete cleanStep.isButtonGroupTransformation;
          delete cleanStep.applied;
          delete cleanStep.originalText;
          delete cleanStep.transformedText;
          delete cleanStep.selectorType;
          delete cleanStep.data;

          updatedStep = cleanStep;
        }

        // ============================================================
        // ✅ ENSURE TIMESTAMP IS SET
        // ============================================================
        if (!updatedStep.timestamp) {
          updatedStep.timestamp = new Date().toISOString();
        }

        // ============================================================
        // ✅ Return the updated rules
        // ============================================================
        const newRules = [...prev];
        newRules[stepIndex] = updatedStep;

        console.log(
          "[🔘 BUTTON GROUP] Step updated successfully:",
          updatedStep,
        );
        console.log("[🔘 BUTTON GROUP] Updated step type:", updatedStep.type);
        if (updatedStep.type === "button-group") {
          console.log("[🔘 BUTTON GROUP] Buttons:", updatedStep.buttons);
        }
        if (updatedStep.type === "button") {
          console.log("[🔘 BUTTON GROUP] Button text:", updatedStep.buttonText);
        }

        return newRules;
      });
    },
    [setTransformationRules],
  );

  // ============================================================
  // RENDER PREVIEW - NO PARSING, ONLY RENDERING
  // ============================================================
  const renderPreview = (linesOverride = null) => {
    console.log(
      "[MISSION CRITICAL renderPreview] START - RENDERING ONLY, NO PARSING",
    );

    if (template && parsedSample) {
      const result = computeCaseAndValidate(template, parsedSample);
      if (result && !result.validation.valid) {
        setError(`Template Error: ${result.validation.errors[0].message}`);
        setRenderedPreview("");
        return;
      }
    }

    setError(null);

    if (!sampleResponse.trim()) {
      setRenderedPreview("(Please provide a sample response)");
      return;
    }

    if (!template.trim()) {
      setRenderedPreview("(Please provide a template)");
      return;
    }

    // ============================================================
    // ✅ Resolve the lines to use.
    //
    // Priority:
    //   1. `linesOverride` — passed explicitly by callers that
    //      just computed a fresh templateLines array (Start Fresh,
    //      Keep Raw View, etc.). Reading this first eliminates the
    //      closure-timing hazard where renderPreview used to see
    //      the *stale* templateLines state on the same tick that
    //      setTemplateLines was called.
    //   2. `templateLines` — normal render path.
    //   3. Reparse inline — fallback when templateLines is empty.
    // ============================================================
    let linesToUse = linesOverride || templateLines;

    if (!linesToUse || linesToUse.length === 0) {
      console.log(
        "[renderPreview] ⚠️ templateLines is empty, parsing inline...",
      );

      if (transformationRules && transformationRules.length > 0) {
        linesToUse = parseTemplateForEditFirstLoadOnly(
          template,
          parsedSample,
          isCase2,
          null,
          transformationRules,
        );
        console.log(
          `[renderPreview] 📊 Parsed with FirstLoadOnly, got ${linesToUse?.length || 0} lines`,
        );
      } else {
        linesToUse = parseTemplateForEdit(
          template,
          parsedSample,
          isCase2,
          null,
        );
        console.log(
          `[renderPreview] 📊 Parsed with regular parser, got ${linesToUse?.length || 0} lines`,
        );
      }

      if (linesToUse && linesToUse.length > 0) {
        setTemplateLines(linesToUse);
      }
    }

    // Track whether the caller passed explicit lines. If they did,
    // they are responsible for having already updated templateLines
    // state. If they didn't (and we had to fall back to inline
    // parsing), we've already called setTemplateLines above.
    const linesWereProvidedByCaller = !!linesOverride;

    let parsed = null;
    let originalIsArray = false;
    let shouldTreatAsArray = false;
    let nestedArrayKey = null;
    let effectiveArrayData = null;
    let isCase3WithMetadata = false;

    let hasContentBeforeLoop = false;
    let hasContentAfterLoop = false;
    let foundForLoop = false;

    try {
      parsed = JSON.parse(sampleResponse);
      console.log(
        "[renderPreview] parsed JSON length:",
        JSON.stringify(parsed).length,
      );

      if (Array.isArray(parsed)) {
        originalIsArray = true;
        if (parsed.length === 0) {
          setError("Sample response is an empty array.");
          setRenderedPreview("");
          return;
        }
        effectiveArrayData = parsed;
        console.log(`[renderPreview] Root array with ${parsed.length} items`);
      }

      // Analyze template structure
      const templateLinesArr = template.split("\n");
      hasContentBeforeLoop = false;
      hasContentAfterLoop = false;
      foundForLoop = false;
      let forLoopDepth = 0;
      let outerLoopStartLine = -1;
      let outerLoopEndLine = -1;
      let loopVarName = "item";
      let loopIterable = "response_data";

      for (let i = 0; i < templateLinesArr.length; i++) {
        const trimmed = templateLinesArr[i].trim();
        if (
          !trimmed ||
          trimmed.startsWith("{#") ||
          trimmed.startsWith("{% comment %")
        )
          continue;

        const isForLoopStart =
          /{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/.test(trimmed);
        const isForLoopEnd = /{%\s*endfor\s*%}/.test(trimmed);

        if (isForLoopStart) {
          foundForLoop = true;
          forLoopDepth++;
          if (outerLoopStartLine === -1) {
            outerLoopStartLine = i;
            const match = trimmed.match(
              /{%\s*for\s+([a-zA-Z_]+)\s+in\s+([^%]+)\s*%}/,
            );
            if (match) {
              loopVarName = match[1];
              loopIterable = match[2].trim();
            }
          }
          continue;
        }

        if (isForLoopEnd) {
          forLoopDepth--;
          if (forLoopDepth === 0 && outerLoopEndLine === -1) {
            outerLoopEndLine = i;
          }
          continue;
        }

        if (!foundForLoop && forLoopDepth === 0) {
          const isTemplateTag =
            trimmed.startsWith("{%") || trimmed.startsWith("{#");
          if (!isTemplateTag && trimmed.length > 0) {
            hasContentBeforeLoop = true;
          }
        }

        if (
          foundForLoop &&
          forLoopDepth === 0 &&
          outerLoopEndLine !== -1 &&
          i > outerLoopEndLine
        ) {
          const isTemplateTag =
            trimmed.startsWith("{%") || trimmed.startsWith("{#");
          if (!isTemplateTag && trimmed.length > 0) {
            hasContentAfterLoop = true;
          }
        }
      }

      // Determine case and set isCase2 state
      if (originalIsArray && Array.isArray(parsed)) {
        shouldTreatAsArray = true;
        effectiveArrayData = parsed;
        setIsCase2(false);
        console.log("[renderPreview] 📊 Case 1: Root array detected");
      } else if (
        foundForLoop &&
        !hasContentBeforeLoop &&
        !hasContentAfterLoop
      ) {
        // ============================================================
        // ✅ Determine which array to iterate by reading the template's
        //    outer {% for X in response_data.<key> %} — NOT by guessing
        //    from object key order.
        //
        // The previous version walked Object.keys(parsed) and took the
        // first array it saw. That picked "staff" for this response
        // (because staff appears before rooms in the JSON), even though
        // the template's outer loop iterates "response_data.rooms".
        // The result: effectiveArrayData was staff, but the loop variable
        // was `room`, so `{{ room.id }}` resolved to staff[0].id and every
        // `{{ room.<rooms-field> }}` came out empty.
        //
        // The correct source of truth is `loopIterable`, which the scan
        // above already extracted from the template. Parse it once,
        // resolve it against `parsed`, and use that array.
        // ============================================================
        let foundNestedArray = false;

        console.log(
          `[renderPreview] 🎯 Template's outer loop iterates: ${loopIterable}`,
        );

        if (
          typeof parsed === "object" &&
          parsed !== null &&
          loopIterable.startsWith("response_data.")
        ) {
          const iterableKey = loopIterable.slice("response_data.".length);

          // Support dotted paths like "response_data.bookings.guests" by
          // walking the object. For a flat "response_data.rooms" this is a
          // single lookup.
          const pathSegments = iterableKey.split(".");
          let candidate = parsed;
          let resolved = true;
          for (const seg of pathSegments) {
            if (
              candidate &&
              typeof candidate === "object" &&
              seg in candidate
            ) {
              candidate = candidate[seg];
            } else {
              resolved = false;
              break;
            }
          }

          if (resolved && Array.isArray(candidate) && candidate.length > 0) {
            shouldTreatAsArray = true;
            nestedArrayKey = iterableKey;
            effectiveArrayData = candidate;
            foundNestedArray = true;

            const otherKeys = Object.keys(parsed).filter(
              (k) => k !== pathSegments[0],
            );
            isCase3WithMetadata = otherKeys.length > 0;

            console.log(
              `[renderPreview] 📊 Case 3: Nested array "${iterableKey}" with ${candidate.length} items (resolved from template)`,
            );
            if (isCase3WithMetadata) {
              console.log(
                `[renderPreview] 📊 Case 3 with metadata: ${otherKeys.join(", ")}`,
              );
            }
          } else {
            console.warn(
              `[renderPreview] ⚠️ Template iterates "${loopIterable}" but that path did not resolve to a non-empty array in the sample. Falling back to first array found.`,
            );
          }
        }

        // ------------------------------------------------------------
        // Fallback: template's iterable did not resolve (e.g. the sample
        // doesn't contain that key, or the template was hand-edited to
        // point at a path the sample doesn't have). Fall back to the old
        // "first array found" heuristic so a best-effort render still
        // happens.
        // ------------------------------------------------------------
        if (!foundNestedArray && typeof parsed === "object" && parsed !== null) {
          for (const key of Object.keys(parsed)) {
            if (Array.isArray(parsed[key]) && parsed[key].length > 0) {
              shouldTreatAsArray = true;
              nestedArrayKey = key;
              effectiveArrayData = parsed[key];
              foundNestedArray = true;

              const otherKeys = Object.keys(parsed).filter((k) => k !== key);
              isCase3WithMetadata = otherKeys.length > 0;

              console.warn(
                `[renderPreview] 📊 Case 3 FALLBACK: using first array "${key}" with ${parsed[key].length} items (template iterable did not resolve)`,
              );
              break;
            }
          }
        }

        if (!foundNestedArray) {
          shouldTreatAsArray = false;
          effectiveArrayData = [parsed];
          setIsCase2(false);
          console.log("[renderPreview] 📊 Single object with loop");
        } else {
          setIsCase2(false);
        }
      } else if (
        foundForLoop &&
        (hasContentBeforeLoop || hasContentAfterLoop)
      ) {
        shouldTreatAsArray = false;
        setIsCase2(true);
        console.log("[renderPreview] 📊 Case 2: Single screen with metadata");
      } else {
        shouldTreatAsArray = false;
        setIsCase2(false);
        console.log("[renderPreview] 📊 No loop detected");
      }

      setIsSampleArray(shouldTreatAsArray);
      setParsedSample(parsed);
    } catch (err) {
      console.error("[renderPreview] ❌ JSON Parse Error:", err);
      setError(`Invalid JSON: ${err.message}`);
      setRenderedPreview("");
      return;
    }

    // ============================================================
    // RENDER USING SHARED FUNCTION
    // ============================================================
    try {
      const enhancedTemplate = enhanceTemplateWithLoopWrappers(
        template,
        linesToUse,
      );
      console.log("[renderPreview] ✅ Template enhanced with loop wrappers");

      const renderedHTML = renderScreens(
        enhancedTemplate,
        parsed,
        effectiveArrayData,
        isCase3WithMetadata,
        isCase2,
        linesToUse,
        shouldTreatAsArray,
      );

      setRenderedPreview(renderedHTML);

      if (
        shouldTreatAsArray &&
        effectiveArrayData &&
        effectiveArrayData.length > 0
      ) {
        setDataType("array");
      } else if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        setDataType("object");
      } else {
        setDataType("text");
      }

      // Build scenario tree
      const conditionBlocks = extractNestedConditions(template);
      if (conditionBlocks.length > 0) {
        const tree = buildScenarioTree(conditionBlocks, parsed);
        setScenarioTree(tree);
        const activePath = findActivePath(tree, parsed);
        if (activePath && activePath.length > 0) {
          setActivePathIds(activePath);
        } else if (tree.length > 0) {
          setActivePathIds([tree[0].id]);
        }
      }
    } catch (err) {
      console.error("[renderPreview] ❌ Template Error:", err);
      setError(`Template Error: ${err.message}`);
      setRenderedPreview("");
    }
  };

  const handleKeepRawView = useCallback(() => {
    console.log(
      "[PreviewTester] 📌 Keep Raw View - preserving button transformations",
    );

    setShowTransformConfirm(false);
    setPendingTransformSteps([]);

    hasShownReapplyPopup.current = true;
    editorWasOpenedRef.current = false;
    setIsInitialLoad(false);

    // ============================================================
    // Keep only the button / button-group transformation steps.
    // These are the ones with a valid _templateRowIndex.
    // ============================================================
    const buttonSteps = loadedTransformationSteps.filter(
      (step) =>
        step.type === "button" ||
        step.type === "button-group" ||
        step.isButtonTransformation === true ||
        step.isButtonGroupTransformation === true,
    );

    console.log(
      `[PreviewTester] 🔘 Preserving ${buttonSteps.length} button/group steps`,
    );

    // Update Control Panel rules.
    setTransformationRules(buttonSteps);

    // ============================================================
    // Reinstate button / button-group rows in templateLines using
    // their saved _templateRowIndex. Because buttons carry that
    // index and groups carry it too, we can map them back to the
    // exact positions where the user originally placed them.
    //
    // Any templateLine that was previously a plain-text row at
    // that index is replaced with the corresponding button/group
    // row so the editor shows the button/group UI and the renderer
    // emits button UIs.
    // ============================================================
    const nextLines = [...templateLines];

    for (const step of buttonSteps) {
      const rowIdx = step._templateRowIndex ?? step.templateRowIndex ?? -1;

      if (
        typeof rowIdx !== "number" ||
        rowIdx < 0 ||
        rowIdx >= nextLines.length
      ) {
        console.warn(
          "[PreviewTester] ⚠️ Button/group step has no valid _templateRowIndex:",
          step,
        );
        continue;
      }

      const existing = nextLines[rowIdx];
      if (!existing) continue;

      if (step.type === "button" || step.isButtonTransformation) {
        // Restore as button row.
        nextLines[rowIdx] = {
          ...existing,
          type: "button",
          isButton: true,
          isButtonGroup: false,
          isCustom: true,
          buttonText: step.buttonText || existing.content,
          content: step.buttonText || existing.content,
          trimmed: (step.buttonText || existing.content || "").trim(),
          customId: existing.customId || step.id,
          customMessage: step.buttonText || existing.content,
          _sourceType: "button",
        };
      } else if (
        step.type === "button-group" ||
        step.isButtonGroupTransformation
      ) {
        // Restore as button-group row.
        const groupName =
          step.groupName ||
          step._metadata?.groupName ||
          existing.content ||
          "Button Group";

        // Buttons: normalize to object form the editor expects.
        let buttons = step.buttons || [];
        if (buttons.length > 0 && typeof buttons[0] === "string") {
          buttons = buttons.map((text, idx) => ({
            id: `btn-${step.id}-${idx}`,
            text,
            index: idx,
          }));
        } else if (buttons.length > 0 && typeof buttons[0] === "object") {
          buttons = buttons.map((b, idx) => ({
            id: b.id || `btn-${step.id}-${idx}`,
            text: b.text || b.label || String(b),
            index: b.index !== undefined ? b.index : idx,
          }));
        }

        nextLines[rowIdx] = {
          ...existing,
          type: "button-group",
          isButtonGroup: true,
          isButton: false,
          isCustom: true,
          content: groupName,
          trimmed: groupName.trim(),
          buttons,
          customId: existing.customId || step.id,
          customMessage: groupName,
          _sourceType: "button-group",
        };
      }
    }

    const reparsed = parseTemplateForEdit(
      template,
      parsedSample,
      isCase2,
      nextLines,
    );
    setTemplateLines(reparsed);

    // ✅ Pass lines explicitly — no timing hazard.
    renderPreview(reparsed);

    console.log(
      "[PreviewTester] ✅ Keep Raw View: reinstated",
      buttonSteps.length,
      "button/group rows",
    );
  }, [
    setShowTransformConfirm,
    setPendingTransformSteps,
    setTransformationRules,
    setTemplateLines,
    loadedTransformationSteps,
    templateLines,
    template,
    parsedSample,
    isCase2,
    setIsInitialLoad,
    parseTemplateForEdit,
    renderPreview,
  ]);

  // ============================================================
  // RECALCULATE TEMPLATE FRESH
  //
  // Rebuilds the template from the raw sample response, but for
  // objects that contain arrays it goes through the same
  // Single-vs-Multiple popup the user sees on a fresh paste —
  // instead of silently picking `detectedArrays[0]` (insertion
  // order) which could rebuild around the wrong array.
  //
  // We do this entirely inside PreviewTester. The previous version
  // tried to reset state so LeftPanel's detection would re-fire,
  // but that raced against the URL-param effects that re-set
  // `template` from `templateFromProps` and re-set `sampleResponse`
  // from the API fetch. Driving the popup locally sidesteps the
  // race.
  // ============================================================
  const handleRecalculateTemplateFresh = useCallback(() => {
    console.log(
      "[PreviewTester] ♻️ Recalculate Template Fresh — starting",
    );

    // ----------------------------------------------------------
    // 1. Close popup + wipe in-memory transformation state
    // ----------------------------------------------------------
    setShowTransformConfirm(false);
    setPendingTransformSteps([]);
    hasShownReapplyPopup.current = true;
    editorWasOpenedRef.current = false;
    hasAppliedLoadedTransformations.current = false;
    isApplyingTransformations.current = false;
    setIsInitialLoad(false);

    setTransformationRules([]);
    setActions([]);
    setLoadedTransformationSteps([]);
    setLoadedActions([]);

    setShowPhaseWizard(false);
    setCombinedStep(null);
    setIsSelectingMode(false);
    setShowTransformationPanel(false);
    setSelectedText("");
    setSelectedRange(null);
    setSelectedContainer(null);
    setSelectionType(null);

    // ----------------------------------------------------------
    // 2. Parse the current sample response
    // ----------------------------------------------------------
    let parsed = null;
    try {
      parsed = JSON.parse(sampleResponse);
    } catch (err) {
      console.warn(
        "[PreviewTester] ⚠️ Invalid JSON — resetting to DEFAULT template",
        err.message,
      );
      setTemplate(DEFAULT_TEMPLATE);
      setUserScreenChoice(null);
      setIsCase2(false);
      setTemplateLines([]);
      setParsedSample(null);
      renderPreview();
      return;
    }

    // ----------------------------------------------------------
    // 3a. Primitive (string / number / boolean / null)
    // ----------------------------------------------------------
    if (
      parsed === null ||
      parsed === undefined ||
      typeof parsed === "string" ||
      typeof parsed === "number" ||
      typeof parsed === "boolean"
    ) {
      const newTemplate =
        parsed === null || parsed === undefined
          ? DEFAULT_TEMPLATE
          : String(parsed);

      setTemplate(newTemplate);
      setUserScreenChoice("single");
      setIsCase2(true);

      const freshLines = parseTemplateForEdit(newTemplate, parsed, true, null);
      setTemplateLines(freshLines);
      setParsedSample(parsed);
      renderPreview(freshLines);
      return;
    }

    // ----------------------------------------------------------
    // 3b. Root array → Case 1 (no popup needed)
    // ----------------------------------------------------------
    if (Array.isArray(parsed)) {
      const generated = generateTemplateFromJSON(parsed);
      const newTemplate =
        generated && typeof generated === "string" && generated.trim()
          ? generated
          : DEFAULT_TEMPLATE;

      setTemplate(newTemplate);
      setUserScreenChoice("multiple");
      setIsCase2(false);

      const freshLines = parseTemplateForEdit(newTemplate, parsed, false, null);
      setTemplateLines(freshLines);
      setParsedSample(parsed);
      renderPreview(freshLines);
      return;
    }

    // ----------------------------------------------------------
    // 3c. Object → check for arrays
    // ----------------------------------------------------------
    if (typeof parsed === "object") {
      const detectedArrays = detectNestedArrays(parsed);

      if (detectedArrays.length > 0) {
        // ── Object with arrays: show the layout-choice popup ──
        console.log(
          "[PreviewTester] 🔔 Recalc Fresh — opening layout-choice popup",
        );

        // Seed parsedSample so downstream pieces (e.g. flattenObject
        // for availableVariables) have a value while the popup is up.
        setParsedSample(parsed);

        const nestedArrays = detectedArrays.filter((a) => a.isNested);

        setRecalcParsedResponse(parsed);
        setRecalcDetectedArrays({
          allArrays: detectedArrays,
          nestedArrays,
        });
        setShowRecalcLayoutPopup(true);
        return;
      }

      // ── Flat object → Case 2 ──
      const generated = generateTemplateFromJSON(parsed);
      const newTemplate =
        generated && typeof generated === "string" && generated.trim()
          ? generated
          : DEFAULT_TEMPLATE;

      setTemplate(newTemplate);
      setUserScreenChoice("single");
      setIsCase2(true);

      const freshLines = parseTemplateForEdit(newTemplate, parsed, true, null);
      setTemplateLines(freshLines);
      setParsedSample(parsed);
      renderPreview(freshLines);
      return;
    }

    // Unreachable, but keep a safe default.
    setTemplate(DEFAULT_TEMPLATE);
    setUserScreenChoice(null);
    setIsCase2(false);
    setTemplateLines([]);
    setParsedSample(null);
    renderPreview();
  }, [sampleResponse, renderPreview]);

  // ------------------------------------------------------------
  // Recalc-Fresh popup handlers
  // ------------------------------------------------------------

  const handleRecalcConfirmSingleScreen = useCallback(() => {
    console.log("[PreviewTester] ♻️ Recalc Fresh → Single Screen");

    const parsed = recalcParsedResponse;
    setShowRecalcLayoutPopup(false);
    setRecalcParsedResponse(null);
    setRecalcDetectedArrays(null);

    if (!parsed) return;

    const generated = generateTemplateFromJSON(parsed);
    const newTemplate =
      generated && typeof generated === "string" && generated.trim()
        ? generated
        : DEFAULT_TEMPLATE;

    setTemplate(newTemplate);
    setUserScreenChoice("single");
    setIsCase2(true);

    const freshLines = parseTemplateForEdit(newTemplate, parsed, true, null);
    setTemplateLines(freshLines);
    setParsedSample(parsed);
    renderPreview(freshLines);
  }, [recalcParsedResponse, renderPreview]);

  const handleRecalcConfirmMultipleScreens = useCallback(
    (selectedArrayPath) => {
      console.log(
        "[PreviewTester] ♻️ Recalc Fresh → Multiple Screens with array:",
        selectedArrayPath,
      );

      const parsed = recalcParsedResponse;
      setShowRecalcLayoutPopup(false);
      setRecalcParsedResponse(null);
      setRecalcDetectedArrays(null);

      if (!parsed || !selectedArrayPath) return;

      const generated = generateCase3Template(parsed, selectedArrayPath);
      const newTemplate =
        generated && typeof generated === "string" && generated.trim()
          ? generated
          : DEFAULT_TEMPLATE;

      setTemplate(newTemplate);
      setUserScreenChoice("multiple");
      setIsCase2(false);

      const freshLines = parseTemplateForEdit(newTemplate, parsed, false, null);
      setTemplateLines(freshLines);
      setParsedSample(parsed);
      renderPreview(freshLines);
    },
    [recalcParsedResponse, renderPreview],
  );

  const handleRecalcClosePopup = useCallback(() => {
    console.log(
      "[PreviewTester] ♻️ Recalc Fresh → cancelled (defaults to Single Screen)",
    );

    const parsed = recalcParsedResponse;
    setShowRecalcLayoutPopup(false);
    setRecalcParsedResponse(null);
    setRecalcDetectedArrays(null);

    if (!parsed) return;

    // Match the fresh-paste behavior: cancel defaults to Single Screen.
    const generated = generateTemplateFromJSON(parsed);
    const newTemplate =
      generated && typeof generated === "string" && generated.trim()
        ? generated
        : DEFAULT_TEMPLATE;

    setTemplate(newTemplate);
    setUserScreenChoice("single");
    setIsCase2(true);

    const freshLines = parseTemplateForEdit(newTemplate, parsed, true, null);
    setTemplateLines(freshLines);
    setParsedSample(parsed);
    renderPreview(freshLines);
  }, [recalcParsedResponse, renderPreview]);


  // In PreviewTester.js
  const handleUpdateAction = (updatedAction) => {
    console.log("[PreviewTester] ✏️ Updating action:", updatedAction);
    setActions((prev) =>
      prev.map((action) =>
        action.id === updatedAction.id ? updatedAction : action,
      ),
    );
  };

  // ============================================================
  // HANDLE START FRESH - Remove ALL transformations
  // ============================================================

  const handleStartFresh = useCallback(() => {
    console.log(
      "[PreviewTester] 🧹 Start Fresh - removing all transformations",
    );

    setShowTransformConfirm(false);
    setPendingTransformSteps([]);

    hasShownReapplyPopup.current = true;
    editorWasOpenedRef.current = false;
    setIsInitialLoad(false);

    // Clear transformation rules and actions.
    setTransformationRules([]);
    setActions([]);

    // ============================================================
    // templateLines already has plain-text rows for both the
    // regular button and the button-group name (that's how they
    // were saved). We deliberately do NOT use _templateRowIndex
    // here — no button/group rows to reinstate.
    //
    // So: just re-render from the current templateLines. The
    // enhancer + wrapFieldValuesWithSpans will produce plain
    // text rows for both.
    //
    // We pass templateLines explicitly to renderPreview so it
    // reads the *current* value, not whatever its closure holds.
    // ============================================================
    const currentLines =
      templateLines.length > 0
        ? templateLines
        : parseTemplateForEdit(template, parsedSample, isCase2, null);

    // Ensure both rows are plain text (defensive — should already be
    // the case, but guards against stale state from a prior render).
    const cleanedLines = currentLines.map((row) => {
      if (row.type === "button-group" || row.isButtonGroup === true) {
        return {
          ...row,
          type: "text",
          isButtonGroup: false,
          isButton: false,
          isCustom: false,
          buttons: null,
          customId: null,
          customMessage: null,
          _sourceType: "text",
        };
      }
      if (row.type === "button" || row.isButton === true) {
        return {
          ...row,
          type: "text",
          isButton: false,
          isButtonGroup: false,
          isCustom: false,
          buttonText: null,
          customId: null,
          customMessage: null,
          _sourceType: "text",
        };
      }
      return row;
    });

    const reparsed = parseTemplateForEdit(
      template,
      parsedSample,
      isCase2,
      cleanedLines,
    );
    setTemplateLines(reparsed);

    // ✅ Pass lines explicitly — no timing hazard.
    renderPreview(reparsed);

    console.log("[PreviewTester] ✅ All transformations removed");
    console.log("[PreviewTester] 📋 transformationRules count: 0");
    console.log("[PreviewTester] 📋 actions count: 0");
  }, [
    setShowTransformConfirm,
    setPendingTransformSteps,
    setTransformationRules,
    setTemplateLines,
    setActions,
    templateLines,
    template,
    parsedSample,
    isCase2,
    setIsInitialLoad,
    parseTemplateForEdit,
    renderPreview,
  ]);

  // ============================================================
  // HANDLE APPLY SAVED TRANSFORMATIONS - Full apply
  // ============================================================
  const handleApplyTransformations = useCallback(() => {
    console.log("[PreviewTester] ✅ User chose: Apply Saved Transformations");
    setShowTransformConfirm(false);

    // ✅ Call applyLoadedTransformations - it handles everything internally
    applyLoadedTransformations(pendingTransformSteps);

    // ✅ REMOVED: Duplicate conversion - already handled inside applyLoadedTransformations
    // The button group conversion is done inside applyLoadedTransformations
    // to ensure proper ordering and avoid overwriting

    setPendingTransformSteps([]);
    setIsInitialLoad(false);
  }, [
    pendingTransformSteps,
    applyLoadedTransformations,
    setPendingTransformSteps,
    setIsInitialLoad,
  ]);

  const updateUrlParams = (scenario, configIdVal, kbIdVal, statusCodeVal) => {
    const params = new URLSearchParams();
    if (scenario) params.set("scenario", scenario);
    if (configIdVal) params.set("config_id", configIdVal);
    if (kbIdVal) params.set("kb_id", kbIdVal);
    if (statusCodeVal) params.set("status_code", statusCodeVal);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  };

  const immediateSelectionRef = useRef({
    text: "",
    selector: null,
    container: null,
  });

  // ============================================================
  // CRITICAL FIX: handleTextSelect - Stores selector immediately
  // ============================================================
  const handleTextSelect = (text, range, containerElement) => {
    console.log(
      "[PreviewTester] 📝 handleTextSelect called:",
      text ? text.substring(0, 50) + "..." : "empty",
    );
    console.log("[PreviewTester] 📥 Full Input:", {
      text: text,
      textLength: text?.length || 0,
      range: range,
      rangeStartContainer: range?.startContainer,
      rangeEndContainer: range?.endContainer,
      rangeStartOffset: range?.startOffset,
      rangeEndOffset: range?.endOffset,
      rangeCollapsed: range?.collapsed,
      containerElement: containerElement,
      containerElementTag: containerElement?.tagName,
      containerElementClass: containerElement?.className,
      containerElementId: containerElement?.id,
      isSelectingMode: isSelectingMode,
    });

    if (!isSelectingMode) {
      console.log("[PreviewTester] ⏭️ Not in select mode, ignoring selection");
      setShowTransformationPanel(false);
      return;
    }

    if (!text || text.trim().length === 0) {
      console.log("[PreviewTester] ⏭️ Empty selection, ignoring");
      setShowTransformationPanel(false);
      setSelectedText("");
      setSelectedRange(null);
      setSelectedContainer(null);
      setSelectionType(null);
      immediateSelectionRef.current = {
        text: "",
        selector: null,
        container: null,
      };
      return;
    }

    console.log(
      "[PreviewTester] 📝 Selection detected, text length:",
      text.length,
    );

    const selector = generatePositionSelector(text, containerElement, range, 2);

    immediateSelectionRef.current = {
      text: text,
      selector: selector,
      container: containerElement,
    };

    setSelectedText(text);
    setSelectedRange(range);
    setSelectedContainer(containerElement);

    let hasForLoopStarted = false;

    if (range && containerElement) {
      try {
        let startContainer = range.startContainer;
        let endContainer = range.endContainer;

        if (startContainer && startContainer.nodeType === 3) {
          startContainer = startContainer.parentElement;
        }
        if (endContainer && endContainer.nodeType === 3) {
          endContainer = endContainer.parentElement;
        }

        const checkForForLoop = (element) => {
          if (!element) return false;
          let current = element;
          while (
            current &&
            current !== containerElement &&
            current !== document.body
          ) {
            if (
              current.classList &&
              current.classList.contains("for-loop-started")
            ) {
              return true;
            }
            current = current.parentElement;
          }
          return false;
        };

        hasForLoopStarted =
          checkForForLoop(startContainer) || checkForForLoop(endContainer);

        console.log(
          "[PreviewTester] 📊 hasForLoopStarted (DOM check):",
          hasForLoopStarted,
        );
      } catch (err) {
        console.warn(
          "[PreviewTester] ⚠️ Error detecting for-loop-started:",
          err,
        );
      }
    }

    const typeInfo = detectSelectionType(text, hasForLoopStarted);
    setSelectionType(typeInfo);
    console.log("[PreviewTester] 📊 Selection type:", typeInfo);

    if (showPhaseWizard && text && text.trim().length > 0) {
      console.log("[PreviewTester] 📍 Generated selector:", selector);

      setCombinedStep((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          selector: selector,
          selectors: selector ? [selector] : [],
        };
      });

      console.log(
        "[PreviewTester] ✅ Showing transformation panel (wizard active + text selected)",
      );
      setShowTransformationPanel(true);
    } else {
      console.log("[PreviewTester] ⏭️ Conditions not met for showing panel:", {
        showPhaseWizard,
        hasText: !!text && text.trim().length > 0,
      });
      setShowTransformationPanel(false);
    }
  };

  const handleCloseTransformationPanel = () => {
    setShowTransformationPanel(false);
    if (!showPhaseWizard) {
      setSelectedText("");
      setSelectedRange(null);
      setSelectedContainer(null);
      setSelectionType(null);
    }
  };


  // PreviewTester.js - createDeepSelectors

  /**
   * Creates per-row deep selectors for a multi-row selection inside a loop.
   *
   * RULES OBSERVED:
   *   R2. Every returned selector has start/end relative to loopSelector.
   *   R4. Row-level (shallow) when the row is fully covered;
   *       leaf-level (deep) when the row is partially covered.
   *
   * SHAPE CONTRACT WITH getRelativePath:
   *   getRelativePath is the single source of truth for loop-relative
   *   path shapes. It now ALWAYS includes the iteration wrapper
   *   segment (`div:nth-child(1)`) for both row-level and leaf-level
   *   selections inside a wrapped loop.
   *
   *   createDeepSelectors therefore MUST NOT prepend that segment.
   *   Doing so produces a doubled segment and breaks resolution.
   */
  const createDeepSelectors = (
    selector,
    container,
    isInsideLoop,
    loopElement,
  ) => {
    console.log(`[createDeepSelectors] START (isInsideLoop: ${isInsideLoop})`);

    // ============================================================
    // ✅ Get metadata safely - supports both old and new formats
    // ============================================================
    const metadata = selector._metadata || {};

    // Get start and end paths - supports both old and new formats
    const startPath = selector.start?.elementPath || selector.start || "";
    const endPath = selector.end?.elementPath || selector.end || "";

    console.log("[createDeepSelectors] startPath:", startPath);
    console.log("[createDeepSelectors] endPath:", endPath);

    // Parse the paths to determine the row level and indices
    const startParts = startPath.split(" > ");
    const endParts = endPath.split(" > ");

    console.log("[createDeepSelectors] startParts:", startParts);
    console.log("[createDeepSelectors] endParts:", endParts);

    // ============================================================
    // ✅ STEP 1: Check if this is a single-row partial selection
    // ============================================================
    let divergenceIndex = -1;
    const minParts = Math.min(startParts.length, endParts.length);

    for (let i = 0; i < minParts; i++) {
      if (startParts[i] !== endParts[i]) {
        divergenceIndex = i;
        break;
      }
    }

    console.log("[createDeepSelectors] divergenceIndex:", divergenceIndex);

    const isSingleRowPartial =
      divergenceIndex > 2 &&
      startParts.slice(0, divergenceIndex).join(" > ") ===
      endParts.slice(0, divergenceIndex).join(" > ");

    console.log(
      "[createDeepSelectors] isSingleRowPartial:",
      isSingleRowPartial,
    );

    if (isSingleRowPartial) {
      console.log(
        "[createDeepSelectors] 📍 Single-row partial selection detected",
      );
      return [
        {
          start: startPath,
          end: endPath,
          ...(selector.loopSelector
            ? { loopSelector: selector.loopSelector }
            : {}),
        },
      ];
    }

    // ============================================================
    // ✅ STEP 2: Determine row level and indices
    // ============================================================
    let rowDepth = 0;

    for (let i = 0; i < minParts; i++) {
      if (startParts[i] !== endParts[i]) {
        rowDepth = i;
        break;
      }
    }

    if (rowDepth === 0 && startParts.length !== endParts.length) {
      rowDepth = Math.min(startParts.length, endParts.length) - 1;
    }

    if (rowDepth === 0) {
      let commonPrefix = 0;
      for (let i = 0; i < minParts; i++) {
        if (startParts[i] === endParts[i]) {
          commonPrefix = i + 1;
        } else {
          break;
        }
      }
      rowDepth = commonPrefix;
    }

    rowDepth = Math.max(2, rowDepth);

    console.log(`[createDeepSelectors] 📍 Row depth: ${rowDepth}`);

    // ============================================================
    // ✅ STEP 3: Parse row indices from metadata or paths
    // ============================================================
    const rowPattern = /div:nth-child\((\d+)\)/;
    let startRowIndex = -1;
    let endRowIndex = -1;

    // First try metadata
    if (
      metadata.startRowIndex !== undefined &&
      metadata.endRowIndex !== undefined
    ) {
      startRowIndex = metadata.startRowIndex;
      endRowIndex = metadata.endRowIndex;
      console.log(
        "[createDeepSelectors] 📍 Using metadata row indices:",
        { startRowIndex, endRowIndex },
      );
    } else {
      // Parse from paths
      if (startParts.length > rowDepth) {
        const startRowPart = startParts[rowDepth];
        const startMatch = startRowPart.match(rowPattern);
        if (startMatch) {
          startRowIndex = parseInt(startMatch[1]) - 1;
        }
      }

      if (endParts.length > rowDepth) {
        const endRowPart = endParts[rowDepth];
        const endMatch = endRowPart.match(rowPattern);
        if (endMatch) {
          endRowIndex = parseInt(endMatch[1]) - 1;
        }
      }
    }

    if (startRowIndex === -1 || endRowIndex === -1) {
      console.log(
        "[createDeepSelectors] ⚠️ Invalid row indices, treating as single-row partial",
      );
      return [
        {
          start: startPath,
          end: endPath,
          ...(selector.loopSelector
            ? { loopSelector: selector.loopSelector }
            : {}),
        },
      ];
    }

    if (startRowIndex === endRowIndex) {
      console.log(
        "[createDeepSelectors] 📍 Same row, single-row partial selection",
      );
      return [
        {
          start: startPath,
          end: endPath,
          ...(selector.loopSelector
            ? { loopSelector: selector.loopSelector }
            : {}),
        },
      ];
    }

    console.log(
      `[createDeepSelectors] 📍 Row indices: ${startRowIndex} -> ${endRowIndex}`,
    );

    // ============================================================
    // ✅ STEP 4: Handle loop context
    // ============================================================
    if (isInsideLoop && loopElement) {
      const allGlobalRows = container.querySelectorAll(".row");
      const allGlobalRowsArray = Array.from(allGlobalRows);

      const startGlobalRow = allGlobalRowsArray[startRowIndex];
      const endGlobalRow = allGlobalRowsArray[endRowIndex];

      if (startGlobalRow && endGlobalRow) {
        const allLoopRows = loopElement.querySelectorAll(".row");
        const allLoopRowsArray = Array.from(allLoopRows);

        const startLoopIndex = allLoopRowsArray.indexOf(startGlobalRow);
        const endLoopIndex = allLoopRowsArray.indexOf(endGlobalRow);

        if (startLoopIndex !== -1 && endLoopIndex !== -1) {
          startRowIndex = startLoopIndex;
          endRowIndex = endLoopIndex;
          console.log(
            `[createDeepSelectors] 🔄 Using loop indices: ${startRowIndex} -> ${endRowIndex}`,
          );
        }
      }
    }

    const minIdx = Math.min(startRowIndex, endRowIndex);
    const maxIdx = Math.max(startRowIndex, endRowIndex);

    const allRows =
      isInsideLoop && loopElement
        ? loopElement.querySelectorAll(".row")
        : container.querySelectorAll(".row");

    console.log(
      `[createDeepSelectors] Creating deep selectors for rows ${minIdx} to ${maxIdx}`,
    );
    console.log(
      `[createDeepSelectors] ${isInsideLoop ? "🔄 Inside" : "📍 Outside"} loop, found ${allRows.length} rows`,
    );

    if (allRows.length === 0) {
      console.log("[createDeepSelectors] ⚠️ No rows found");
      return [
        {
          start: startPath,
          end: endPath,
          ...(selector.loopSelector
            ? { loopSelector: selector.loopSelector }
            : {}),
        },
      ];
    }

    // ============================================================
    // ✅ STEP 5: Get child path parts (after row level)
    // ============================================================
    const startChildParts = startParts.slice(rowDepth + 1);
    const endChildParts = endParts.slice(rowDepth + 1);

    // Get the target child elements from the original selection
    let targetStartChild =
      startChildParts.length > 0
        ? startChildParts[startChildParts.length - 1]
        : "";
    let targetEndChild =
      endChildParts.length > 0 ? endChildParts[endChildParts.length - 1] : "";

    // Parse child indices - these are the actual selected elements within each row
    let globalStartChildIdx = 0;
    let globalEndChildIdx = -1;

    if (targetStartChild) {
      const match = targetStartChild.match(rowPattern);
      if (match) {
        globalStartChildIdx = parseInt(match[1]) - 1;
      }
    }

    if (targetEndChild) {
      const match = targetEndChild.match(rowPattern);
      if (match) {
        globalEndChildIdx = parseInt(match[1]) - 1;
      }
    }

    if (globalEndChildIdx === -1) {
      globalEndChildIdx = globalStartChildIdx;
    }

    // ============================================================
    // ✅ STEP 6: Determine what was actually selected
    // ============================================================
    const selectedText = metadata.selectedTextPreview || "";
    const includesSeparator = selectedText.includes("-");
    const includesKey = /[a-z_]+/.test(selectedText);
    const includesValue = /\d+/.test(selectedText);

    console.log(`[createDeepSelectors] 📍 Selected text analysis:`, {
      includesSeparator,
      includesKey,
      includesValue,
      selectedText: selectedText.substring(0, 50),
    });

    const selectors = [];

    // ============================================================
    // ✅ STEP 6a: Detect wrapped-loop mode.
    //
    // NOTE: This is retained for the log message only. The
    //       iteration wrapper segment is now INCLUDED by
    //       getRelativePath itself, so we do NOT prepend it here.
    // ============================================================
    const isWrappedLoop = !!(
      loopElement &&
      loopElement.hasAttribute &&
      loopElement.hasAttribute("data-wrap-iterations")
    );

    console.log(`[createDeepSelectors] 📍 isWrappedLoop: ${isWrappedLoop}`);

    // ============================================================
    // ✅ STEP 7: Generate selectors for each row
    // ============================================================
    for (let i = minIdx; i <= maxIdx && i < allRows.length; i++) {
      const row = allRows[i];
      const isFirstRow = i === minIdx;
      const isLastRow = i === maxIdx;
      const isSingleRow = minIdx === maxIdx;

      console.log(
        `[createDeepSelectors] 📍 Processing row ${i + 1} (first: ${isFirstRow}, last: ${isLastRow})`,
      );

      // ============================================================
      // Get the row path.
      //
      // ✅ getRelativePath is the single source of truth. It already
      //    includes the iteration wrapper segment for wrapped loops
      //    (both row-level and leaf-level selections). DO NOT prepend
      //    anything on top of it.
      // ============================================================
      let rowPath;
      if (isInsideLoop && loopElement) {
        rowPath = getRelativePath(row, loopElement);
      } else {
        rowPath = getNthChildPath(row, container);
      }

      if (!rowPath) {
        console.log(
          `[createDeepSelectors] ⚠️ Could not get path for row ${i + 1}`,
        );
        continue;
      }

      // ============================================================
      // ✅ DEV-ONLY GUARD — 1G
      // INVARIANT: The path produced for a row must have the same
      //            segment count as the canonical path that
      //            getRelativePath returns for the same row.
      //
      // Reason: getRelativePath is the single source of truth for
      // the shape of a loop-relative path. If any code path between
      // generation and storage manipulates the path (e.g. prepends
      // or strips a segment), the segment count will diverge. That
      // is exactly what caused the earlier "one extra segment"
      // regression in multi-row selections inside wrapped loops.
      //
      // This guard fires when startPathForRow (per-row output) has
      // an unexpected segment count relative to what getRelativePath
      // would produce. It runs per row, per call, and would have
      // caught the double-prepend immediately.
      //
      // Owned by: getRelativePath in renderingUtils.js
      // ============================================================
      if (process.env.NODE_ENV !== "production") {
        if (isInsideLoop && loopElement) {
          const canonicalRowPath = getRelativePath(row, loopElement);
          const canonicalSegs = canonicalRowPath.split(" > ").length;
          const producedSegs = rowPath.split(" > ").length;

          if (producedSegs !== canonicalSegs) {
            console.warn(
              "[INVARIANT:1G] createDeepSelectors produced a row path whose segment count diverges from getRelativePath.",
              {
                rowIndex: i,
                canonicalRowPath,
                canonicalSegs,
                producedRowPath: rowPath,
                producedSegs,
              },
            );
          }
        }
      }

      const rowChildren = Array.from(row.children);
      const hasChildElements = rowChildren.length > 0;

      let startPathForRow = rowPath;
      let endPathForRow = rowPath;

      if (hasChildElements) {
        // ============================================================
        // ✅ CRITICAL: Determine which child elements to select
        // ============================================================
        let localStartChildIdx = 0;
        let localEndChildIdx = rowChildren.length - 1;

        // For the first row, use the start child index from the original selection
        if (isFirstRow) {
          localStartChildIdx = Math.min(
            globalStartChildIdx,
            rowChildren.length - 1,
          );
        }

        // For the last row, use the end child index from the original selection
        if (isLastRow) {
          localEndChildIdx = Math.min(
            globalEndChildIdx,
            rowChildren.length - 1,
          );
        }

        // For middle rows, select the entire row (we want to highlight the whole row)
        if (!isFirstRow && !isLastRow) {
          localStartChildIdx = 0;
          localEndChildIdx = rowChildren.length - 1;
        }

        // If it's a single row, use the full range
        if (isSingleRow) {
          localStartChildIdx = Math.min(
            globalStartChildIdx,
            rowChildren.length - 1,
          );
          localEndChildIdx = Math.min(
            globalEndChildIdx,
            rowChildren.length - 1,
          );
        }

        // Ensure start <= end
        if (localStartChildIdx > localEndChildIdx) {
          [localStartChildIdx, localEndChildIdx] = [
            localEndChildIdx,
            localStartChildIdx,
          ];
        }

        // ============================================================
        // ✅ Check if we should select the entire row or specific children
        // ============================================================
        const shouldSelectSeparator = includesSeparator;
        const shouldSelectValue = includesValue;
        const shouldSelectKey = includesKey;

        // Determine if we're selecting the separator or the value
        let selectSeparator = false;
        let selectValue = false;
        let selectKey = false;

        // Check what the user actually selected
        if (isFirstRow) {
          selectSeparator = true;
          selectValue = true;
          selectKey = false;
        } else if (isLastRow) {
          selectKey = true;
          selectSeparator = true;
          selectValue = false;
        }

        console.log(`[createDeepSelectors] 📍 Row ${i + 1} selection flags:`, {
          selectKey,
          selectSeparator,
          selectValue,
          localStartChildIdx,
          localEndChildIdx,
        });

        // ============================================================
        // ✅ Build the child paths based on what to select
        // ============================================================
        const childTag = rowChildren[0]?.tagName?.toLowerCase() || "div";

        if (selectKey && selectSeparator && !selectValue) {
          // Select key and separator
          let keyIdx = -1;
          let sepIdx = -1;
          for (let c = 0; c < rowChildren.length; c++) {
            const child = rowChildren[c];
            if (child.classList && child.classList.contains("key")) {
              keyIdx = c;
            }
            if (child.classList && child.classList.contains("separator")) {
              sepIdx = c;
            }
          }
          if (keyIdx !== -1 && sepIdx !== -1) {
            localStartChildIdx = Math.min(keyIdx, sepIdx);
            localEndChildIdx = Math.max(keyIdx, sepIdx);
          }
        } else if (selectSeparator && selectValue && !selectKey) {
          // Select separator and value
          let sepIdx = -1;
          let valIdx = -1;
          for (let c = 0; c < rowChildren.length; c++) {
            const child = rowChildren[c];
            if (child.classList && child.classList.contains("separator")) {
              sepIdx = c;
            }
            if (child.classList && child.classList.contains("value")) {
              valIdx = c;
            }
          }
          if (sepIdx !== -1 && valIdx !== -1) {
            localStartChildIdx = Math.min(sepIdx, valIdx);
            localEndChildIdx = Math.max(sepIdx, valIdx);
          }
        } else if (selectKey && selectSeparator && selectValue) {
          // Select all three - key, separator, value (full row)
          localStartChildIdx = 0;
          localEndChildIdx = rowChildren.length - 1;
        }

        console.log(
          `[createDeepSelectors] 📍 Row ${i + 1} final child range: ${localStartChildIdx} -> ${localEndChildIdx}`,
        );

        // ============================================================
        // ✅ Build the selector for this row
        // ============================================================
        if (
          localStartChildIdx === 0 &&
          localEndChildIdx === rowChildren.length - 1
        ) {
          // Full row
          startPathForRow = rowPath;
          endPathForRow = rowPath;
          console.log(`[createDeepSelectors] 📍 Row ${i + 1} FULL row`);
        } else {
          // Partial row - select specific children
          const startNth = localStartChildIdx + 1;
          const endNth = localEndChildIdx + 1;

          let startChildPath = "";
          let endChildPath = "";

          if (localStartChildIdx === localEndChildIdx) {
            // Single child
            startChildPath = `${rowPath} > ${childTag}:nth-child(${startNth})`;
            endChildPath = startChildPath;
          } else {
            // Multiple children
            startChildPath = `${rowPath} > ${childTag}:nth-child(${startNth})`;
            endChildPath = `${rowPath} > ${childTag}:nth-child(${endNth})`;
          }

          startPathForRow = startChildPath;
          endPathForRow = endChildPath;

          console.log(
            `[createDeepSelectors] 📍 Row ${i + 1} PARTIAL: ${startNth} -> ${endNth}`,
          );
        }

        // ============================================================
        // ✅ DEV-ONLY GUARD — 1G (final check)
        // INVARIANT: The final startPathForRow / endPathForRow must
        //            have a segment count that matches:
        //              canonicalRowPath (row-level)     →  N
        //              canonicalRowPath + 1 child leaf  →  N + 1
        //
        // Reason: catches any late manipulation (e.g. erroneous
        // child-suffix append) that would break resolution.
        //
        // Owned by: this function.
        // ============================================================
        if (process.env.NODE_ENV !== "production") {
          if (isInsideLoop && loopElement) {
            const canonicalRowPath = getRelativePath(row, loopElement);
            const canonicalSegs = canonicalRowPath.split(" > ").length;
            const producedStartSegs = startPathForRow.split(" > ").length;
            const producedEndSegs = endPathForRow.split(" > ").length;

            const startDelta = producedStartSegs - canonicalSegs;
            const endDelta = producedEndSegs - canonicalSegs;

            // Permitted deltas: 0 (full row) or 1 (partial row → child appended)
            if (startDelta < 0 || startDelta > 1) {
              console.warn(
                "[INVARIANT:1G] createDeepSelectors startPathForRow has unexpected segment delta.",
                {
                  rowIndex: i,
                  canonicalRowPath,
                  canonicalSegs,
                  producedStart: startPathForRow,
                  producedStartSegs,
                  startDelta,
                },
              );
            }
            if (endDelta < 0 || endDelta > 1) {
              console.warn(
                "[INVARIANT:1G] createDeepSelectors endPathForRow has unexpected segment delta.",
                {
                  rowIndex: i,
                  canonicalRowPath,
                  canonicalSegs,
                  producedEnd: endPathForRow,
                  producedEndSegs,
                  endDelta,
                },
              );
            }
          }
        }
      } else {
        console.log(
          `[createDeepSelectors] 📍 Row ${i + 1} has no children, using row-level path`,
        );
      }

      // Create the selector for this row
      const baseSelector = {
        start: startPathForRow,
        end: endPathForRow,
        ...(selector.loopSelector
          ? { loopSelector: selector.loopSelector }
          : {}),
      };

      selectors.push(baseSelector);
    }

    console.log(
      `[createDeepSelectors] ✅ Created ${selectors.length} deep selectors`,
    );
    return selectors;
  };

  // PreviewTester.js - Updated applyTransformation with multi-select styles support

  const applyTransformation = (
    operation,
    isStructural = false,
    isMedia = false,
    operationData = null,
  ) => {
    console.log("[MISSION CRITICAL applyTransformation] START");
    console.log("[MISSION CRITICAL applyTransformation] operation:", operation);
    console.log(
      "[MISSION CRITICAL applyTransformation] operationData:",
      operationData,
    );

    const currentText = immediateSelectionRef.current.text || selectedText;
    const currentSelector = immediateSelectionRef.current.selector;
    const currentContainer =
      immediateSelectionRef.current.container || selectedContainer;

    console.log(
      "[MISSION CRITICAL applyTransformation] currentText:",
      currentText,
    );
    console.log(
      "[MISSION CRITICAL applyTransformation] currentSelector:",
      currentSelector,
    );
    console.log(
      "[MISSION CRITICAL applyTransformation] currentContainer:",
      currentContainer,
    );

    if (!currentText && !isMedia) {
      console.log("[PreviewTester] ⚠️ No text selected");
      alert("Please select some text to transform.");
      return;
    }

    if (!renderedPreview || renderedPreview.length < 10) {
      console.warn("[PreviewTester] ⚠️ No valid preview content");
      alert("Please render the preview first.");
      return;
    }

    // ============================================================
    // MEDIA TRANSFORMATION
    // ============================================================
    if (isMedia) {
      console.log("[PreviewTester] 🖼️ ENTERING MEDIA TRANSFORMATION BLOCK");

      const imageUrls = extractImageUrls(currentText);
      if (imageUrls.length === 0) {
        alert("No image URLs found in the selected content.");
        return;
      }

      const imageWidth = operationData?.imageWidth || 250;
      const imageHeight = operationData?.imageHeight || 250;
      const preserveAspectRatio = operationData?.preserveAspectRatio !== false;

      console.log(
        `[PreviewTester] 🖼️ Image dimensions: ${imageWidth}×${imageHeight}, preserveRatio: ${preserveAspectRatio}`,
      );
      console.log(
        `[PreviewTester] 🖼️ Found ${imageUrls.length} image URLs in selection`,
      );

      // ✅ Get selector - supports both old and new formats
      const selector =
        currentSelector ||
        generatePositionSelector(currentText, currentContainer, null);

      const startPath = selector?.start?.elementPath || selector?.start || "";
      const endPath = selector?.end?.elementPath || selector?.end || "";
      const hasLoopSelector =
        selector && selector.loopSelector && selector.loopSelector.length > 0;

      console.log("[PreviewTester] 🖼️ Selector paths:", {
        startPath,
        endPath,
        hasLoopSelector,
      });

      let result = renderedPreview;
      let totalReplacedCount = 0;

      // ============================================================
      // ✅ WRAPPED LOOP → delegate to applyMediaToAllInLoop
      // ============================================================
      if (hasLoopSelector) {
        console.log(
          "[PreviewTester] 🖼️ Loop selector detected — delegating to applyMediaToAllInLoop",
        );

        const mediaResult = applyMediaToAllInLoop(
          selector,
          currentText,
          renderedPreview,
          currentContainer,
          {
            width: imageWidth,
            height: imageHeight,
            preserveAspectRatio,
          },
        );

        if (!mediaResult.success) {
          console.error(
            "[PreviewTester] ⚠️ applyMediaToAllInLoop failed:",
            mediaResult.error,
          );
          alert("No image URLs found in the selected content to transform.");
          return;
        }

        result = mediaResult.newPreview;
        totalReplacedCount += mediaResult.replacedCount;

        console.log(
          `[PreviewTester] 🖼️ Screen 0 - Replaced ${mediaResult.replacedCount} images`,
        );
      } else {
        // ============================================================
        // Non-loop media: apply directly.
        // ============================================================
        console.log(
          "[PreviewTester] 🖼️ No loop selector, applying single transformation",
        );

        const parser = new DOMParser();
        const doc = parser.parseFromString(renderedPreview, "text/html");

        let screens = doc.querySelectorAll(".item-ui-screen");

        if (screens.length === 0) {
          const contentWrapsList = doc.querySelectorAll(".contentWraps");
          if (contentWrapsList.length > 0) {
            console.log(
              "[PreviewTester] 🖼️ No item-ui-screen found, wrapping contentWraps",
            );
            const wrappedScreens = [];
            for (const wrap of contentWrapsList) {
              const wrapper = doc.createElement("div");
              wrapper.className = "item-ui-screen";
              wrapper.setAttribute("data-item-ui-index", "0");
              const parent = wrap.parentNode;
              parent.replaceChild(wrapper, wrap);
              wrapper.appendChild(wrap);
              wrappedScreens.push(wrapper);
            }
            screens = wrappedScreens;
          }
        }

        console.log(`[PreviewTester] 🖼️ Found ${screens.length} screens`);

        const createImageHtmlWithDimensions = (url, width, height) => {
          const cleanUrl = url.replace(/"/g, "&quot;");
          const objectFit = preserveAspectRatio ? "cover" : "fill";
          return `<div class="media-wrapper" style="display:inline-block;margin:4px 0;">
      <img 
        src="${cleanUrl}" 
        alt="Image" 
        class="preview-image rendered-image" 
        style="width:${width}px;height:${height}px;object-fit:${objectFit};border-radius:8px;border:1px solid #e2e8f0;max-width:100%;box-shadow:0 2px 4px rgba(0,0,0,0.1);" 
        loading="lazy" 
        onerror="this.style.display='none';this.parentElement.innerHTML='<div style=\\'color:#a0aec0;font-size:12px;\\'>🖼️ Image unavailable</div>';"
      />
    </div>`;
        };

        for (let screenIdx = 0; screenIdx < screens.length; screenIdx++) {
          const screen = screens[screenIdx];
          const screenIndex =
            screen.getAttribute("data-item-ui-index") || screenIdx;

          console.log(`[PreviewTester] 🖼️ Processing screen ${screenIndex}`);

          // ----------------------------------------------------------
          // ✅ TOLERANT resolution.
          //
          // The path was generated against the LIVE DOM (which has
          // React's `dangerouslySetInnerHTML` wrapper <div> between
          // `item-ui-screen` and `contentWraps`). `renderedPreview`
          // does NOT have that wrapper. So a 4-segment path like:
          //
          //   div:nth-child(1) > div:nth-child(1) > div:nth-child(2) > div:nth-child(3)
          //   (screen > wrapper > contentWraps > row(2) > value(3))
          //
          // must be tried with the first segment stripped:
          //
          //   div:nth-child(1) > div:nth-child(2) > div:nth-child(3)
          //   (screen > contentWraps > row(2) > value(3))
          //
          // Try in this order:
          //   1. screen + original
          //   2. contentWraps + original
          //   3. screen + stripped (one leading segment removed)
          //   4. contentWraps + stripped
          // ----------------------------------------------------------
          let containerForPath = screen;
          let targetElement = findElementByPath(screen, startPath);

          if (!targetElement) {
            const contentWraps = screen.querySelector(".contentWraps");
            if (contentWraps) {
              targetElement = findElementByPath(contentWraps, startPath);
              if (targetElement) containerForPath = contentWraps;
            }
          }

          if (!targetElement && startPath && startPath.includes(" > ")) {
            const stripped = startPath.split(" > ").slice(1).join(" > ");

            targetElement = findElementByPath(screen, stripped);
            if (targetElement) {
              containerForPath = screen;
            } else {
              const contentWraps = screen.querySelector(".contentWraps");
              if (contentWraps) {
                targetElement = findElementByPath(contentWraps, stripped);
                if (targetElement) containerForPath = contentWraps;
              }
            }
          }

          if (!targetElement) {
            console.log(
              `[PreviewTester] 🖼️ ⚠️ No target element found in screen ${screenIndex}`,
            );
            continue;
          }

          let imageUrl = null;
          let elementToReplace = null;

          if (targetElement.getAttribute("data-image-url")) {
            imageUrl = targetElement.getAttribute("data-image-url");
            elementToReplace = targetElement;
          } else if (
            targetElement.classList?.contains("value") &&
            targetElement.classList?.contains("image-url")
          ) {
            imageUrl = targetElement.textContent?.trim() || "";
            elementToReplace = targetElement;
          } else {
            const valueSpan = targetElement.querySelector?.(".value.image-url");
            if (valueSpan) {
              imageUrl = valueSpan.textContent?.trim() || "";
              elementToReplace = valueSpan;
            } else {
              const dataImageElement =
                targetElement.querySelector?.("[data-image-url]");
              if (dataImageElement) {
                imageUrl =
                  dataImageElement.getAttribute("data-image-url") ||
                  dataImageElement.textContent?.trim() ||
                  "";
                elementToReplace = dataImageElement;
              }
            }
          }

          if (!elementToReplace || !imageUrl) {
            console.log(
              `[PreviewTester] 🖼️ ⚠️ No image URL found in screen ${screenIndex}`,
            );
            continue;
          }

          const imgHtml = createImageHtmlWithDimensions(
            imageUrl,
            imageWidth,
            imageHeight,
          );
          const tempDiv = doc.createElement("div");
          tempDiv.innerHTML = imgHtml;
          const newElement = tempDiv.firstChild;
          if (newElement) {
            elementToReplace.parentNode.replaceChild(
              newElement,
              elementToReplace,
            );
            totalReplacedCount++;
            console.log(
              `[PreviewTester] 🖼️ Replaced image in screen ${screenIndex} (${imageWidth}×${imageHeight})`,
            );
          }
        }

        let serialized = "";
        for (const child of doc.body.children) {
          serialized += child.outerHTML;
        }
        result = serialized;
      }

      if (totalReplacedCount === 0) {
        console.log("[PreviewTester] ⚠️ No images were replaced");
        alert("No image URLs found in the selected content to transform.");
        return;
      }

      console.log(
        `[PreviewTester] ✅ Media transformation successful, replaced ${totalReplacedCount} images (${imageWidth}×${imageHeight})`,
      );

      // ============================================================
      // ✅ Create minimal media step using schema helper
      // ============================================================
      let selectorsToSave = [];
      if (currentSelector) {
        if (Array.isArray(currentSelector)) {
          selectorsToSave = currentSelector.map((s) => ({
            start: s.start?.elementPath || s.start || "",
            end: s.end?.elementPath || s.end || "",
            ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
          }));
        } else {
          selectorsToSave = [
            {
              start:
                currentSelector.start?.elementPath ||
                currentSelector.start ||
                "",
              end:
                currentSelector.end?.elementPath || currentSelector.end || "",
              ...(currentSelector.loopSelector
                ? { loopSelector: currentSelector.loopSelector }
                : {}),
            },
          ];
        }
      } else {
        const singleSelector = generatePositionSelector(
          currentText,
          currentContainer,
          null,
        );
        if (singleSelector) {
          selectorsToSave = [
            {
              start:
                singleSelector.start?.elementPath || singleSelector.start || "",
              end: singleSelector.end?.elementPath || singleSelector.end || "",
              ...(singleSelector.loopSelector
                ? { loopSelector: singleSelector.loopSelector }
                : {}),
            },
          ];
        }
      }

      const step = createMediaStep(
        [],
        selectorsToSave,
        {
          width: imageWidth,
          height: imageHeight,
          preserveAspectRatio: preserveAspectRatio,
        },
        `media_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      );

      step.data = {
        imageUrls,
        count: imageUrls.length,
        replacedCount: totalReplacedCount,
        width: imageWidth,
        height: imageHeight,
        preserveAspectRatio: preserveAspectRatio,
      };

      console.log("[PreviewTester] 🖼️ Created minimal media step:", step);

      setTransformationRules((prev) => [...prev, step]);
      setRenderedPreview(result);
      setTransformedPreview(result);

      handleCloseTransformationPanel();
      console.log("[PreviewTester] 🔧 END applyTransformation (media)");
      return;
    }

    // ============================================================
    // STYLE & STRUCTURAL TRANSFORMATIONS
    // ============================================================
    console.log("[PreviewTester] 🔍 Checking for HTML tokens");
    const hasHtmlTokens =
      renderedPreview.includes("<div") || renderedPreview.includes("<div");
    console.log("[PreviewTester] 🔍 hasHtmlTokens:", hasHtmlTokens);

    if (!hasHtmlTokens) {
      console.warn("[PreviewTester] ⚠️ Preview does not contain HTML tokens");
      alert("Please render the preview with a valid template first.");
      return;
    }

    // ============================================================
    // ✅ Extract operations and style_meta for multi-select styles
    // ============================================================
    let operations = [];
    let styleMeta = { full_row: false, alignment: "left" };

    if (!isStructural && !isMedia) {
      if (
        operationData?.operations &&
        Array.isArray(operationData.operations) &&
        operationData.operations.length > 0
      ) {
        operations = operationData.operations;
      } else if (operation) {
        operations = [operation];
      } else {
        operations = ["highlight"];
      }

      if (operationData?.style_meta) {
        styleMeta = operationData.style_meta;
      }

      console.log("[PreviewTester] 🎨 Operations to apply:", operations);
      console.log("[PreviewTester] 🎨 Style meta:", styleMeta);
    } else if (isStructural) {
      operations = [operation || "table"];
    }

    let selectorsToPass = [];

    if (!isStructural && !isMedia) {
      console.log(
        "[PreviewTester] 🎨 Using stored selector for style transformation",
      );

      if (currentSelector) {
        const isLoopSelector =
          currentSelector.loopSelector &&
          currentSelector.loopSelector.length > 0;
        let loopElement = null;
        if (isLoopSelector) {
          loopElement = currentContainer.querySelector(".for-loop-started");
        }

        const metadata = currentSelector._metadata || {};
        const startPath =
          currentSelector.start?.elementPath || currentSelector.start || "";
        const endPath =
          currentSelector.end?.elementPath || currentSelector.end || "";

        const isMultiRow =
          metadata.isMultiRowSelection === true ||
          metadata.isMultiLine === true ||
          (metadata.startRowIndex !== undefined &&
            metadata.endRowIndex !== undefined &&
            metadata.startRowIndex !== metadata.endRowIndex) ||
          (startPath && endPath && startPath !== endPath);

        if (isMultiRow && startPath && endPath) {
          const normalizedSelector = {
            start: { elementPath: startPath },
            end: { elementPath: endPath },
            loopSelector: currentSelector.loopSelector,
            _metadata: {
              startRowIndex:
                metadata.startRowIndex !== undefined
                  ? metadata.startRowIndex
                  : 0,
              endRowIndex:
                metadata.endRowIndex !== undefined ? metadata.endRowIndex : 0,
              isMultiRowSelection: true,
              isMultiLine: metadata.isMultiLine || false,
              selectedTextPreview: metadata.selectedTextPreview || "",
              isFullRow: metadata.isFullRow || false,
              isInsideLoop: metadata.isInsideLoop || false,
              loopVar: metadata.loopVar || null,
              loopSelector: metadata.loopSelector || null,
              relativePath: metadata.relativePath || null,
            },
          };

          selectorsToPass = createDeepSelectors(
            normalizedSelector,
            currentContainer,
            isLoopSelector,
            loopElement,
          );
          console.log(
            `[PreviewTester] ✅ Created ${selectorsToPass.length} deep selectors`,
          );
        }

        if (selectorsToPass.length === 0) {
          selectorsToPass = [
            {
              start: startPath,
              end: endPath,
              ...(currentSelector.loopSelector
                ? { loopSelector: currentSelector.loopSelector }
                : {}),
            },
          ];
        }
      } else {
        console.warn(
          "[PreviewTester] ⚠️ No stored selector available, trying to generate",
        );
        const singleSelector = generatePositionSelector(
          currentText,
          currentContainer,
          selectedRange,
        );
        if (singleSelector) {
          const startPath =
            singleSelector.start?.elementPath || singleSelector.start || "";
          const endPath =
            singleSelector.end?.elementPath || singleSelector.end || "";
          if (startPath && endPath) {
            selectorsToPass = [
              {
                start: startPath,
                end: endPath,
                ...(singleSelector.loopSelector
                  ? { loopSelector: singleSelector.loopSelector }
                  : {}),
              },
            ];
            console.log(
              "[PreviewTester] ✅ Generated single selector from text",
            );
          }
        }
      }
    } else {
      console.log(
        "[PreviewTester] 📐 Using stored selector for structural transformation",
      );

      if (currentSelector) {
        const startPath =
          currentSelector.start?.elementPath || currentSelector.start || "";
        const endPath =
          currentSelector.end?.elementPath || currentSelector.end || "";
        selectorsToPass = [
          {
            start: startPath,
            end: endPath,
            ...(currentSelector.loopSelector
              ? { loopSelector: currentSelector.loopSelector }
              : {}),
          },
        ];
      } else {
        const singleSelector = generatePositionSelector(
          currentText,
          currentContainer,
          selectedRange,
        );
        if (singleSelector) {
          const startPath =
            singleSelector.start?.elementPath || singleSelector.start || "";
          const endPath =
            singleSelector.end?.elementPath || singleSelector.end || "";
          if (startPath && endPath) {
            selectorsToPass = [
              {
                start: startPath,
                end: endPath,
                ...(singleSelector.loopSelector
                  ? { loopSelector: singleSelector.loopSelector }
                  : {}),
              },
            ];
          }
        }
      }
    }

    if (selectorsToPass.length === 0) {
      console.error("[PreviewTester] ❌ No valid selectors available");
      alert(
        "Could not find a valid selector for the selected text. Please reselect the text.",
      );
      return;
    }

    // ✅ Clean selectors to the format expected by applyTransformationLogic
    const cleanedSelectors = selectorsToPass.map((s) => {
      if (s.start && typeof s.start === "object" && s.start.elementPath) {
        return s;
      }
      return {
        start: { elementPath: s.start || "", nodeType: 1 },
        end: { elementPath: s.end || "", nodeType: 1 },
        ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
      };
    });

    if (cleanedSelectors.length > 1) {
      console.log(
        `[PreviewTester] 📊 Multi-row selection detected: ${cleanedSelectors.length} rows`,
      );
      cleanedSelectors.forEach((s, idx) => {
        console.log(`  [${idx}] start: ${s.start?.elementPath}`);
        console.log(`  [${idx}] end: ${s.end?.elementPath}`);
        if (s.loopSelector) {
          console.log(`  [${idx}] loopSelector: ${s.loopSelector}`);
        }
      });
    } else {
      console.log(`[PreviewTester] 📊 Single row selection: 1 selector`);
      if (cleanedSelectors[0]?.loopSelector) {
        console.log(
          `[PreviewTester] 📊 Loop selector: ${cleanedSelectors[0].loopSelector}`,
        );
      }
    }

    console.log(
      `[PreviewTester] 📊 Passing ${cleanedSelectors.length} selectors to applyTransformationLogic`,
    );

    // ✅ Get the current scrollMode from ref (or fallback to state)
    const currentScrollMode = scrollModeRef.current || scrollMode;

    // ============================================================
    // ✅ Pass operations and style_meta to applyTransformationLogic
    // ============================================================
    const result = applyTransformationLogic(
      currentText,
      operations[0] || operation || "highlight",
      isStructural,
      renderedPreview,
      template,
      parsedSample,
      currentContainer,
      null,
      transformationRules,
      isSampleArray,
      dataType,
      arrayLayoutDirection,
      layoutDirection,
      {
        selectors: cleanedSelectors,
        operations: operations,
        style_meta: styleMeta,
        isFullRow: styleMeta.full_row || false,
        alignment: styleMeta.alignment || "left",
        scrollMode: currentScrollMode,
      },
    );

    console.log("[PreviewTester] 📐 applyTransformationLogic result:", result);

    if (!result.success) {
      console.error("[PreviewTester] ❌ Transformation failed:", result.error);
      alert(result.error);
      return;
    }

    console.log("[PreviewTester] ✅ Transformation successful");

    if (result.newPreview) {
      console.log(
        "[PreviewTester] 📊 result.newPreview length:",
        result.newPreview.length,
      );
      setRenderedPreview(result.newPreview);
      setTransformedPreview(result.newPreview);
    }

    if (result.step) {
      console.log("[PreviewTester] 📋 step:", result.step);
      console.log(
        "[PreviewTester] 📋 step.selectors count:",
        result.step.selectors?.length || 0,
      );

      // ✅ Ensure step is in new format
      const minimalStep = { ...result.step };

      // Remove old format flags if present
      delete minimalStep.isStructural;
      delete minimalStep.isMedia;
      delete minimalStep.isStructuralMultiNode;
      delete minimalStep.isMultiRowSelection;
      delete minimalStep.rowCount;
      delete minimalStep.isButtonTransformation;
      delete minimalStep.isButtonGroupTransformation;
      delete minimalStep.originalText;
      delete minimalStep.transformedText;
      delete minimalStep.selectorType;
      delete minimalStep.applied;
      delete minimalStep._metadata;

      // ✅ If it's a style step, ensure operations and style_meta are set
      if (minimalStep.type === "style") {
        if (!minimalStep.operations || minimalStep.operations.length === 0) {
          minimalStep.operations =
            operations.length > 0 ? operations : [operation || "highlight"];
        }
        if (!minimalStep.style_meta) {
          minimalStep.style_meta = styleMeta;
        }
        if (!minimalStep.operation) {
          minimalStep.operation = minimalStep.operations[0] || "highlight";
        }
      }

      // ✅ If it's a button step, ensure buttonText is set
      if (minimalStep.type === "button" && !minimalStep.buttonText) {
        minimalStep.buttonText = currentText || "Button";
      }

      // ✅ If it's a button group step, ensure groupName and buttons are set
      if (minimalStep.type === "button-group") {
        if (!minimalStep.groupName) {
          minimalStep.groupName = "Button Group";
        }
        if (!minimalStep.buttons || minimalStep.buttons.length === 0) {
          minimalStep.buttons = ["Button 1", "Button 2"];
        }
        minimalStep.buttons = minimalStep.buttons.map((b) =>
          typeof b === "string" ? b : b.text || String(b),
        );
      }

      // ✅ Ensure selectors are in new format
      if (minimalStep.selectors) {
        minimalStep.selectors = minimalStep.selectors.map((s) => ({
          start: s.start?.elementPath || s.start || "",
          end: s.end?.elementPath || s.end || "",
          ...(s.loopSelector ? { loopSelector: s.loopSelector } : {}),
        }));
      }

      // ✅ Ensure timestamp is set
      if (!minimalStep.timestamp) {
        minimalStep.timestamp = new Date().toISOString();
      }

      setTransformationRules((prev) => {
        const newRules = [...prev, minimalStep];
        console.log(
          "[PreviewTester] 📋 Updated transformationRules:",
          newRules,
        );
        return newRules;
      });
      console.log(
        "[PreviewTester] 📋 Saved transformation step:",
        minimalStep.id,
      );
    }

    handleCloseTransformationPanel();
    console.log("[PreviewTester] 🔧 END applyTransformation");
  };

  // ============================================================
  // HANDLE TEMPLATE UPDATE - ALWAYS USE parseTemplateForEdit
  // ============================================================
  const handleTemplateUpdate = (newTemplate) => {
    console.log(
      "================================================================",
    );
    console.log("[🔘 BUTTON FLOW] handleTemplateUpdate START");

    let templateString = newTemplate;
    let existingLinesForParse = null;

    if (Array.isArray(newTemplate)) {
      templateString = newTemplate.map((line) => line.content).join("\n");
      existingLinesForParse = newTemplate;
    } else if (typeof newTemplate !== "string") {
      templateString = String(newTemplate || "");
    }

    setTemplate(templateString);

    const linesToUse = existingLinesForParse || templateLines;

    if (parsedSample) {
      // ✅ ALWAYS use parseTemplateForEdit for editor updates
      console.log(
        "[🔘 BUTTON FLOW] Calling parseTemplateForEdit (editor update)",
      );
      const editLines = parseTemplateForEdit(
        templateString,
        parsedSample,
        isCase2,
        linesToUse,
      );
      setTemplateLines(editLines);
    }

    console.log("[🔘 BUTTON FLOW] handleTemplateUpdate END");
    console.log(
      "================================================================",
    );
  };

  // ============================================================
  // WATCH FOR TEMPLATE CHANGES
  // ============================================================
  // In the useEffect that watches template
  useEffect(() => {
    // ✅ Skip re-rendering if transformations are being applied
    if (isApplyingTransformations.current) {
      console.log(
        "[PreviewTester] ⏭️ Skipping render - transformations being applied",
      );
      return;
    }

    if (template && sampleResponse && sampleResponse.trim()) {
      console.log("[PreviewTester] 🔄 Template changed, re-rendering preview");
      renderPreview();
    }
  }, [template]);

  const findFirstActive = (tree) => {
    for (const node of tree) {
      if (node.values && node.values.some((v) => v.isActive)) return node;
      if (node.children && node.children.length > 0) {
        const childActive = findFirstActive(node.children);
        if (childActive) return childActive;
      }
    }
    return null;
  };

  const findActivePath = (tree, currentData) => {
    const evaluateCondition = (condition, data) => {
      try {
        if (condition.includes(" && ") || condition.includes(" and ")) {
          const parts = condition.includes(" && ")
            ? condition.split(" && ")
            : condition.split(" and ");
          return parts.every((p) => evaluateCondition(p.trim(), data));
        }
        if (condition.includes(" || ") || condition.includes(" or ")) {
          const parts = condition.includes(" || ")
            ? condition.split(" || ")
            : condition.split(" or ");
          return parts.some((p) => evaluateCondition(p.trim(), data));
        }

        const operators = [">=", "<=", ">", "<", "===", "==", "!==", "!="];
        let field = null,
          operator = null,
          compareValue = null;

        for (const op of operators) {
          if (condition.includes(op)) {
            const parts = condition.split(op);
            field = parts[0].trim();
            compareValue = parts[1].trim();
            operator = op;
            break;
          }
        }

        if (!field || !operator) {
          if (field && field.startsWith("response_data."))
            field = field.substring("response_data.".length);
          const fieldParts = field ? field.split(".") : [];
          let value = data;
          for (const part of fieldParts) {
            if (value && typeof value === "object" && part in value)
              value = value[part];
            else {
              value = undefined;
              break;
            }
          }
          return !!value;
        }

        if (field.startsWith("response_data."))
          field = field.substring("response_data.".length);

        const fieldParts = field.split(".");
        let value = data;
        for (const part of fieldParts) {
          if (value && typeof value === "object" && part in value)
            value = value[part];
          else {
            value = undefined;
            break;
          }
        }

        if (value === undefined || value === null) return false;

        const compareVal = compareValue.replace(/^['"]|['"]$/g, "");
        const isNumber = !isNaN(compareVal);

        if (isNumber) {
          const numCompare = parseInt(compareVal);
          const numValue = parseInt(value);
          switch (operator) {
            case ">":
              return numValue > numCompare;
            case ">=":
              return numValue >= numCompare;
            case "<":
              return numValue < numCompare;
            case "<=":
              return numValue <= numCompare;
            case "==":
            case "===":
              return numValue === numCompare;
            case "!=":
            case "!==":
              return numValue !== numCompare;
            default:
              return false;
          }
        } else {
          const strValue = String(value);
          switch (operator) {
            case "==":
            case "===":
              return strValue === compareVal;
            case "!=":
            case "!==":
              return strValue !== compareVal;
            default:
              return false;
          }
        }
      } catch (err) {
        return false;
      }
    };

    const traverse = (node, path) => {
      const newPath = [...path, node.id];

      if (node.isElseBlock) {
        const isActive = evaluateCondition(node.condition, currentData);
        return isActive ? newPath : null;
      }

      const isActive = evaluateCondition(node.condition, currentData);
      if (isActive) {
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            const childPath = traverse(child, newPath);
            if (childPath) return childPath;
          }
        }
        return newPath;
      }
      return null;
    };

    for (const scenario of tree) {
      const path = traverse(scenario, []);
      if (path) return path;
    }

    for (const scenario of tree) {
      if (scenario.isElseBlock) {
        if (evaluateCondition(scenario.condition, currentData))
          return [scenario.id];
      }
      if (scenario.children) {
        for (const child of scenario.children) {
          if (
            child.isElseBlock &&
            evaluateCondition(child.condition, currentData)
          ) {
            return [scenario.id, child.id];
          }
        }
      }
    }

    if (tree.length > 0) return [tree[0].id];
    return null;
  };

  const findPathToNode = (tree, targetId) => {
    const findExactPath = (node, targetId, currentPath = []) => {
      const path = [...currentPath, node];
      if (node.id === targetId) return path;
      if (node.children) {
        for (const child of node.children) {
          const result = findExactPath(child, targetId, path);
          if (result) return result;
        }
      }
      return null;
    };

    for (const scenario of tree) {
      const result = findExactPath(scenario, targetId);
      if (result) return result;
    }

    const findPartialMatch = (node, targetId, currentPath = []) => {
      const path = [...currentPath, node];
      if (targetId.includes(node.id) || node.id.includes(targetId)) return path;
      if (node.children) {
        for (const child of node.children) {
          const result = findPartialMatch(child, targetId, path);
          if (result) return result;
        }
      }
      return null;
    };

    for (const scenario of tree) {
      const result = findPartialMatch(scenario, targetId);
      if (result) return result;
    }
    return null;
  };

  const getPathUpdates = (tree, targetId, currentData) => {
    const path = findPathToNode(tree, targetId);
    if (!path) return null;

    const updates = [];
    let updatedData = { ...currentData };

    for (let i = 0; i < path.length; i++) {
      const node = path[i];
      if (node.values && node.values.length > 0) {
        const valueToUse = node.values[0];
        if (valueToUse && valueToUse.dataUpdate) {
          updates.push(valueToUse.dataUpdate);
          updatedData = deepMerge(updatedData, valueToUse.dataUpdate);
        }
      }
    }

    return { updates, updatedData };
  };

  const isInActivePath = (scenarioId) => {
    return activePathIds.includes(scenarioId);
  };

  const handleScenarioClick = (dataUpdate, scenarioId, value) => {
    try {
      const currentData = JSON.parse(sampleResponse);

      let dataForUpdate = currentData;
      if (Array.isArray(currentData)) {
        if (currentData.length === 0) return;
        dataForUpdate = currentData[0];
      }

      const pathResult = getPathUpdates(
        scenarioTree,
        scenarioId,
        dataForUpdate,
      );

      let updatedData;
      if (pathResult) {
        updatedData = pathResult.updatedData;
      } else {
        updatedData = deepMerge(dataForUpdate, dataUpdate);
      }

      let newSampleResponse;
      if (Array.isArray(currentData)) {
        const newArray = [...currentData];
        newArray[0] = updatedData;
        newSampleResponse = JSON.stringify(newArray, null, 2);
      } else {
        newSampleResponse = JSON.stringify(updatedData, null, 2);
      }

      setSampleResponse(newSampleResponse);
      updatePreviewAndTree(updatedData);
    } catch (err) {
      console.log("[HUNTING_CLICK_COND_PATH] ❌ error:", err);
    }
  };

  const updatePreviewAndTree = (updatedData) => {
    try {
      const renderContext = { response_data: updatedData };
      const rendered = nunjucks.renderString(template, renderContext);

      let finalRendered = rendered;
      for (const rule of transformationRules) {
        if (rule.applied) {
          finalRendered = finalRendered.replace(
            rule.originalText,
            rule.transformedText,
          );
        }
      }

      setRenderedPreview(finalRendered);

      setParsedSample(updatedData);
      const flattened = flattenObject({ response_data: updatedData });
      setFlattenedFields(flattened);

      const isArrayResult = isArrayData(finalRendered);
      const parsedDataResult = safeJSONParse(finalRendered);
      setParsedData(parsedDataResult);

      if (!isSampleArray) {
        if (isArrayResult) {
          setDataType("array");
        } else if (
          parsedDataResult &&
          typeof parsedDataResult === "object" &&
          !Array.isArray(parsedDataResult)
        ) {
          setDataType("object");
        } else {
          setDataType("text");
        }
      }

      const conditionBlocks = extractNestedConditions(template);
      if (conditionBlocks.length > 0) {
        const tree = buildScenarioTree(conditionBlocks, updatedData);
        setScenarioTree(tree);

        const newActivePath = findActivePath(tree, updatedData);
        if (newActivePath && newActivePath.length > 0) {
          setActivePathIds(newActivePath);
        }
      }
    } catch (renderErr) {
      console.log("[HUNTING_CLICK_COND_PATH] ❌ Re-render error:", renderErr);
    }
  };

  const handleRender = () => {
    setActivePathIds([]);
    renderPreview();
  };

  const insertField = (fieldPath) => {
    const textarea = document.getElementById("template-input");
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = template.substring(0, start);
    const after = template.substring(end);
    const newTemplate = before + `{{ ${fieldPath} }}` + after;
    setTemplate(newTemplate);
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + `{{ ${fieldPath} }}`.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const buildScenarioTree = (
    conditionBlocks,
    currentData,
    parentId = null,
    parentIsElse = false,
  ) => {
    const scenarios = [];

    conditionBlocks.forEach((block, index) => {
      if (block.isElseBlock || block.type === "elseblock") {
        const elseKey = block.condition.replace(/[^a-zA-Z0-9]/g, "_");
        const elseId = parentId
          ? `${parentId}_else_${elseKey}`
          : `root_else_${elseKey}`;

        const elseScenario = {
          id: elseId,
          condition: block.condition,
          allConditions: [block.condition],
          conditions: block.conditions || [],
          values: generateTestValues(block.condition, currentData, true),
          children: [],
          isActive: false,
          isNested: false,
          depth: block.depth || 0,
          hasElse: false,
          isElseBlock: true,
          isInsideElse: true,
          elifConditions: block.elifConditions || [],
          isElif: false,
        };

        if (block.children && block.children.length > 0) {
          const childScenarios = buildScenarioTree(
            block.children,
            currentData,
            elseId,
            true,
          );
          elseScenario.children = childScenarios;
        }

        scenarios.push(elseScenario);
        return;
      }

      if (block.type === "elseblock") return;

      const mainCondition = block.condition;
      const isNested = block.depth > 0;
      const hasElse = block.hasElse;
      const isElif = block.isElif || false;
      const isInsideElse = parentIsElse || false;

      const conditionKey = mainCondition.replace(/[^a-zA-Z0-9]/g, "_");
      let blockId;

      if (isElif) {
        blockId = parentId
          ? `${parentId}_elif_${conditionKey}`
          : `elif_${conditionKey}`;
      } else {
        blockId = parentId
          ? `${parentId}_${conditionKey}`
          : `root_${conditionKey}`;
      }

      const scenario = {
        id: blockId,
        condition: mainCondition,
        allConditions: block.allConditions || [mainCondition],
        conditions: block.conditions || [],
        values: generateTestValues(mainCondition, currentData, false),
        children: [],
        isActive: false,
        isNested: isNested,
        depth: block.depth || 0,
        hasElse: hasElse,
        isElseBlock: false,
        isInsideElse: isInsideElse,
        elifConditions: block.elifConditions || [],
        isElif: isElif,
      };

      if (block.children && block.children.length > 0) {
        const childScenarios = buildScenarioTree(
          block.children,
          currentData,
          blockId,
          isInsideElse,
        );
        scenario.children = childScenarios;
      }

      if (block.elseBlock) {
        const elseBlock = block.elseBlock;
        const elseKey = elseBlock.condition.replace(/[^a-zA-Z0-9]/g, "_");
        const elseId = `${blockId}_else_${elseKey}`;

        const elseScenario = {
          id: elseId,
          condition: elseBlock.condition,
          allConditions: [elseBlock.condition],
          conditions: elseBlock.conditions || [],
          values: generateTestValues(elseBlock.condition, currentData, true),
          children: [],
          isActive: false,
          isNested: false,
          depth: block.depth || 0,
          hasElse: false,
          isElseBlock: true,
          isInsideElse: true,
          elifConditions: elseBlock.elifConditions || [],
          isElif: false,
        };

        if (elseBlock.children && elseBlock.children.length > 0) {
          const childScenarios = buildScenarioTree(
            elseBlock.children,
            currentData,
            elseId,
            true,
          );
          elseScenario.children = childScenarios;
        }

        if (block.depth > 0) {
          scenario.children.push(elseScenario);
        }
      }

      if (!isElif) {
        scenarios.push(scenario);
      }

      if (block.elifConditions && block.elifConditions.length > 0 && !isElif) {
        block.elifConditions.forEach((elifCond) => {
          const elifKey = elifCond.replace(/[^a-zA-Z0-9]/g, "_");
          const elifId = parentId
            ? `${parentId}_elif_${elifKey}`
            : `elif_${elifKey}`;

          const elifScenario = {
            id: elifId,
            condition: elifCond,
            allConditions: [elifCond],
            conditions: [{ type: "elif", condition: elifCond }],
            values: generateTestValues(elifCond, currentData, false),
            children: [],
            isActive: false,
            isNested: isNested,
            depth: block.depth || 0,
            hasElse: false,
            isElseBlock: false,
            isInsideElse: isInsideElse,
            isElif: true,
            parentCondition: mainCondition,
          };
          scenarios.push(elifScenario);
        });
      }

      if (isElif) {
        scenarios.push(scenario);
      }
    });

    return scenarios;
  };

  const getScrollStyle = () => {
    const baseStyle = {
      flex: "1",
      backgroundColor: "#f7fafc",
      borderRadius: "8px",
      border: "2px solid #e2e8f0",
      padding: "16px",
      minHeight: "300px",
      maxHeight: "100%",
      overflow: "auto",
    };

    // ✅ Use scrollModeRef.current for the latest value, fallback to scrollMode
    const currentScrollMode = scrollModeRef.current || scrollMode;

    switch (currentScrollMode) {
      case "horizontal":
        return { ...baseStyle, overflowX: "auto", overflowY: "hidden" };
      case "both":
        return { ...baseStyle, overflowX: "auto", overflowY: "auto" };
      default:
        return { ...baseStyle, overflowX: "hidden", overflowY: "auto" };
    }
  };

  const getPreviewContent = () => {
    console.log(
      "[PROPER INITIAL STRUCTURE RENDERING] getPreviewContent called",
    );

    if (error) {
      return (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fed7d7",
            color: "#c53030",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      );
    }

    if (!renderedPreview) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#a0aec0",
            fontSize: "14px",
          }}
        >
          No preview to display
        </div>
      );
    }

    const screens = [];
    const openTag = '<div class="item-ui-screen"';
    let searchIndex = 0;

    while (true) {
      const startIdx = renderedPreview.indexOf(openTag, searchIndex);
      if (startIdx === -1) break;

      const openTagEnd = renderedPreview.indexOf(">", startIdx);
      if (openTagEnd === -1) break;

      let depth = 0;
      let pos = openTagEnd + 1;
      let found = false;

      while (pos < renderedPreview.length) {
        const nextOpen = renderedPreview.indexOf("<div", pos);
        const nextClose = renderedPreview.indexOf("</div>", pos);

        if (nextClose === -1) break;

        if (nextOpen !== -1 && nextOpen < nextClose) {
          depth++;
          pos = nextOpen + 4;
        } else {
          if (depth === 0) {
            const endIdx = nextClose + 6;
            const contentStart = openTagEnd + 1;
            const content = renderedPreview.substring(contentStart, endIdx - 6);
            const indexMatch = renderedPreview
              .substring(startIdx, openTagEnd)
              .match(/data-item-ui-index="(\d+)"/);
            const index = indexMatch ? parseInt(indexMatch[1]) : screens.length;
            screens.push({ index, content, start: startIdx, end: endIdx });
            found = true;
            searchIndex = endIdx;
            break;
          } else {
            depth--;
            pos = nextClose + 6;
          }
        }
      }

      if (!found) break;
    }

    const screenCount = screens.length;
    console.log(`[PreviewTester] 📊 Found ${screenCount} item-ui-screen divs`);

    if (screenCount > 0) {
      const isHorizontal = arrayLayoutDirection === "horizontal";
      const isSingle = screenCount === 1;

      return (
        <div
          className={isHorizontal ? styles.horizontals : styles.verticals}
        // style={{
        //   display: "flex",
        //   flexDirection: isHorizontal ? "row" : "column",
        //   flexWrap: isHorizontal ? "wrap" : "nowrap",
        //   gap: isHorizontal ? "14px" : "4px",
        //   width: "100%",
        //   padding: "4px",
        //   height: "auto",
        //   alignItems: isHorizontal ? "stretch" : "flex-start",
        // }}
        >
          {screens.map(({ index, content }) => {
            const screenStyle = {
              marginBottom: isSingle ? "0" : isHorizontal ? "0" : "12px",
              userSelect: isSelectingMode ? "text" : "auto",
              flex: isHorizontal ? "0 0 calc(33.33% - 10px)" : "0 0 auto",
              maxWidth: isHorizontal ? "calc(33.33% - 10px)" : "100%",
              minWidth: isHorizontal ? "250px" : "100%",
              height: isHorizontal ? "auto" : "auto",
              alignSelf: isHorizontal ? "stretch" : "flex-start",
              borderBottom: isHorizontal ? "none" : "1px solid #e2e8f0",
              paddingBottom: isHorizontal ? "0" : "8px",
            };

            return (
              <div
                key={index}
                className="item-ui-screen"
                data-item-ui-index={index}
                style={screenStyle}
              >
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </div>
            );
          })}
        </div>
      );
    }

    console.log(
      "[PreviewTester] 📊 No item-ui-screen divs found - wrapping as single screen",
    );

    const wrappedContent = `<div style="white-space:pre-wrap;word-break:break-word;">${renderedPreview}</div>`;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          width: "100%",
          padding: "4px",
          height: "auto",
          alignItems: "flex-start",
        }}
      >
        <div
          className="item-ui-screen"
          data-item-ui-index="0"
          style={{
            marginBottom: "0",
            userSelect: isSelectingMode ? "text" : "auto",
            flex: "0 0 auto",
            maxWidth: "100%",
            minWidth: "100%",
            height: "auto",
            alignSelf: "flex-start",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: wrappedContent }} />
        </div>
      </div>
    );
  };

  const renderScenarioTree = (
    scenarios,
    level = 0,
    isInElse = false,
    forceCollapse = false,
  ) => {
    if (!scenarios || scenarios.length === 0) return null;

    return (
      <div
        style={{ marginLeft: level * 20 }}
        className={`scenario-level level-${level}`}
      >
        {scenarios.map((scenario, index) => {
          const isActive = isInActivePath(scenario.id);
          const hasActive =
            scenario.values.some((v) => v.isActive) ||
            hasActiveChild(scenario.children);
          const isElse = scenario.isElseBlock || false;
          const isElif = scenario.isElif || false;

          return (
            <ScenarioBranch
              key={scenario.id || index}
              scenario={scenario}
              index={index}
              hasActive={hasActive}
              isElse={isElse}
              isElif={isElif}
              isActivePath={isActive}
              handleScenarioClick={(dataUpdate) =>
                handleScenarioClick(dataUpdate, scenario.id, scenario.values[0])
              }
              renderScenarioTree={renderScenarioTree}
              level={level}
              forceCollapse={forceCollapse}
            />
          );
        })}
      </div>
    );
  };

  const statusDropdownStyles = {
    container: {
      position: "relative",
      display: "inline-block",
      minWidth: "200px",
    },
    button: {
      padding: "8px 16px",
      backgroundColor: "#4a6cf7",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    dropdown: {
      position: "absolute",
      top: "100%",
      left: 0,
      marginTop: "4px",
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "4px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      minWidth: "200px",
      zIndex: 1000,
      maxHeight: "200px",
      overflowY: "auto",
    },
    dropdownItem: {
      padding: "8px 16px",
      cursor: "pointer",
      fontSize: "14px",
      color: "#2d3748",
      borderBottom: "1px solid #f7fafc",
      transition: "background-color 0.2s",
    },
  };

  return (
    <TourProvider>
      <div className={styles.container}>
        <h1 className={styles.title}>Design Your Screen</h1>

        {/* Scenario Indicator — simplified: tenant + app name only */}
        {(() => {
          // Parse kb_id into tenant + app name.
          // kb_id format: "tenant5__Mynewapp1"  →  { tenant: "tenant5", appName: "Mynewapp1" }
          const parseKbId = (id) => {
            if (!id || typeof id !== "string")
              return { tenant: "", appName: "" };
            const parts = id.split("__");
            if (parts.length >= 2) {
              return { tenant: parts[0], appName: parts.slice(1).join("__") };
            }
            return { tenant: "", appName: id };
          };

          const { tenant, appName } = parseKbId(kbId);

          return (
            <div
              style={{
                backgroundColor: "#f7fafc",
                padding: "12px 20px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: `3px solid ${modalMode
                  ? SCENARIO_CONFIGS[ENTRY_TYPES.LOCAL_APP].color
                  : SCENARIO_CONFIGS[selectedScenario].color
                  }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {/* Left side: tenant + app name */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span style={{ fontSize: "24px" }}>📱</span>
                <div>
                  {tenant && (
                    <div
                      style={{
                        fontWeight: "700",
                        fontSize: "16px",
                        color: "#2d3748",
                      }}
                    >
                      {tenant}
                    </div>
                  )}
                  {appName && (
                    <div style={{ fontSize: "13px", color: "#4a5568" }}>
                      {appName}
                    </div>
                  )}
                  {!tenant && !appName && (
                    <div style={{ fontSize: "13px", color: "#a0aec0" }}>
                      No app selected
                    </div>
                  )}
                </div>
              </div>

              {/* Right side: config_id + status_code (small neutral chips) */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {configId && (
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: "12px",
                      backgroundColor: "#edf2f7",
                      color: "#4a5568",
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: "monospace",
                    }}
                    title={configId}
                  >
                    ID {configId.substring(0, 8)}…
                  </span>
                )}
                {!isAllTemplatesView && statusCode && (
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: "12px",
                      backgroundColor: "#e8f5e9",
                      color: "#2e7d32",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    Status {statusCode}
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        <div className={styles.mainLayout}>
          <div
            className={
              isLeftPanelCollapsed
                ? styles.collapsedWrapper
                : isEditorOpen
                  ? styles.leftPanelWrapperExpanded
                  : styles.leftPanelWrapper
            }
          >
            <LeftPanel
              sampleResponse={sampleResponse}
              setSampleResponse={setSampleResponse}
              template={template}
              setTemplate={setTemplate}
              error={error}
              handleRender={handleRender}
              parsedSample={parsedSample}
              flattenedFields={flattenedFields}
              insertField={insertField}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              templateLines={templateLines}
              setTemplateLines={setTemplateLines}
              availableVariables={availableVariables}
              onTemplateUpdate={handleTemplateUpdate}
              onTrackChange={handleTrackChange}
              isDisabled={!sampleResponse || !sampleResponse.trim()}
              onResetAll={() => {
                console.log(
                  "[PreviewTester] 🔄 Resetting all state due to new sample response",
                );
                // === STEP 2d: Reset user's screen choice too ===
                setUserScreenChoice(null);
                setTransformationRules([]);
                if (selectorTracker) {
                  selectorTracker.clearChanges();
                }
                setIsEditMode(false);
                setTemplateLines([]);
                setSelectedText("");
                setSelectedRange(null);
                setSelectedContainer(null);
                setSelectionType(null);
                setIsSelectingMode(false);
                setShowTransformationPanel(false);
                setShowPhaseWizard(false);
                setCombinedStep(null);
              }}
              transformationRules={transformationRules}
              showPhaseWizard={showPhaseWizard}
              parseTemplateForEdit={parseTemplateForEdit}
              isCase2={isCase2}
              // === STEP 2e: Pass screen choice down ===
              userScreenChoice={userScreenChoice}
              setUserScreenChoice={setUserScreenChoice}
              validation={validation}
              onOpenEditor={handleOpenEditor}
              onCloseEditor={handleCloseEditor}
              hasPreviewData={hasPreviewData}
              onAddTransformationStep={handleAddTransformationStep}
              onRemoveTransformationStep={handleRemoveTransformationStep}
              onUpdateTransformationStep={handleUpdateTransformationStep}
              // ✅ UX: collapse / expand support
              isCollapsed={isLeftPanelCollapsed}
              onToggleCollapse={() => setIsLeftPanelCollapsed((v) => !v)}
            />
          </div>

          <RightPanel
            ref={previewContainerRef}
            renderedPreview={renderedPreview}
            error={error}
            scrollMode={scrollMode}
            setScrollMode={setScrollMode}
            layoutDirection={layoutDirection}
            setLayoutDirection={setLayoutDirection}
            dataType={dataType}
            parsedData={parsedData}
            getScrollStyle={getScrollStyle}
            getPreviewContent={getPreviewContent}
            scenarioTree={scenarioTree}
            renderScenarioTree={renderScenarioTree}
            isSampleArray={isSampleArray}
            arrayLayoutDirection={arrayLayoutDirection}
            setArrayLayoutDirection={setArrayLayoutDirection}
            isControlsOpen={isControlsOpen}
            setIsControlsOpen={setIsControlsOpen}
            parsedSample={parsedSample}
            template={template}
            configId={configId}
            kbId={kbId}
            statusCode={statusCode}
            actions={actions}
            onAddAction={handleAddAction}
            onRemoveAction={handleRemoveAction}
            isSelectingMode={isSelectingMode}
            setIsSelectingMode={setIsSelectingMode}
            selectedText={selectedText}
            selectionType={selectionType}
            showTransformationPanel={showTransformationPanel}
            onApplyTransformation={applyTransformation}
            onCloseTransformationPanel={handleCloseTransformationPanel}
            onTextSelect={handleTextSelect}
            transformationRules={transformationRules}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            templateLines={templateLines}
            setTemplateLines={setTemplateLines}
            availableVariables={availableVariables}
            onTemplateUpdate={handleTemplateUpdate}
            combinedStep={combinedStep}
            onStartPhaseWizard={handleStartPhaseWizard}
            onAddOperationToPhase={handleAddOperationToPhase}
            onRemoveOperationFromPhase={handleRemoveOperationFromPhase}
            onNextPhase={handleNextPhase}
            onPrevPhase={handlePrevPhase}
            onFinalizeCombinedStep={handleFinalizeCombinedStep}
            onCancelWizard={handleCancelWizard}
            showPhaseWizard={showPhaseWizard}
            isStructuralSelection={selectionType?.isStructural || false}
            currentPhase={combinedStep?.currentPhase || 0}
            hasPreviewData={hasPreviewData}
            onTrackChange={handleTrackChange}
            hasTransformations={transformationRules.length > 0}
            onSaveSuccess={handleSaveSuccess}
            modalMode={modalMode || false}
            onTemplateDataReady={onTemplateDataReady || null}
            caseType={validation.caseType || "unknown"}
            queryId={queryId}
            onOpenEditor={handleOpenEditor}
            onCloseEditor={handleCloseEditor}
            handleUpdateAction={handleUpdateAction}
          />
        </div>

        <style>
          {`
            @keyframes slideUp {
              from { opacity: 0; transform: translateX(-50%) translateY(20px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}
        </style>
      </div>

      {/* Confirmation Popup */}
      {showTransformConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            animation: "fadeIn 0.3s ease",
          }}
        >
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
              }
              @keyframes slideUp {
                from { transform: translateY(20px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
            `}
          </style>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px 40px",
              maxWidth: "520px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              animation: "slideUp 0.3s ease",
              position: "relative",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                textAlign: "center",
                marginBottom: "12px",
              }}
            >
              {isInitialLoad ? "📋" : "✨"}
            </div>
            <h2
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#2d3748",
                textAlign: "center",
                margin: "0 0 8px 0",
              }}
            >
              {isInitialLoad
                ? `Saved Transformations Found (${pendingTransformSteps.length})`
                : "Apply Saved Transformations?"}
            </h2>
            <p
              style={{
                fontSize: "15px",
                color: "#4a5568",
                textAlign: "center",
                lineHeight: "1.6",
                margin: "0 0 16px 0",
              }}
            >
              {isInitialLoad
                ? `This template has ${pendingTransformSteps.length} saved transformation${pendingTransformSteps.length > 1 ? "s" : ""} from the database.`
                : "The preview has been reset to its raw structure. Would you like to re-apply the saved transformations?"}
            </p>

            <div
              style={{
                backgroundColor: "#f7fafc",
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "20px",
                border: "1px solid #e2e8f0",
                maxHeight: "150px",
                overflow: "auto",
              }}
            >
              {pendingTransformSteps.map((step, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 0",
                    fontSize: "13px",
                    color: "#4a5568",
                    borderBottom:
                      index < pendingTransformSteps.length - 1
                        ? "1px solid #edf2f7"
                        : "none",
                  }}
                >
                  <span
                    style={{
                      padding: "1px 8px",
                      borderRadius: "10px",
                      fontSize: "10px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      backgroundColor: step.isButtonTransformation
                        ? "#f0fff4"
                        : step.type === "style"
                          ? "#fefcbf"
                          : "#ebf8ff",
                      color: step.isButtonTransformation
                        ? "#22543d"
                        : step.type === "style"
                          ? "#744210"
                          : "#2b6cb0",
                    }}
                  >
                    {step.isButtonTransformation ? "🔘" : step.type || "style"}
                  </span>
                  <span style={{ fontWeight: "500" }}>{step.operation}</span>
                  {step.fields && step.fields.length > 0 && (
                    <span style={{ fontSize: "11px", color: "#718096" }}>
                      ({step.fields.join(", ")})
                    </span>
                  )}
                </div>
              ))}
            </div>

            {isInitialLoad ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >

                <button
                  onClick={handleRecalculateTemplateFresh}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "transparent",
                    color: "#2b6cb0",
                    border: "2px solid #90cdf4",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ebf8ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span style={{ fontSize: "20px" }}>♻️</span>
                  <div>
                    <div style={{ fontWeight: "600" }}>
                      Recalculate Template Fresh
                    </div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>
                      Discard the saved template and rebuild it from scratch using the current
                      sample response (auto-detects case & layout).
                    </div>
                  </div>
                </button>

                <button
                  onClick={handleKeepRawView}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "transparent",
                    color: "#4a5568",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f7fafc";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  <span style={{ fontSize: "20px" }}>📄</span>
                  <div>
                    <div style={{ fontWeight: "600" }}>Keep Raw View</div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>
                      Keep raw structure preview with button transformations
                      preserved in Control Panel
                    </div>
                  </div>
                </button>

                <button
                  onClick={handleStartFresh}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "transparent",
                    color: "#e53e3e",
                    border: "2px solid #fc8181",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#fff5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  <span style={{ fontSize: "20px" }}>🧹</span>
                  <div>
                    <div style={{ fontWeight: "600" }}>Start Fresh</div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>
                      Remove ALL transformations (including buttons) from
                      preview and Control Panel
                    </div>
                  </div>
                </button>

                <button
                  onClick={handleApplyTransformations}
                  style={{
                    padding: "10px 16px",
                    backgroundColor: "#9f7aea",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#805ad5";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#9f7aea";
                  }}
                >
                  <span style={{ fontSize: "20px" }}>✅</span>
                  <div>
                    <div style={{ fontWeight: "600" }}>
                      Apply Saved Transformations
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgba(255,255,255,0.8)",
                      }}
                    >
                      Apply all transformations to preview and show in Control
                      Panel
                    </div>
                  </div>
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={handleDismissReapplyPopup}
                  style={{
                    padding: "10px 24px",
                    backgroundColor: "transparent",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    flex: 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f7fafc";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  Keep Current View
                </button>
                <button
                  onClick={() => {
                    setShowTransformConfirm(false);
                    applyLoadedTransformations(pendingTransformSteps);
                    setPendingTransformSteps([]);
                  }}
                  style={{
                    padding: "10px 32px",
                    backgroundColor: "#9f7aea",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    flex: 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#805ad5";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#9f7aea";
                  }}
                >
                  Re-apply Transformations
                </button>
              </div>
            )}
          </div>
        </div>
      )}

       {/* Recalculate-Fresh: Single-vs-Multiple layout choice */}
      <LayoutChoicePopup
        isOpen={showRecalcLayoutPopup}
        onClose={handleRecalcClosePopup}
        onConfirmSingleScreen={handleRecalcConfirmSingleScreen}
        onConfirmMultipleScreens={handleRecalcConfirmMultipleScreens}
        detectedArrays={recalcDetectedArrays}
        parsedResponse={recalcParsedResponse}
      />
    </TourProvider>
  );
};

export default PreviewTester;
